/**
 * @file CreateBill.js
 * @version 3.11.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-04
 * @modified 2026-11-02
 *
 * 3.10.0 (2026-10-30, Cam — Mole Valley's FSCN0019792 was uploaded in Add
 * Bill on 27 Oct, read as a credit note, its due date emptied, and saved as
 * a BILL that Xero would not approve): a NEW bill whose PDF reads as a credit
 * note asks first (utils/billCreditRead) — "Enter as a supplier credit" turns
 * the screen into Add Supplier Credit in place, with the same PDF, supplier,
 * credit number, date and lines (figures positive; any POs become what it
 * relates to; "Credit is for" still to be chosen — never defaulted); "Keep it
 * as a bill" changes nothing and is not asked again for that upload (the
 * read's due date is now kept on a bill); "Decide when saving" leaves it a
 * bill and the save asks before anything is sent. Editing a bill: only a note
 * (a bill can't become a credit). Bulk Add Bills' review is never asked.
 * 3.9.0 (2026-10-30, Cam — "Why can't we edit bills added locally (then
 * pushed to Xero) when they are supplier credits?"): Edit Supplier Credit on a
 * credit already in Xero opens editable as a bill in Xero does (the server's
 * edit_context 'patch'; the reason asked when it is approved there), greyed
 * with the server's reason when any of it is allocated. "Credit is for" can
 * be changed there — the reason window says its item lines then go to Xero
 * with or without their item codes; a credit keyed in Xero with none chosen
 * may be saved without choosing one. The notice and the reason window speak
 * of the credit, not a bill.
 * 3.7.0 (2026-10-30, Cam — bill I_00881855): a line whose code is none of our
 * items (a supplier's own, read from their invoice) is noted — it would go to
 * Xero without an item — with "Clear the code"; choose ours in the Item cell.
 * 3.6.0 (2026-10-28, Cam — units clear on every price): the grid says under
 *   each line's Unit Price what it is per (OrderLinesGrid `priceUnitFacts`):
 *   as billed — a tonne or a kg where the invoice printed one — else its PO
 *   line's unit (a tote, a 25kg bag, a tonne), else the item's; the PO /
 *   Order / Contract costs in the price cell's card say their unit too.
 * 3.5.0 (2026-10-28, Cam — "if the bill has a different cost price to the
 *   order (where order is not 0) or the order had (or now has) a contract
 *   price … and the cost on the contract differs … make the cost field
 *   mandatory and not chosen by default … Most times user would want to query
 *   that bill, but sometimes they will accept"): a bill line whose cost (in
 *   its PO line's unit) differs from the PO's or the order line's (not 0), or
 *   whose order line's contract (Pricing & Costing's — billing-context
 *   `cost_check`) has a cost (not 0) differing from either, is flagged
 *   (utils/billCostCheck, the server's rule): a warning note names every
 *   figure — "Bill £x/T · order cost £y/T (PO-D…, SO-D…) · contract … cost
 *   £z/T (…; in force on DD/MM/YYYY)" — with Query the bill / Accept the
 *   bill's cost / Use the order's cost / Use the contract's cost; the Unit
 *   price cell starts EMPTY ("Choose a cost", outlined in the error colour;
 *   its card offers the bill's, the PO's, the order's, the contract's, or
 *   type another — a typed price is a choice too); "Also set the PO's cost"
 *   starts unticked (never ticked on a queried bill); Save is greyed with
 *   the reason until each such line has a cost or the bill is Queried (its
 *   lines then keep the costs as invoiced). The choice goes with the save as
 *   `line_choices[].choices.price` ('bill' / 'po' / 'order' / 'contract' /
 *   'typed'); the server refuses without it (409 COST_CHOICE_REQUIRED —
 *   those lines then ask). Edit Bill: a line answered on an earlier save
 *   (GET /:id `cost_choices`) is not asked again; a bill in Xero is not asked.
 *
 * 3.4.1 (2026-10-28, Cam — "when item is changed via the item picker please
 *   replace the sales/ purchase description with the newly selected item's"):
 *   a different item picked on a bill line takes the item's purchase words
 *   (the grid, a new item from Add Item — utils/pickedItemWords); the Item
 *   cell's card choosing the PO's item takes the PO line's words for it, the
 *   bill's item its purchase words. The Description cell still offers the
 *   PO's and the bill's words.
 *
 * 3.4.0 (2026-10-22, Cam — "Xero-made POs should be editable too and synced
 *   with Xero at time of save"): "PO in the bill's units" is no longer greyed
 *   for a PO made in Xero (only while the Xero PO sync is on, its scope); the
 *   choice says Xero's copy changes with the save. A save refused because the
 *   PO was changed in Xero since (XERO_PO_NEWER) asks "Send ours to Xero"
 *   (xero_newer_confirmed, per PO); one Xero refused says so, nothing saved.
 *
 * 3.3.0 (2026-10-22, Cam — "offer the user the option to convert the bill's
 *   units to match our PO … or keep the bill's units and change our PO's to
 *   match the supplier's … or keep both … they should be given the choice"):
 *   a line billed in another unit than its PO line (utils/billUnitConversion,
 *   which now also reads a tonne line's pack size from its words and takes an
 *   exact quantity with the invoice's unit when the PO is costed in the wrong
 *   unit) offers THREE ways, each with its maths, in the line's note and in
 *   the Quantity cell: the bill in the PO's units (convertToPoUnits, as
 *   before), the PO in the bill's units (utils/poUnitsFromBill — its quantity,
 *   cost, unit weight, qty per pallet and supplied converted, the amount and
 *   pallets unchanged, its order line following; sent as `po_edits` `units:
 *   'bill'`, the server works it out again), or both kept as they are (the
 *   cost tick then shows the maths, "£400.00 a tonne = £10.00 a 25kg bag").
 *   Greyed with the reason: a bill in Xero, a PO made in Xero, an invoiced
 *   order, a split family's line, a PO billed by another bill, a bill in kg,
 *   priced additives. The quantity notes and the save's window compare in one
 *   unit whichever way was chosen; the choice goes in `line_choices` (`units`:
 *   'po' / 'bill' / 'keep').
 *
 * 3.2.0 (2026-10-16, Cam — Burcombe's QA03476: "added a duplicate of the very
 *   last bill row's last sub line … and the VAT did not get put in from the
 *   item's purchase VAT stored in inventory, nor did it see from the bill that
 *   VAT was on every line"): a line the reader listed twice is left out when
 *   the invoice's net says so (utils/billReadLines.dropRepeatedLines — the
 *   server does it on every read; a read kept by Bulk Add Bills is checked
 *   here, its jobs' line indexes renumbered). Each line's VAT defaults PO
 *   line → item's purchase tax type → the bill's (its own VAT %, else the
 *   invoice's VAT total as one rate of its net) — on a read, on lines from
 *   POs, on a match (billLineReconcile.mergeLines) and on a haulier's charge
 *   lines (billJobLines); the Tax cell offers the PO's / item's / bill's
 *   (`taxChoice`). The lines' VAT is shown beside the invoice's VAT total,
 *   and the totals panel adds VAT line by line, as saved (`perLine`).
 * 3.1.0 (2026-10-16, Cam — "check that bills added from other suppliers (not
 *   Burcombe) are handling the line details right"):
 *   - Defaults per Cam's rule: item, words, account and tax from the PO line;
 *     quantity and unit price from the BILL (no longer started from the
 *     supplied quantity) — the PO's supplied (else ordered) quantity is the
 *     cell's alternative and a difference is a note.
 *   - The haulier's job logic (EXTH items, job headings) runs only on a bill
 *     of jobs (utils/billJobLines.isBillOfJobs: two or more jobs, every line
 *     in them a charge). MVFS / Alta goods bills quoting our POs were coming
 *     in as EXTH (Bag) / (Bulk) at 320 / 321; now their quoted POs are
 *     brought on and the lines matched to the PO lines, untouched.
 *   - Direct Delivery: a product's bill line never auto-matches the PO's
 *     haulage line (billLineReconcile); where its price includes the
 *     delivery it takes " (includes direct delivery)" after the PO's words
 *     (billLineFields.directDeliveryInPrice) — the cell offers the words
 *     without it — and, when the PO carries the haulage on its own line,
 *     "Also set the PO's cost" starts unticked.
 *   - On save, one window for the PO lines whose supplied qty the bill's
 *     differs from (or none recorded): set the PO's supplied qty to the
 *     bill's / leave it and mark the bill Queried / leave it as it is
 *     (BillQtySaveDialog, utils/billQtyOnSave). × goes back to the bill.
 *     Setting supplied is now offered where a supplied qty is recorded too.
 *
 * 3.0.0 (2026-10-16, Cam — "Add bill is currently chaotic to use. It should
 *   be incredibly straightforward … give the user control of the process but
 *   assisted by extracting what we can from the PDF reading and offering
 *   those values or the PO's via straightforward choice"; and "POs and bills
 *   might not always match exactly but the user will know what they are
 *   doing so let them … less restricted, just gentle guiding"):
 *   - ONE form: the header and the line grid. Choosing POs fills the bill
 *     from them; the PDF fills what it can. Every header field and line cell
 *     that has both a PO value and a bill value offers both IN THE FIELD
 *     (FieldChoice — focus a box, or click a picker's marker): "PO …",
 *     "Bill …", or type / choose your own. Where they agree it shows plainly;
 *     where they differ a small marker says which is in use.
 *   - Defaults: structure, item, words, account and tax from the PO; the
 *     unit cost from the bill; the quantity what was supplied where recorded
 *     (else the bill's); dates and the invoice number from the bill.
 *   - "Also update the PO to this", in the cell's card: the cost (the
 *     write-back, `po_cost_updates` — ticked by default, unticked on a
 *     Queried bill; the save sends exactly what the cells say, no window),
 *     the quantity and the words (`po_edits`, off by default) — greyed with
 *     the reason where the PO cannot take it (utils/billLineFields).
 *   - Under each line, gentle notes with one-click actions: the bill's
 *     quantity against what was supplied ("Mark bill Queried", "Use the
 *     bill's quantity as supplied" — `supplied_copies`, never automatic);
 *     billed in another unit, with the maths ("Bill 2,000.00 kg at £2.015/kg
 *     = 80 × 25kg bag at £50.375/bag (same £4,030.00)") and "Use the PO's
 *     units"; an invoice line on no PO line (match it), a PO line not on the
 *     invoice (remove it).
 *   - A haulier's bill of jobs (Burcombe): one heading row per job (date ·
 *     their ref · our document — a picker of the matches, exact / likely
 *     taken · delivered to · Bag / Bulk), its charge lines under it, each an
 *     EXTH item (utils/billJobLines) with the document's reference in its
 *     words; every matched document linked to the bill.
 *   - Supplier: the invoice's named supplier within the PO's supplier group
 *     (MVFS), never a question; an unrelated one is offered in the field.
 *   - Nothing that only differs between the bill and its PO stops a save any
 *     more: the reconcile list, the "invoice and PO disagree" window, the
 *     "Update PO from bill?" window, the unallocated / lines-on-no-PO / PO
 *     with no line refusals are gone or are notes. Kept as hard stops only
 *     what the data or Xero cannot take: no supplier, no line, a line with
 *     no account, a credit's number and purpose; the server's own questions
 *     and refusals as before.
 *   - Retired: BillLineChoicesPanel, BillFieldReconciler,
 *     BillReferenceLinksPanel, the linked PO panels; BillPoCostDialog is no
 *     longer opened here (Bulk Add Bills' Accept and "Update PO from this
 *     bill…" still use it).
 *
 * 2.24.0 (2026-10-16, Cam — layout):
 *   - The "upload the PDF first" note beside the page title (Add / Edit Bill,
 *     Add / Edit Supplier Credit); the title the size and weight of Bill
 *     Details and Line Items.
 *   - The attach section on one row with no title: a round upload button
 *     (btn-round, not a slide-out), "PDF only · max 20 MB", then the file.
 *   - On the details row: the POs' buttons, Stock, PO Reference, Status,
 *     Queried. PO Reference ("Other PO#", as wide as Reference) is greyed
 *     until Stock is on — a value already held stays, is shown greyed and is
 *     saved as it is; the save rules for both are unchanged.
 *   - The PDF preview opens expanded on a display bigger than a laptop
 *     (utils/displaySize) until the user chooses; their choice is kept for the
 *     tab as before.
 *   - Before VAT on one row, centred, the box wide enough for its placeholder;
 *     its figures kept together.
 *
 * 2.23.0 (2026-10-15, Cam — "choose, check, save; never retype"):
 *   - "PO and bill, line by line" (BillLineChoicesPanel,
 *     utils/billLineChoices): for each PO line the bill bills, quantity, unit
 *     cost, description and item — the PO's value beside the bill's. Click
 *     the PO's and the bill line takes it; click the bill's and the PO line
 *     takes it when the bill is saved (a quantity or the words as a PO edit,
 *     the cost as "Update PO from bill" — the same paths and rules as before;
 *     a cost may be kept different on purpose). Per line and whole-bill "All
 *     from the PO / the bill"; every choice shows and can be undone until the
 *     save. A direction that may not be taken is greyed with the reason. Only
 *     where a bill may change its POs (a bill, new or not yet in Xero).
 *   - Billed in another unit (kg / tonnes against bags…), on evidence: "Use
 *     the PO's quantity and units" — the line stored as the PO counts it at
 *     amount ÷ quantity (4 dp), its amount unchanged to the penny (else greyed
 *     with the figures); the figures as invoiced go with it (`as_billed`) and
 *     are shown; "Back to the bill's units" undoes it.
 *   - The save's "Update PO from bill" window asks only about the costs not
 *     already chosen (their answers are sent with it); none left, no window.
 *     The choices go with the save as `line_choices` (audited).
 *   - Header (Cam): Choose POs, Haulage for a PO and PO links from references
 *     side by side in one row; PO Reference (110px) and Stock on the details
 *     row with the rest.
 *
 * 2.21.1 (2026-10-14, Cam — "the first two should read everything from the
 *   scanned bill … and forget the PO it found"): "Remove the PO link" (review
 *   / stock) on an already billed PO no longer falls back to — or, when the
 *   invoice names another supplier, chooses — the PO's supplier; the supplier
 *   match, the jobs' PO links and the line words are the reader's unbiased
 *   ones (utils/billPoReference.withoutPoLink 1.1.0).
 *
 * 2.21.0 (2026-10-14, Cam — "it would not allow me to choose the right bill
 *   to link with the credit note, only a PO"): Relates to's bills come from
 *   CreditBillPicker, searched on the server as the person types (number,
 *   amount, date, PO, supplier) — it was the supplier's newest 100 bills,
 *   loaded once and filtered here, so an older NWF or Mole Valley bill could
 *   not be named. Each option shows date · invoice no. · total · owes ·
 *   status; the bills already named are listed, tagged ADDED.
 *
 * 2.20.0 (2026-10-14, Cam — Burcombe's QA03476): "PO links from references".
 *   A bill of several jobs (a haulier's), each quoting an SO-D, a PO-H, a
 *   bare "10183" or an INV- from before cutover: after a scan (the read's
 *   `reference_links`), or from "PO links from references" beside Choose POs
 *   for lines already on screen (a new bill, or one entered here and not yet
 *   in Xero), BillReferenceLinksPanel shows each job's suggested PO with how
 *   sure and why, a picker and a tick (exact ones ticked; one already billed
 *   by another bill never). Apply (utils/billReferenceLinks.applyLinks): a
 *   PO-H links the job's lines as its haulage lines; the supplier's own PO-D
 *   brings its lines to be reconciled; a Xero PO from the old way becomes a
 *   third-party haulage link; "No PO" keeps the lines without one.
 *
 * 2.19.1 (2026-10-14, Cam — Lacto Production): each line keeps the unit its
 *   invoice printed (`bill_unit`, read by the PDF reader); the quantity check
 *   passes it to utils/billUnitConversion (billed per kg, or per tonne,
 *   against a PO line in packs or tonnes). A line whose quantity or price is
 *   retyped in the grid loses it (keepReaderUnits) — the figures are no
 *   longer the invoice's.
 *
 * 2.19.0 (2026-10-14, Cam — PO Reference and Stock):
 *   - An uploaded invoice quoting a PO already billed for its goods by
 *     another bill (utils/billPoReference) asks, once the scan is read, in
 *     the shared ConfirmDialog: "Remove the PO link and review the bill" (the
 *     PO is not linked, nothing else), or "Remove the PO link, keep the PO
 *     number as the PO Reference — mark as Stock"; × keeps the link (the save
 *     then asks "a partial or second bill?", as before). The same in Bulk Add
 *     Bills' review (it is this screen).
 *   - Two fields of this system's own on a bill: "PO Reference" (text) and a
 *     Stock toggle (off by default). Never sent to Xero: on a bill already in
 *     Xero they are saved apart from the edit (the server takes them out
 *     before anything that may reach Xero). Greyed with the reason until
 *     their migration has run (`local_fields_ready`), and never sent then.
 *
 * 2.18.0 (2026-10-14, Cam — one bill line over several POs' lines): a
 *   supplier's one line (10 T of an item) covering several PO lines of that
 *   product (PO A 1 T, PO B 9 T) is split into one ordinary bill line per PO
 *   line (utils/billLineSplit): each its share of the quantity at the line's
 *   price, the amounts and VAT adding up to the line's to the penny. A sure
 *   split (exact, one set of PO lines) is made as the invoice is reconciled,
 *   as a sure match is — highlighted, with Undo; anything less sure is
 *   offered above the lines ("Split it", or choose the PO lines and
 *   quantities in BillLineSplitDialog). An invoice line on no PO line can be
 *   split by hand from the reconcile list. Editing a bill not yet in Xero, a
 *   line covering PO lines no line bills is offered the same. The server sees
 *   ordinary bill lines.
 *
 * 2.17.0 (2026-10-13, Jade — "Add Supplier Credit to Bill"):
 *   - The window's title says what it is: Add / Edit Supplier Credit (or
 *     Bill) — `onTitleChange` from Dashboard.
 *   - A credit's details are laid out on their own: supplier, type, credit
 *     number, date, status and Queried on one row; "Credit is for" and
 *     "Relates to" on the next — its POs and bills as chips in one place,
 *     added from ONE control ("Add a PO or bill…": Choose POs… first, then
 *     the supplier's bills), with the "a reference only" hint under it.
 *   - No due date on a credit (not shown, not read from its PDF, not sent).
 *   - A credit's figures are positive: its PDF is read with `credit=1` and
 *     turned over if printed negative, a minus typed in the lines is taken
 *     off when the figure is left, and the save sends it positive
 *     (utils/creditSigns, the server's own rule).
 *   - Raised against ONE bill (CreditAgainstBill): "clear the query when this
 *     credit is saved" when the bill is queried, and "Allocate this credit to
 *     BILL" — both ticked — with what will happen (£x once the credit is in
 *     Xero; paid / part paid) or why not; sent as `against_bill_id`,
 *     `clear_bill_query`, `allocate_to_bill`. After the save the answer's
 *     `against_bill` is said before the window closes.
 *
 * 2.16.0 (2026-10-13, Jade — "Bill & PO Units Differ"): a bill line priced per
 *   tonne against a PO line in packs (or the other way round) is read in the
 *   PO line's unit (utils/billUnitConversion): the quantity check, the copy
 *   into supplied, "Use PO quantities" and "Update PO from bill" convert it
 *   (1.00 T at £400 = 40 × 25kg bag at £10), and the line says so with its
 *   total against the PO line's.
 *
 * 2.15.0 (2026-10-12, Cam — Bulk Add Bills): this screen is the review step
 *   of Bulk Add Bills, given `bulk` ({ item, loadFile, mode, existingBillId,
 *   onSaved, onCancel }). It opens on the item's own pages and what the
 *   reader made of them (no second read): a new bill (a credit in Add Supplier
 *   Credit's mode) filled exactly as an upload fills it, or — Replace — the
 *   existing bill in Edit Bill with the document's dates, number and lines
 *   put over it (its lines matched to the document's, the rest flagged to
 *   delete or match, as an upload is reconciled with a PO). The save is the
 *   normal one with `bulk_item_id` (a new bill: saved once, the server
 *   attaches the pages; Replace: the normal edit, then the PDF uploaded as
 *   usual). Cancel goes back to the summary; Reset goes back to what was read.
 *
 * 2.14.1 (2026-10-09, Cam): a delivered order's PO line description can be
 *   corrected from Add / Edit Bill (words are our record); its quantity stays.
 *
 * 2.14.0 (2026-10-08, Cam): a supplier credit never changes a PO's cost —
 *   it comes after the bill, a one-off amount that its allocation takes off
 *   the bill. Saving a credit no longer asks "Update PO from credit?" (the
 *   preview endpoint is gone); it sends `po_cost_updates: []`.
 * 2.12.1 (2026-10-08, review): editing a credit, "Update PO from credit?"
 *   never re-offers a line it already applied (credit_id on the preview).
 * 2.12.0 (2026-10-08, Cam): a credit says what it is for — "Goods returned"
 *   (its items go to Xero, stock goes down) or "Price correction" (account
 *   only, stock untouched) — chosen beside the type, required, never
 *   defaulted; sent as `credit_kind`; fixed once the credit is in Xero.
 * 2.11.0 (2026-10-08, Cam — Add Supplier Credit): this screen in its credit
 *   mode (opened from Search Bills' "Add Supplier Credit" or a bill's "Add
 *   credit against this bill" — utils/supplierCredits; or Type: Credit Note).
 *   The supplier's credit PDF is read as a bill's is; the credit number, date
 *   (the lock rule), lines (item, account, tax as on a bill). The POs and
 *   bills it relates to are chosen as REFERENCES ("Related POs" — the PO
 *   picker, billed POs shown — and "Related bills"): no PO panels, no PO line
 *   edits, no supplied quantities, no PO marked billed; due date optional.
 *   (Asking "Update PO from credit?" on save was removed in 2.14.0.) Sent
 *   `purchase_order_ids` (references), `credit_bill_ids`, `po_cost_updates`;
 *   the server saves it as a credit and queues it for Xero as an
 *   ACCPAYCREDIT. A bill is not turned into a credit on an edit.
 * 2.10.1 (2026-10-08, review): "Keep" is remembered on an edit — a line whose
 *   bill cost is what it was when the bill was opened starts unticked, and an
 *   edit that changed no bill cost (notes, due date) does not ask; an order
 *   line whose cost differs while the PO agrees is offered too (costs.view
 *   only); after the save, lines that still differ are named with "Update PO
 *   from this bill…".
 * 2.10.0 (2026-10-08, Cam):
 *   - "Update PO from bill" is a CHOICE. When saving would change any PO
 *     line's cost (utils/billCostWriteBack.poCostChanges — the server's rule),
 *     BillPoCostDialog asks first, line by line (PO, product, PO cost → bill
 *     cost, both quantities for information), every line ticked — none on a
 *     Queried bill. "Update PO from bill" saves with the ticked lines, "Keep
 *     PO as it is" with none (`po_cost_updates`); × goes back to the form.
 *     Nothing to change, no question.
 *   - Copying the bill's quantity into supplied on an order whose invoice was
 *     raised and has not gone out: the server answers with the invoice changes
 *     it needs (409 INVOICE_CHANGES_PROPOSED); InvoiceProposalDialog shows them
 *     — "Apply these changes and save" saves with `invoice_changes` and the
 *     invoice is edited after the save; "Continue editing the bill" saves
 *     nothing. What happened to the invoice is said before the window closes
 *     (a refusal: NOT changed, and "Update invoice from supplied qty…" in the
 *     bill window). An invoice that has gone out refuses the copy.
 *   - "Haulage for a PO" (was "…another supplier's PO"): a supplier's own
 *     separate haulage bill for its Xero-made product PO may be linked too.
 *
 * 2.9.0 (2026-10-07, Cam — supplier groups): from an uploaded invoice the
 *   bill's supplier is the one NAMED ON THE INVOICE (matched to a contact),
 *   not the matched PO's. When it matches none, the PO's supplier — its
 *   supplier group's parent for a child's PO (MVFS - Lifton → Mole Valley Feed
 *   Solutions) — outlined in the error colour with "Supplier not found on the
 *   invoice — taken from the PO; check it" until a supplier is picked or "It's
 *   right" pressed. A bill started from POs of a group child (or several
 *   members of one group, now allowed together) defaults to the parent, marked
 *   the same way (utils/billSupplierDefault). The haulage PO lookup is sent the
 *   chosen supplier, so its own group's POs are never offered as haulage.
 * 2.8.0 (2026-10-07, Cam — PO-26135 / INV-0532): third-party haulage links
 *   (utils/billHaulageLink). A new haulier's bill can be the haulage for
 *   another supplier's PO made in Xero — "Haulage for another supplier's PO"
 *   (HaulagePoLinkDialog), or offered when the save finds such a PO among the
 *   goods POs (409 HAULAGE_LINK_OFFERED: yes moves it to haulage, off any line
 *   that named it, and saves again). Sent as `haulage_po_ids`; the PO's cost
 *   and status are not changed. Editing a bill keeps its haulage links (shown,
 *   changed in the bill window's POs).
 * 2.7.1 (2026-10-06, review): copying the bill's quantity into supplied when
 *   it is more than the query limit from what was ordered asks first, naming
 *   both figures (`confirmed` is sent; the server refuses an unconfirmed copy
 *   over 1 apart — SUPPLIED_NEEDS_CONFIRMATION); "all lines" ticks only those
 *   within the limit. A line whose supplied quantity was just copied from the
 *   bill is checked against what was ORDERED. A split family's shared line is
 *   not offered (record it on each order).
 * 2.7.0 (2026-10-06, Cam):
 *   - A PO made in Xero, or whose customer order is delivered, can have only
 *     its COST changed in its panel (description and quantity shown, fixed,
 *     with the reason); the scope comes from POST
 *     /api/purchase-orders/billing-context. A Xero-made PO's new cost goes to
 *     Xero after the save (the server, in the background).
 *   - PO lines can be changed on an edit too, for a bill entered here and not
 *     yet in Xero (saved 'Changed in Edit Bill <no>').
 *   - Each PO panel shows what was SUPPLIED beside each line. The bill is
 *     checked against that (utils/billQtyCheck.compareQuantity); a PO-D line
 *     with none offers to copy the bill's quantity in — the user ticks it, per
 *     line or all at once (`supplied_copies`, saved through the order line;
 *     never a Xero-made PO, never a PO-H).
 *
 * 2.5.0 (2026-10-05, Cam — editing bills already in Xero): with editing
 *   bills in Xero switched on (GET /api/bills/:id → edit_context.mode
 *   'patch'), a bill in Xero — entered here or keyed there, draft or approved —
 *   opens for editing and saving sends Xero only what was changed (the server
 *   patches Xero's own copy and checks it afterwards). The supplier, type,
 *   status and Queried are fixed there (status has its own button); an
 *   approved bill asks for a one-line reason first (PostedEditDialog), listing
 *   what will change in Xero. Each line carries the saved line it is
 *   (`bill_line_id`), so it is changed in place. A PDF chosen there is
 *   attached as it is, never read into the fields or lines. Editing a bill
 *   keyed in Xero no longer needs every linked PO to have a line (it was
 *   linked as a whole).
 *
 *   NOTE for the Add Bill package: only edit mode is touched here — patchMode,
 *   the reason window, handleSaveBill's 4th argument (opts.reason) and the two
 *   keyedInXero exceptions in its PO checks.
 * 2.5.0 (2026-10-05, Jade's testing — the Add Bill flow only, not editing):
 *   - A bill date on or before the Xero lock (read from the invoice, typed, or
 *     from a PO) becomes the first open day, said in the window
 *     (utils/billLockDate), never a dialog.
 *   - The invoice/PO reconciler opens only when there is something to decide;
 *     dates the invoice has that do not clash (a due date a bill from a PO
 *     never has) now apply.
 *   - A bill filled from an uploaded invoice is reconciled line by line with
 *     its POs (utils/billLineReconcile): sure matches are made, and every
 *     invoice line on no PO line, and every PO line not on the invoice, is
 *     flagged and must be matched or deleted before saving.
 *   - Before VAT defaults to the lines' total, highlighted until the user
 *     checks it against the invoice.
 *   - Status offers Approved (Authorised) or Draft only (utils/billStatus).
 *   - The supplier named on the invoice is matched to a contact; one that is
 *     not the PO's supplier or its supplier group is a disagreement like the
 *     others (utils/billSupplierMatch). With no PO it fills the supplier.
 *   - A PO detected from the upload brings its lines in. The bill's POs' lines
 *     can be changed in their panels — Xero-made POs and POs before the lock
 *     included — and are saved to the PO with the bill (`po_edits`, audited
 *     'Changed in Add Bill <invoice no>'; utils/billPoEdits).
 *   - A bill line more than 0.5 T (bulk) or 1 bag (bagged) from its PO line is
 *     flagged to query with the supplier (utils/billQtyCheck); smaller
 *     differences keep the gentle note.
 *
 * 2.4.0 (2026-10-05, Cam):
 *   - The window closes on a successful save (new or edit) and the list it
 *     was opened from (sessionStorage 'createBill.returnTo', else Search
 *     Bills) loads afresh. A refusal (possible duplicate, missing supplier
 *     invoice number, Xero) keeps it open with the reason and nothing lost.
 *   - The bill's unit costs now go onto its POs when it is saved (the server,
 *     billService._writeCostsToPos); its quantities never do. Where a bill
 *     quantity differs from its PO's, the save says so and asks the user to
 *     check (weighbridge tickets and the like) before the window closes. The
 *     "Update PO quantities" button is gone for the same reason.
 *   - Ticking Queried on a bill already approved in Xero asks first: Xero
 *     will void it and receive it again as a Draft.
 *   - "Raise a copy" of a voided bill (VoidedDocumentDialog) opens this screen
 *     filled from it (sessionStorage 'copyBillId'): the same supplier, POs,
 *     lines and prices, as a new Draft.
 *
 * 2.3.0 (2026-10-05) — a clear status choice: Authorised (the default) or
 * Draft, both sent to Xero as chosen; Queried makes it a draft (the status is
 * held at Draft while Queried is on, and goes back to Authorised when it is
 * cleared).
 *
 * 2.2.1 (2026-10-03) — the reconciler's dead "update the PO" tick box is gone
 * (nothing read it); linked POs are now clickable (by-db-id returns the Xero id).
 *
 * 2.2.0 (2026-10-03) — bills related to POs are entered here and sent to Xero
 * approved. "Choose POs" (BillPoPicker) brings in a supplier's PO-D, PO-H or
 * Xero-made PO- lines, each line keeping its PO (a PO column in the grid); the
 * supplier invoice's net total is typed and the unallocated figure must reach
 * £0 before saving, with at most one line on no PO (a PO-H week's haulage
 * figure is split by hand this way). Possible duplicates are shown as they are
 * found and the server's questions (duplicate, already billed) are asked
 * before saving. Saving never waits for Xero: the post-save window and the
 * edit header show where the bill is on its way there (BillPushStatus), with
 * Retry.
 *
 * Lines are the shared editable grid (OrderLinesGrid, variant "bill"), the same
 * one Add Customer Order, Add Invoice and Add Purchase Order use. It replaced an
 * "Add Line" form over a read-only table, which could not take a typed line at
 * all — broadband, rent or fuel could only arrive by PDF — and dropped each
 * line's additives whenever a bill was edited. Lines map to and from the bill's
 * own shape in utils/lineItems.
 *
 * 2.22.0 (2026-10-15, Cam): a save dated in a month that should be locked in
 * Xero by now is asked about (409 PRIOR_MONTH_CONFIRM,
 * usePriorMonthConfirm); Save anyway sends confirm_prior_month.
 *
 * 3.8.0 (2026-10-30, Cam — the Item picker's X): each "Recently bought" row
 * can be taken off this supplier's list for everyone until it is bought
 * again, with Undo (hooks/usePreviousItemRemoval). Not the "On linked POs"
 * rows.
 *
 * 3.11.0 (2026-11-02, Cam — the item's supplier): the Supplier list pins each
 * line's item's supplier at the top (a supplier group's depots when the item
 * is a group's), in view while the rest scrolls. Picking an item fills the
 * Supplier only when none is chosen yet and every line's item names the same
 * ONE supplier. The old item-code guess (100… → Mole Valley, 2… → NWF, K / P
 * → ForFarmers) is kept for items that name no supplier, but never fills a
 * supplier group's member — it pins the group's suppliers instead ("always
 * make users pick a supplier for any MVFS supplier group member").
 * utils/itemSupplier.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { API_URL } from '../config';
import './DocumentForm.css';
import './CreateBill.css';
import Input from './Input';
import EntityLink from './EntityLink';
import usePersistedState from '../hooks/usePersistedState';
import DateInput from './DateInput';
import Dropdown from './Dropdown';
import SearchableDropdown from './SearchableDropdown';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import usePriorMonthConfirm, { isPriorMonthConfirm } from '../hooks/usePriorMonthConfirm';
import usePreviousItemRemoval from '../hooks/usePreviousItemRemoval';
import ContactModal from './ContactModal';
import ConflictResolutionModal from './ConflictResolutionModal';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import { perUnitOf } from '../utils/unitPriceLabel';
import OrderLinesGrid, { blankLine, lineIsEmpty, lineTotals, LineTotalsPanel } from './OrderLinesGrid';
import InventoryItemFormModal from './InventoryItemFormModal';
import {
  billLinesFromApi, billLinesToPayload, resolveItemForVariant,
  suggestedSupplierFor, findItemForLine, withAdditiveNames, withoutAdditiveNames
} from '../utils/lineItems';
import { formatQuantity, formatDecimalInput, formatCount } from '../utils/numberFormatters';
import { withStoreTag } from '../utils/stockSupplier';
import {
  headerSupplierFill, pinnedSupplierIds, pinSupplierOptions, itemSupplierOf, guessAllowed, groupPins
} from '../utils/itemSupplier';
import useItemSupplierOptions from '../hooks/useItemSupplierOptions';
import useXeroLock from '../hooks/useXeroLock';
import { isLargerThanLaptop } from '../utils/displaySize';
import BillPoPicker from './BillPoPicker';
import BillPushStatus from './BillPushStatus';
import { COPY_BILL_KEY } from '../utils/voidedDocument';
import { quantityDifferencesMessage } from '../utils/billCostWriteBack';
import PostedEditDialog from './PostedEditDialog';
import { describeBillEditInXero } from '../utils/billEdit';
import { BILL_STATUS_CHOICES } from '../utils/billStatus';
import { openBillDate, lockedDateNotice } from '../utils/billLockDate';
import { billLineQuantityCheck, quantityCheck, quantityUnitKind } from '../utils/billQtyCheck';
import { getTaxRate, getTaxTypeLabel } from '../utils/taxTypes';
import { keepReaderUnits, billUnitConversion, conversionCostText, conversionQtyText } from '../utils/billUnitConversion';
import TickCheckbox from './TickCheckbox';
import { supplierClash, unmatchedSupplierNote } from '../utils/billSupplierMatch';
import { supplierFromUpload, supplierCheckFromPos } from '../utils/billSupplierDefault';
import {
  poLineKey, autoMatch, matchLine, reconcileState, isOpenPdfLine, isOpenPoLine, lineLabel, isChargeLine
} from '../utils/billLineReconcile';
import {
  poLineFacts, gridCandidates, freeCandidates, findSplit, applySplit, unsplitLine, appliedSplits,
  splitOffers, autoSplit, offerText, sameProduct
} from '../utils/billLineSplit';
import BillLineSplitDialog from './BillLineSplitDialog';
import { effectivePoLine, poEditsPayload, poQuantityFor, suppliedCopiesPayload } from '../utils/billPoEdits';
import HaulagePoLinkDialog from './HaulagePoLinkDialog';
import SupplierCheckHint from './SupplierCheckHint';
import { haulageForLabel, haulageOfferFrom, moveToHaulage } from '../utils/billHaulageLink';
import FieldChoice from './FieldChoice';
import { convertLine, unconvertLine, convertToPoUnits, money, quantityInUnit } from '../utils/billLineChoices';
import { poUnitsFromBill, poUnitsShortText } from '../utils/poUnitsFromBill';
import { lineFieldSides, poUpdateBlock, unitsMathsText, lineChoicesFor, sameValue, directDeliveryInPrice, withDdSuffix } from '../utils/billLineFields';
import { wordsOnItemPick, isItemChange } from '../utils/pickedItemWords';
import { dropRepeatedLines, jobsWithoutLines, billTaxOf, defaultTaxType, hasTax } from '../utils/billReadLines';
import BillQtySaveDialog from './BillQtySaveDialog';
import { qtyQuestions, qtyAnswers } from '../utils/billQtyOnSave';
import InvoiceProposalDialog from './InvoiceProposalDialog';
import {
  poCostChanges, defaultCostTicks, billCostsByKey, stillDifferMessage, suppliedFromContext, costKey
} from '../utils/billCostWriteBack';
import { useAccess } from '../hooks/useAccess';
import { isInvoiceQuestion, applyAnswer, invoiceUpdateMessage } from '../utils/invoiceProposals';
import {
  takeCreditMode, CREDIT_PURPOSES, creditKindOf, againstBillOf, againstBillResultMessage
} from '../utils/supplierCredits';
import { positiveCreditGridLines, positiveCreditDocument } from '../utils/creditSigns';
import CreditAgainstBill from './CreditAgainstBill';
import CreditBillPicker from './CreditBillPicker';
import { poBilledElsewhere, billedPoSentence, withoutPoLink, BILLED_PO_CHOICES } from '../utils/billPoReference';
import { creditReadAction, saveNeedsCreditAnswer, readSupplierName, creditReadQuestion, CREDIT_READ_SWITCHED_NOTE, CREDIT_READ_EDIT_NOTE } from '../utils/billCreditRead';
import { refFieldsOf, referenceRequest, suggestBlock, chosenTarget, billedWarning } from '../utils/billReferenceLinks';
import { applyJobs, initialJobChoices, jobPack, billPackDefault, jobDocOptions, NO_PO, isBillOfJobs, goodsBillPoLoads } from '../utils/billJobLines';
import { formatDate } from '../utils/dateFormatters';
import {
  costCheck, billCostInPoUnit, costCheckText, costCheckWhy, costChoiceRequiredText, checkKey, costMoney, perUnitText, contractName, costsDiffer
} from '../utils/billCostCheck';

/** How many of the bill's POs show as chips before "+N more" (a haulier's bill can carry 15). */
const PO_CHIPS_SHOWN = 6;

/** Relates to's first option: the supplier's POs (the PO picker), then its bills. */
const CHOOSE_POS_OPTIONS = Object.freeze([{ value: '__pos__', label: 'Choose POs…', sticky: true }]);

/** Why PO Reference and Stock are greyed: their migration has not run yet. */
const LOCAL_FIELDS_NOT_READY = 'PO Reference and Stock are not switched on yet — the database update for them has not been run.';

/**
 * Why the PO Reference box can or cannot be typed in. It notes the PO number a
 * Stock bill's invoice quoted, so it opens with Stock (Cam, 2026-10-16).
 */
export function poReferenceTitle(localFieldsReady, isStock) {
  if (!localFieldsReady) return LOCAL_FIELDS_NOT_READY;
  if (!isStock) return 'Turn Stock on to note the PO number this invoice quoted';
  return 'The PO number this invoice quoted, when that PO is not linked. Kept in this system only — never sent to Xero. Searchable from Search Bills.';
}

const numOf = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// ── Add Bill's cost check (Cam, 2026-10-28; utils/billCostCheck) ──
// A flagged line's cost starts EMPTY: its price is kept aside as `_billPrice`
// (`_costBlanked`) until a cost is chosen (`_costPick`: 'bill' | 'po' |
// 'order' | 'contract' | 'typed').
const r4c = (n) => Math.round((Number(n) || 0) * 10000) / 10000;

/** The line as its price was before a cost was chosen — for the units maths and the checks. */
export const withBillFigure = (l) => (l && l._costBlanked && !l._costPick ? { ...l, unit_price: l._billPrice ?? '' } : l);

/**
 * The bill's own unit price for a line, in the unit the line is in now: a
 * line put in the PO's units, its amount as invoiced over its quantity; else
 * the price kept aside when the cost was emptied or a cost picked; else the
 * line's own (as read, or as typed — what the bill line will say).
 */
export function billFigureOf(line) {
  if (!line) return null;
  const as = line._asBilled;
  if (as && numOf(line.quantity) > 0 && numOf(as.line_amount) !== null) return r4c(numOf(as.line_amount) / numOf(line.quantity));
  if (line._billPrice !== undefined && line._billPrice !== null && line._billPrice !== '') return numOf(line._billPrice);
  return numOf(line.unit_price);
}

/** A cost chosen for a line: its price set, the choice and the bill's own figure kept. */
export function withCostPick(line, source, value) {
  const keep = line._billPrice !== undefined ? line._billPrice : (line._costPick ? undefined : line.unit_price);
  return { ...line, unit_price: value === null || value === undefined ? '' : formatDecimalInput(value), _costPick: source, _billPrice: keep, _highlight: false };
}

/** Before the units change on a line: its cost choice goes (asked again in the new unit), its price back to the bill's. */
export function withoutCostPick(line) {
  if (!line || (!line._costBlanked && !line._costPick && line._billPrice === undefined)) return line;
  const { _costBlanked, _costPick, _billPrice, ...rest } = line;
  return { ...rest, unit_price: _costBlanked && !_costPick ? (_billPrice ?? '') : (_billPrice !== undefined && _costPick ? _billPrice : line.unit_price) };
}

/**
 * The grid's change, with a price typed into a line's cost cell recorded as
 * its choice ('typed') — only a price change, not an item picked.
 */
export function withTypedCosts(prev, next) {
  const before = new Map((prev || []).map(l => [l.id, l]));
  return (next || []).map(l => {
    const p = before.get(l.id);
    if (!p || String(p.unit_price ?? '') === String(l.unit_price ?? '')) return l;
    if (p.product_code !== l.product_code || p.inventory_item_id !== l.inventory_item_id) return l;
    if (l.unit_price === '' || l.unit_price === null || l.unit_price === undefined) {
      return l._costPick ? { ...l, _costPick: undefined } : l;
    }
    return { ...l, _costPick: 'typed' };
  });
}

/** The PO line a bill line bills, from the bill's PO details: { po, li, index } or null. */
function findPoLine(poMap, line) {
  const po = line && poMap[line.purchase_order_id];
  if (!po || !line.po_line_key) return null;
  const lines = Array.isArray(po.line_items) ? po.line_items : [];
  const index = lines.findIndex((li, i) => poLineKey(li, i) === line.po_line_key);
  return index < 0 ? null : { po, li: lines[index], index };
}

// Where the window goes back to after a save or Cancel, unless the screen
// that opened it said (sessionStorage 'createBill.returnTo').
// A PO made in Xero whose line changes with the bill: Xero's copy takes it in
// the same save (2026-10-22 — the server's services/xeroPoLineSyncService).
const XERO_COPY_FOLLOWS = "; it was made in Xero, and Xero's copy is changed with it when the bill is saved";

const DEFAULT_RETURN_TO = 'search-bills';
const RETURN_TO_KEY = 'createBill.returnTo';

function CreateBill({ onNavigate, bulk = null, onTitleChange = null }) {
  // Xero refuses a bill dated on or before its lock date; the calendar stops there.
  const { firstAllowedDate } = useXeroLock();
  // Read once; cleared by the mount effect (not here — StrictMode runs an
  // initialiser twice).
  const [returnTo] = useState(() => sessionStorage.getItem(RETURN_TO_KEY) || DEFAULT_RETURN_TO);
  // The bill as loaded for an edit: approved in Xero (not queried)? Ticking
  // Queried then asks first — Xero voids it and receives it again as a Draft.
  const [approvedInXero, setApprovedInXero] = useState(false);
  const [confirmQueryApproved, setConfirmQueryApproved] = useState(false);
  // Which edit this bill gets (server's edit_context): 'patch' = a bill in
  // Xero whose save sends Xero only what changed. The bill as loaded, to say
  // what will change in Xero; and the reason window for an approved one.
  const [editCtx, setEditCtx] = useState(null);
  const [editSnapshot, setEditSnapshot] = useState(null);
  const [reasonPrompt, setReasonPrompt] = useState(null);
  // The invoice changes the server proposes (2026-10-08).
  // Order line costs are offered only to someone who may see costs.
  const { can } = useAccess();
  const seeCosts = can('costs.view');
  const [invoiceQuestion, setInvoiceQuestion] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingBillId, setEditingBillId] = useState(null);
  const patchMode = editMode && editCtx?.mode === 'patch';
  // Bulk Add Bills (2026-10-12): Replace edits an existing bill from a batch item.
  const bulkReplace = !!bulk && bulk.mode === 'replace';
  // What the item's reading still has to put on the form, once the screen is ready.
  const [bulkApply, setBulkApply] = useState(null);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Slideout buttons — single call with all modal/state deps (see below)

  // Supplier
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierXeroContactId, setSupplierXeroContactId] = useState('');
  // A supplier defaulted from a PO rather than read off the invoice (or its
  // supplier group's parent): the hint shown, highlighted, until the user
  // picks or confirms one (utils/billSupplierDefault). The ref: the user (or a
  // saved bill) settled the supplier, so an upload does not change it.
  const [supplierCheck, setSupplierCheck] = useState('');
  const supplierConfirmedRef = useRef(false);
  const [pendingSupplierUpdate, setPendingSupplierUpdate] = useState(null);

  // Linked POs (many-to-many — a bill can link to multiple POs)
  const [linkedPOIds, setLinkedPOIds] = useState([]);              // ordered array of PO IDs
  const [linkedPODetailsMap, setLinkedPODetailsMap] = useState({}); // { [poId]: poObj }
  // Third-party haulage links: POs made in Xero this haulier's bill carried
  // (utils/billHaulageLink). Not goods — no lines, no cost, no status.
  const [haulagePOs, setHaulagePOs] = useState([]);
  const [showHaulagePicker, setShowHaulagePicker] = useState(false);
  const [haulageOffer, setHaulageOffer] = useState(null);
  // Edit Bill: a haulage link is made / taken off at once on the saved bill.
  const [haulageBusy, setHaulageBusy] = useState(false);
  // A supplier credit (Type: Credit Note): the POs and bills it relates to —
  // a reference only, never billing (2026-10-08). [{ id, purchase_order_number,
  // xero_purchase_order_id }] / [{ id, supplier_reference }].
  const [creditPoRefs, setCreditPoRefs] = useState([]);
  const [creditBillRefs, setCreditBillRefs] = useState([]);
  const [showCreditPoPicker, setShowCreditPoPicker] = useState(false);
  // What the credit is for: 'return' | 'price' — the user's choice, no default.
  const [creditKind, setCreditKind] = useState(null);
  // A credit raised against ONE bill (Jade, 2026-10-13): what saving it does
  // with that bill (GET /api/bills/:id/credit-against), and the user's two
  // answers — clear its query, allocate to it — both yes unless changed.
  const [againstPreview, setAgainstPreview] = useState(null);
  const [clearBillQuery, setClearBillQuery] = useState(true);
  const [allocateToBill, setAllocateToBill] = useState(true);
  // An edited credit's automatic allocation, as saved (xero_push_info.auto_allocation).
  const [savedAutoAllocation, setSavedAutoAllocation] = useState(null);

  // Header fields
  const [transactionType, setTransactionType] = useState('BILL');
  const [billDate, setBillDate] = useState(new Date().toISOString().split('T')[0]);
  // Where the bill date came from (the invoice, typed, a PO, a copy), so a
  // date in a locked period can be explained (utils/billLockDate).
  const billDateSourceRef = useRef('typed');
  const setBillDateFrom = (date, source) => { billDateSourceRef.current = source; setBillDate(date); };
  // { from, to, source } once a locked date has been moved to the first open day.
  const [lockNotice, setLockNotice] = useState(null);
  const [dueDate, setDueDate] = useState('');
  const [reference, setReference] = useState('');
  const [supplierReference, setSupplierReference] = useState('');
  // Bills go to Xero approved (Cam, 2026-10-03); a Draft stays here until it is.
  const [status, setStatus] = useState('Authorised');
  const [isQueried, setIsQueried] = useState(false);
  const [notes, setNotes] = useState('');
  // This system's own (2026-10-14): the PO number the invoice quoted, kept as
  // text when that PO is not linked, and whether the bill bought for stock.
  const [poReference, setPoReference] = useState('');
  const [isStock, setIsStock] = useState(false);
  // An upload quoting a PO already billed: { s, hasText, meta, billed } while asked.
  const [billedPoQuestion, setBilledPoQuestion] = useState(null);
  // An upload that reads as a credit note on a new bill (Cam, 30 Oct 2026 —
  // utils/billCreditRead): the read and the answer — null until answered,
  // 'bill' (kept as a bill), 'credit' (entered as a supplier credit) — and the
  // question while it is open: { stage: 'upload' | 'save', saveArgs? }.
  const [creditReadState, setCreditReadState] = useState(null);
  const [creditReadDialog, setCreditReadDialog] = useState(null);
  // A bill being edited given a PDF that reads as a credit note: said, no switch.
  const [creditReadEditNote, setCreditReadEditNote] = useState(false);
  // A bill of jobs (a haulier's — Burcombe, 2026-10-16): the documents the
  // jobs quote and the user's choice per job (utils/billJobLines) —
  // { jobs, ids, choices: { [job_key]: { po_id, pack? } } }.
  const [jobLinks, setJobLinks] = useState(null);
  const [refLinksBusy, setRefLinksBusy] = useState(false);
  // The PO ids / haulage links the jobs put on the bill, so a changed choice
  // takes the old one off again.
  const jobPoIdsRef = useRef({ goods: [], haulage: [] });
  // The header's two sides (2026-10-16): what the PO(s) say and what the
  // invoice says — offered in each field (FieldChoice).
  const [poHeader, setPoHeader] = useState(null);
  const [showAllPoChips, setShowAllPoChips] = useState(false);
  const [billHeader, setBillHeader] = useState(null);

  // Line items, in the grid's shape. A fresh bill opens with one empty row: a
  // grid with nothing in it reads as "nothing to do here".
  const [lineItems, setLineItems] = useState(() => [blankLine()]);

  // The supplier invoice's net total (before VAT), typed or read from the
  // PDF. On a bill against POs the lines must add up to it: what they do not
  // cover is "unallocated" and must reach £0 before saving.
  const [invoiceNet, setInvoiceNet] = useState('');
  // The invoice's VAT total as read, against the lines' VAT (2026-10-16).
  const [invoiceVat, setInvoiceVat] = useState(null);
  // Before VAT defaults to the lines' total; the figure the user confirmed
  // against the invoice (null: not yet), so a later change asks again.
  const [netConfirmed, setNetConfirmed] = useState(null);
  // Lines were read from an uploaded invoice (a new bill): they are matched
  // with the bill's PO lines, and what is left is noted on the lines.
  const [pdfLinesApplied, setPdfLinesApplied] = useState(false);
  // "Also update the PO to this", ticked in a cell (2026-10-16):
  // { [poId|poLineKey]: { qty?: true, desc?: true } }. The PO edits are the
  // line's values as they are when saved (utils/billPoEdits).
  const [poEditTicks, setPoEditTicks] = useState({});
  // The quantity / words edits ticked in the cells; the units choice ("PO
  // takes the bill's units", 2026-10-22) is merged in below as `poEdits`.
  const poEditsTyped = useMemo(() => {
    const out = {};
    for (const line of lineItems) {
      if (!line.purchase_order_id || !line.po_line_key) continue;
      const t = poEditTicks[costKey(line.purchase_order_id, line.po_line_key)];
      if (!t || (!t.qty && !t.desc)) continue;
      const found = findPoLine(linkedPODetailsMap, line);
      if (!found) continue;
      const e = {};
      if (t.qty) e.quantity = formatDecimalInput(line.quantity);
      if (t.desc) e.description = withAdditiveNames(line.product_name, found.li.additives);
      out[line.purchase_order_id] = { ...(out[line.purchase_order_id] || {}), [line.po_line_key]: e };
    }
    return out;
  }, [lineItems, poEditTicks, linkedPODetailsMap]);
  // Each PO's billing context (POST /api/purchase-orders/billing-context):
  // { [poId]: { scope, xeroMade, lines: { [poLineKey]: { supplied, billBlock, ... } } } }.
  const [poContext, setPoContext] = useState({});
  // The user's choice to record the bill's quantity as supplied:
  // { [poId]: { [poLineKey]: true | 'confirmed' } } ('confirmed': far from what was ordered, and said yes).
  const [suppliedCopies, setSuppliedCopies] = useState({});
  // Asking before copying a bill quantity far from what was ordered: { poId, key, number, what, billQty, ordered }.
  const [copyConfirm, setCopyConfirm] = useState(null);
  // The save-time quantities window (BillQtySaveDialog): { questions, answers, args } while open.
  const [qtySave, setQtySave] = useState(null);
  // PO lines answered "Leave it as it is" there — not asked again ({ [poId|key]: true }).
  const [qtyLeft, setQtyLeft] = useState({});
  // Add Bill's cost check (Cam, 2026-10-28): the price choice recorded for
  // each PO line on the bill's earlier saves (Edit Bill — GET /:id
  // `cost_choices`), and PO lines the server said still need a choice
  // (409 COST_CHOICE_REQUIRED) — both keyed `${poId}|${poLineKey}`.
  const [recordedCostChoices, setRecordedCostChoices] = useState({});
  const [serverCostRequired, setServerCostRequired] = useState(() => new Set());
  // Choose POs window; taking a PO off (with its lines) asks first.
  const [showPoPicker, setShowPoPicker] = useState(false);
  const [confirmUnlinkPo, setConfirmUnlinkPo] = useState(null);
  // Is sending bills to Xero switched on? (GET /api/bills/push-settings)
  const [pushSettings, setPushSettings] = useState({ enabled: null, reason: null, localFieldsReady: false, asBilledReady: false });
  // Where the bill being edited / just saved is on its way to Xero.
  const [pushStatus, setPushStatus] = useState(null);
  // Bills that look like this one, found while it is typed in.
  const [duplicates, setDuplicates] = useState([]);
  // The server's questions before saving (possible duplicate, PO already billed).
  const [saveQuestion, setSaveQuestion] = useState(null);
  // A PO made in Xero changed in Xero since it was read: send ours over it? (2026-10-22)
  const [xeroNewerQuestion, setXeroNewerQuestion] = useState(null);
  // "Dated in a month that should be locked" — asked when the save says so.
  const priorMonth = usePriorMonthConfirm();

  // Suppliers / products lists
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  // The supplier groups (for the item-code guess's group rule), and the
  // pins a guess that landed on a group's member asked for.
  const supplierLists = useItemSupplierOptions();
  const [codeGuessPins, setCodeGuessPins] = useState([]);
  // Purchase accounts for the grid's Account column.
  const [accounts, setAccounts] = useState([]);
  // Items bought from this supplier before, for the Item picker.
  const [previousItems, setPreviousItems] = useState([]);
  // The picker's X: take a row off this supplier's "Recently bought" for
  // everyone until it is bought again (Cam, 30 Oct 2026), with Undo.
  const { removePrevious, toast: previousItemToast } = usePreviousItemRemoval({
    side: 'supplier',
    accountId: selectedSupplierId,
    accountName: suppliers.find(s => String(s.id) === String(selectedSupplierId))?.company_name || '',
    previousItems,
    setPreviousItems,
  });
  // Creating an item from the Item picker: which row asked, so the new item
  // lands on it.
  const [addItemForRow, setAddItemForRow] = useState(null);

  // After-save state (for push-to-xero)
  const [savedBillSource, setSavedBillSource] = useState(null);
  const [savedBillXeroId, setSavedBillXeroId] = useState(null);
  const [savedBillXeroModified, setSavedBillXeroModified] = useState(null);
  const [initialBillData, setInitialBillData] = useState(null);
  const [conflictData, setConflictData] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // PDF Attachment
  const [pendingFile, setPendingFile] = useState(null);       // File object awaiting upload
  const [parsingPDF, setParsingPDF] = useState(false);        // Parsing in progress
  const [ocrApplied, setOcrApplied] = useState(null);         // Summary of auto-applied fields
  const [ocrFilledFields, setOcrFilledFields] = useState(new Set()); // Which fields were auto-filled
  const [attachmentFilename, setAttachmentFilename] = useState(''); // Saved attachment (edit mode)
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);     // Blob URL for staged file preview
  const [pdfZoom, setPdfZoom] = useState(1.0);                // Preview zoom level
  // Expanded gives the preview roughly double the width. Persisted because it is
  // a working preference — someone checking a stack of invoices wants it to stay
  // where they put it. Until they choose, it opens expanded on a display bigger
  // than a laptop and narrow on a laptop (Cam, 2026-10-16; utils/displaySize):
  // the default applies only while the tab holds no choice of theirs.
  const [pdfExpanded, setPdfExpanded] = usePersistedState('createBill.pdfExpanded', isLargerThanLaptop());
  const fileInputRef = useRef(null);
  // Re-measure slideout buttons when:
  //   • a panel/file/conflict toggle changes button visibility
  //   • the bill finishes loading (loading→false reveals the header buttons)
  //   • edit-mode flags or Xero state change which header buttons render
  //     ("Save"/"Save Changes", "Push to Xero", "Update in Xero", "View in Xero")
  useSlideOutButton([
    !!pendingFile, !!attachmentFilename,
    loading, editMode, savedBillXeroId, savedBillSource, supplierXeroContactId, selectedSupplierId,
    pushStatus?.push_status, linkedPOIds.length, haulagePOs.length
  ]);

  // A new bill dated on or before the Xero lock takes the first open day, and
  // the window says so (Jade, 2026-10-05). An existing bill is never moved.
  useEffect(() => {
    if (editMode || !billDate || !firstAllowedDate) return;
    const { date, moved } = openBillDate(billDate, firstAllowedDate);
    if (moved) {
      setLockNotice({ from: billDate, to: date, source: billDateSourceRef.current });
      billDateSourceRef.current = 'lock';
      setBillDate(date);
    } else if (lockNotice && billDate !== lockNotice.to) {
      setLockNotice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billDate, firstAllowedDate, editMode]);

  // Helpers for working with the linked PO list
  const addLinkedPO = (poId) => {
    const id = parseInt(poId, 10);
    if (!Number.isInteger(id) || id <= 0) return;
    setLinkedPOIds(prev => prev.includes(id) ? prev : [...prev, id]);
  };
  const removeLinkedPO = (poId) => {
    const id = parseInt(poId, 10);
    setLinkedPOIds(prev => prev.filter(x => x !== id));
    setLinkedPODetailsMap(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };
  const clearLinkedPOs = () => {
    setLinkedPOIds([]);
    setLinkedPODetailsMap({});
    setHaulagePOs([]);
  };

  // Fetch details for any newly added linked POs we don't yet have cached —
  // and for a bill opened to edit, whose linked POs come without their lines
  // (2026-10-22: without them Edit Bill offered none of the per-line PO
  // choices — the cells' PO values, the notes, the units ways).
  useEffect(() => {
    const missing = linkedPOIds.filter(id => !linkedPODetailsMap[id] || !Array.isArray(linkedPODetailsMap[id].line_items));
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map(id =>
      fetch(`${API_URL}/api/purchase-orders/by-db-id/${id}`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => d.success ? [id, d.purchaseOrder] : null)
        .catch(() => null)
    )).then(results => {
      if (cancelled) return;
      const updates = {};
      for (const r of results) if (r) updates[r[0]] = r[1];
      if (Object.keys(updates).length) {
        setLinkedPODetailsMap(prev => Object.fromEntries([...Object.entries(prev), ...Object.entries(updates).map(([id, p]) => [id, { ...(prev[id] || {}), ...p, line_items: Array.isArray(p.line_items) ? p.line_items : [] }])]));
      }
    });
    return () => { cancelled = true; };
  }, [linkedPOIds, linkedPODetailsMap]);

  // A PO's lines change with a bill on a new bill, or on one entered here and
  // not yet in Xero (the server's local edit path); never from a bill in Xero.
  const billInXero = !!savedBillXeroId && !/^(TEMP|LOCAL)-/i.test(String(savedBillXeroId));
  const poLinesEditable = !editMode || (savedBillSource === 'local' && !billInXero);

  // What each linked PO may still change, and what was supplied on its lines.
  const poContextKey = linkedPOIds.slice().sort((a, b) => a - b).join(',');
  useEffect(() => {
    if (!linkedPOIds.length) { setPoContext({}); return undefined; }
    let cancelled = false;
    fetch(`${API_URL}/api/purchase-orders/billing-context`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: linkedPOIds })
    })
      .then(r => r.json())
      .then(d => { if (!cancelled && d.success) setPoContext(d.pos || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poContextKey]);

  // A supplier credit: the supplier's (and its group's) bills it may name —
  // searched on the server by CreditBillPicker (2026-10-14).
  const isCreditMode = transactionType === 'CREDIT';
  const creditSupplierXeroId = isCreditMode
    ? (suppliers.find(x => String(x.id) === String(selectedSupplierId))?.xero_contact_id || supplierXeroContactId || null)
    : null;

  // The window's title says what it is (Jade, 2026-10-13): Add / Edit
  // Supplier Credit, Add / Edit Bill. Dashboard shows it; cleared on leaving.
  const titleRef = useRef(null);
  useEffect(() => {
    if (!onTitleChange) return undefined;
    titleRef.current = `${editMode ? 'Edit' : 'Add'} ${isCreditMode ? 'Supplier Credit' : 'Bill'}`;
    onTitleChange(titleRef.current);
    return undefined;
  }, [onTitleChange, editMode, isCreditMode]);
  // Leaving: our title only — never one the next screen has already set.
  useEffect(() => () => {
    if (onTitleChange) onTitleChange(prev => (prev === titleRef.current ? null : prev));
  }, [onTitleChange]);

  // A credit raised against ONE bill (Jade, 2026-10-13): what saving it will
  // do with that bill — clear its query, allocate to it — read from the
  // server (the same rule the save applies). Not once the credit is in Xero.
  const againstBill = isCreditMode ? againstBillOf(creditBillRefs) : null;
  const againstBillId = againstBill ? Number(againstBill.id) : null;
  useEffect(() => {
    setClearBillQuery(true);
    setAllocateToBill(true);
    if (!againstBillId) { setAgainstPreview(null); return undefined; }
    let cancelled = false;
    const qs = new URLSearchParams();
    if (selectedSupplierId) qs.set('account_id', String(selectedSupplierId));
    if (supplierXeroContactId) qs.set('xero_contact_id', supplierXeroContactId);
    setAgainstPreview({ loading: true });
    fetch(`${API_URL}/api/bills/${againstBillId}/credit-against?${qs}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled) setAgainstPreview(d.success ? d : { error: d.error || 'Could not check the bill' }); })
      .catch((e) => { if (!cancelled) setAgainstPreview({ error: e.message }); });
    return () => { cancelled = true; };
  }, [againstBillId, selectedSupplierId, supplierXeroContactId]);

  // Type changed to / from Credit Note on a NEW document: a credit's POs are
  // references, a bill's are what it bills — moved across, never both.
  const changeType = (value) => {
    const toCredit = value === 'CREDIT';
    if (editMode && (toCredit !== (transactionType === 'CREDIT'))) {
      setAlertDialog({ isOpen: true, type: 'warning', message: 'A bill cannot become a supplier credit, nor a credit a bill, by editing it. Delete it and enter it again as the right kind.' });
      return;
    }
    if (toCredit && transactionType !== 'CREDIT') {
      const refs = linkedPOIds.map(id => ({
        id, purchase_order_number: linkedPODetailsMap[id]?.purchase_order_number || `PO #${id}`,
        xero_purchase_order_id: linkedPODetailsMap[id]?.xero_purchase_order_id || null
      }));
      setCreditPoRefs(prev => [...prev, ...refs.filter(r => !prev.some(p => Number(p.id) === Number(r.id)))]);
      clearLinkedPOs();
      setPoEditTicks({});
      setSuppliedCopies({});
      setLineItems(prev => prev.map(l => ({ ...l, purchase_order_id: null, po_line_key: null })));
    } else if (!toCredit && transactionType === 'CREDIT') {
      creditPoRefs.forEach(r => addLinkedPO(r.id));
      setCreditPoRefs([]);
      setCreditBillRefs([]);
    }
    setTransactionType(value);
  };

  // Debounced reference-field PO lookup — handles manual PO number entry
  useEffect(() => {
    const trimmed = reference.trim();
    if (!trimmed.match(/^PO-\d+$/i)) return;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/purchase-orders/by-number/${encodeURIComponent(trimmed)}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (data.success && data.id) {
          // A credit relates to it; a bill bills it.
          if (transactionType === 'CREDIT') {
            setCreditPoRefs(prev => (prev.some(p => Number(p.id) === Number(data.id)) ? prev
              : [...prev, { id: Number(data.id), purchase_order_number: trimmed.toUpperCase(), xero_purchase_order_id: data.xero_purchase_order_id || null }]));
          } else {
            addLinkedPO(data.id);
          }
        }
      } catch {}
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  // Create / revoke object URL when pendingFile changes
  useEffect(() => {
    if (pendingFile) {
      const url = URL.createObjectURL(pendingFile);
      setPdfObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPdfObjectUrl(null);
    }
  }, [pendingFile]);

  // Dialogs
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info', onCloseCallback: null });

  useEffect(() => {
    const initializeComponent = async () => {
      // Bulk Add Bills: the batch item, never the hand-offs below (which stay
      // for the next Add Bill that is opened for them).
      if (bulk) {
        const [, items] = await Promise.all([loadSuppliers(), loadProducts(), loadAccounts(), loadPushSettings()]);
        startFromBulk(items);
        return;
      }
      // The item list is passed on explicitly: these loaders run in this same
      // closure, where `products` state is still the empty array it started as,
      // and saved lines are matched back to inventory by it.
      sessionStorage.removeItem(RETURN_TO_KEY);
      const [, items] = await Promise.all([loadSuppliers(), loadProducts(), loadAccounts(), loadPushSettings()]);

      // A copy of a voided bill (VoidedDocumentDialog): a new bill, filled from it.
      const copyId = sessionStorage.getItem(COPY_BILL_KEY);
      if (copyId) {
        sessionStorage.removeItem(COPY_BILL_KEY);
        loadBillForEdit(copyId, items, { asCopy: true });
        return;
      }

      const editId = sessionStorage.getItem('editBillId');
      if (editId) {
        setEditMode(true);
        setEditingBillId(editId);
        sessionStorage.removeItem('editBillId');
        loadBillForEdit(editId, items);
        return;
      }

      // Add Supplier Credit (2026-10-08): the credit mode, with the supplier
      // and the bill it is against when opened from a bill.
      const creditMode = takeCreditMode();
      if (creditMode) {
        setTransactionType('CREDIT');
        setDueDate('');
        if (creditMode.account_id) {
          setSelectedSupplierId(String(creditMode.account_id));
          setSupplierName(creditMode.supplier_name || '');
          setSupplierXeroContactId(creditMode.xero_contact_id || '');
          supplierConfirmedRef.current = true;
        }
        if (creditMode.bill?.id) setCreditBillRefs([creditMode.bill]);
        // From a PO (Search POs, 2026-10-13): the PO and the bills for its goods.
        if (Array.isArray(creditMode.bills) && creditMode.bills.length) setCreditBillRefs(creditMode.bills.filter(b => b && b.id));
        if (Array.isArray(creditMode.pos) && creditMode.pos.length) setCreditPoRefs(creditMode.pos.filter(p => p && p.id));
        return;
      }

      // Multi-PO bulk action: { ids: number[], mode: 'combined' } in JSON
      const fromPOIdsRaw = sessionStorage.getItem('createBillFromPOIds');
      if (fromPOIdsRaw) {
        sessionStorage.removeItem('createBillFromPOIds');
        try {
          const parsed = JSON.parse(fromPOIdsRaw);
          const ids = (parsed.ids || []).map(n => parseInt(n, 10)).filter(Boolean);
          if (ids.length) loadFromPOs(ids, items);
        } catch (e) {
          console.warn('Invalid createBillFromPOIds payload', e);
        }
        return;
      }

      const fromPOId = sessionStorage.getItem('createBillFromPOId');
      if (fromPOId) {
        sessionStorage.removeItem('createBillFromPOId');
        loadFromPO(fromPOId, items);
      }
    };

    initializeComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Bulk Add Bills (2026-10-12) ──
  // The item's own pages are the bill's PDF; what the reader made of them is
  // put on the form as an upload's would be (no second read).
  const startFromBulk = async (items = products) => {
    let file = null;
    try {
      file = await bulk.loadFile();
    } catch (e) {
      setAlertDialog({ isOpen: true, type: 'error', message: `The pages could not be loaded: ${e.message}` });
    }
    const read = bulk.item?.parsed || null;
    if (bulkReplace && bulk.existingBillId) {
      setEditMode(true);
      setEditingBillId(String(bulk.existingBillId));
      await loadBillForEdit(String(bulk.existingBillId), items);
    } else if (bulk.item?.kind === 'credit') {
      setTransactionType('CREDIT');
      setDueDate('');
    }
    if (file) setPendingFile(file);
    if (read?.suggested) {
      setBulkApply({ s: read.suggested, hasText: read.has_text, meta: { notes: read.notes, confidence: read.confidence } });
    }
  };

  // Once the screen has the items (and, for Replace, the bill): apply the read.
  useEffect(() => {
    // The item list was loaded before the read was handed over (startFromBulk).
    if (!bulkApply || loading) return;
    const { s, hasText, meta } = bulkApply;
    setBulkApply(null);
    if (bulkReplace) {
      applyReplacement(s, meta);
    } else {
      routeRead(s, hasText, meta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkApply, loading]);

  // Replace: the document's date, numbers and lines over the existing bill's.
  // Its lines are matched to the bill's own (kept in place — a bill in Xero
  // keeps its Xero lines); the bill's lines the document has not got are
  // flagged, to delete or match, as an upload's are against a PO.
  const applyReplacement = (s, meta = {}) => {
    const applied = [];
    if (s.bill_date) {
      const { date, moved } = openBillDate(s.bill_date, firstAllowedDate);
      if (moved) setLockNotice({ from: s.bill_date, to: date, source: 'supplier' });
      setBillDateFrom(date, 'supplier');
      applied.push('Bill date');
    }
    if (s.due_date) { setDueDate(s.due_date); applied.push('Due date'); }
    if (s.supplier_reference) { setSupplierReference(s.supplier_reference); applied.push('Invoice number'); }
    if (s.sub_total != null && s.sub_total !== '') { setInvoiceNet(String(s.sub_total)); applied.push('Net total'); }
    if (s.line_items?.length > 0) {
      const fromDoc = billLinesFromApi(s.line_items, products, { fromPdf: true }).map(l => ({ ...l, _fromPdf: true }));
      setLineItems(prev => autoMatch([
        ...prev.filter(l => !lineIsEmpty(l)).map(l => ({ ...l, _fromPo: true })),
        ...fromDoc,
      ]).lines);
      setPdfLinesApplied(true);
      applied.push(`${s.line_items.length} line item${s.line_items.length > 1 ? 's' : ''}`);
    }
    setOcrApplied({
      fields: applied, total: s.total || null, noText: false, noFields: applied.length === 0,
      notes: ['Replacing the saved bill with this document: lines the bill has and the document has not are flagged.', meta.notes || ''].filter(Boolean).join(' '),
      confidence: meta.confidence || null,
    });
  };

  const loadSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=SUPPLIER&limit=10000`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSuppliers(data.contacts || []);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch(// activeOnly=false so OrderItemPicker has archived items to offer behind
      // its toggle. They stay hidden until asked for, so the list this opens
      // onto is unchanged.
      `${API_URL}/api/inventory/search?limit=10000&isPurchased=true&activeOnly=false`, {
        credentials: 'include'
      });
      const data = await response.json();
      const items = data.items || [];
      setProducts(items);
      return items;
    } catch (error) {
      console.error('Failed to load products:', error);
      return [];
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/purchase-accounts`, { credentials: 'include' });
      const data = await response.json();
      setAccounts(data.accounts || []);
    } catch (error) {
      console.error('Failed to load purchase accounts:', error);
    }
  };

  // What has been bought from this supplier before, looked up by id.
  useEffect(() => {
    if (!selectedSupplierId) { setPreviousItems([]); return undefined; }
    let cancelled = false;
    fetch(`${API_URL}/api/purchase-orders/supplier-previous-items/${selectedSupplierId}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (!cancelled) setPreviousItems(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setPreviousItems([]); });
    return () => { cancelled = true; };
  }, [selectedSupplierId]);

  const loadPushSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bills/push-settings`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) setPushSettings({ enabled: data.enabled, reason: data.reason, localFieldsReady: !!data.local_fields_ready, asBilledReady: !!data.as_billed_ready });
    } catch (error) {
      console.error('Failed to load bill push settings:', error);
    }
  };

  // `asCopy`: a NEW bill filled from this one (a voided bill raised again) —
  // the same supplier, POs, lines and prices, as a Draft, with nothing that
  // ties it to the old one (no Xero id, no PDF, not queried).
  const loadBillForEdit = async (billId, items = products, { asCopy = false } = {}) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bills/${billId}`, { credentials: 'include' });
      const data = await response.json();

      if (data.success) {
        const bill = data.bill;

        // Set supplier — the saved bill's, settled.
        if (bill.account_id) {
          setSelectedSupplierId(bill.account_id.toString());
        }
        supplierConfirmedRef.current = true;
        setSupplierCheck('');
        setSupplierName(bill.supplier_name || '');
        setSupplierXeroContactId(bill.xero_contact_id || '');

        // Linked POs (read from junction-aggregated linked_pos array)
        if (Array.isArray(bill.linked_pos) && bill.linked_pos.length > 0) {
          const ids = bill.linked_pos.map(p => p.id);
          setLinkedPOIds(ids);
          // Cache details from the response so we don't have to refetch each PO
          const map = {};
          for (const p of bill.linked_pos) map[p.id] = p;
          setLinkedPODetailsMap(map);
        } else {
          clearLinkedPOs();
        }
        // linked_pos is the goods POs only; haulage links come separately.
        setHaulagePOs(Array.isArray(bill.haulage_pos) ? bill.haulage_pos : []);
        // A supplier credit's references (never goods links).
        setCreditPoRefs(Array.isArray(bill.credit_links?.pos) ? bill.credit_links.pos : []);
        setCreditBillRefs(Array.isArray(bill.credit_links?.bills) ? bill.credit_links.bills : []);
        setCreditKind(creditKindOf(bill));
        setSavedAutoAllocation(asCopy ? null : (bill.xero_push_info?.auto_allocation || null));

        billDateSourceRef.current = asCopy ? 'copy' : 'typed';
        setBillDate(bill.bill_date ? bill.bill_date.split('T')[0] : new Date().toISOString().split('T')[0]);
        setDueDate(bill.due_date ? bill.due_date.split('T')[0] : '');
        setReference(bill.reference || '');
        setSupplierReference(bill.supplier_reference || '');
        setStatus(bill.status || 'Draft');
        setTransactionType(bill.transaction_type || 'BILL');
        setIsQueried(bill.is_queried || false);
        setNotes(bill.notes || '');
        setPoReference(bill.po_reference || '');
        setIsStock(!!bill.is_stock);
        setAttachmentFilename(bill.attachment_filename || '');
        setSavedBillSource(bill.source);
        setSavedBillXeroId(bill.xero_bill_id || null);
        setSavedBillXeroModified(bill.xero_modified_date || null);
        setPushStatus(bill.source === 'local' ? {
          source: bill.source,
          push_status: bill.xero_push_status ?? null,
          xero_bill_id: bill.xero_bill_id || null,
          xero_url: bill.xero_url || null,
          error: bill.xero_push_error || null,
          attempts: bill.xero_push_attempts || 0,
          info: bill.xero_push_info || {},
        } : null);
        setInvoiceNet(bill.sub_total != null ? String(bill.sub_total) : '');
        setInitialBillData({
          bill_date: bill.bill_date ? bill.bill_date.split('T')[0] : '',
          due_date: bill.due_date ? bill.due_date.split('T')[0] : '',
          reference: bill.reference || '',
          supplier_reference: bill.supplier_reference || '',
          sub_total: bill.sub_total
        });

        // Additives come back with each line now, so an edit keeps them.
        setLineItems(billLinesFromApi(bill.lines, items));
        // The cost each PO line was chosen at before: not asked again (2026-10-28).
        setRecordedCostChoices(asCopy ? {} : (bill.cost_choices || {}));
        setServerCostRequired(new Set());

        setEditCtx(asCopy ? null : (bill.edit_context || null));
        setEditSnapshot(asCopy ? null : {
          bill_date: bill.bill_date, due_date: bill.due_date, supplier_reference: bill.supplier_reference,
          reference: bill.reference, source: bill.source, lines: bill.lines || [],
          transaction_type: bill.transaction_type || 'BILL', credit_kind: creditKindOf(bill),
        });

        const realXeroId = !!(bill.xero_bill_id && !/^(TEMP|LOCAL)-/i.test(String(bill.xero_bill_id)));
        setApprovedInXero(!asCopy && bill.source === 'local' && realXeroId
          && String(bill.status || '').toLowerCase() === 'authorised' && !bill.is_queried);

        if (asCopy) {
          setStatus('Draft');
          setIsQueried(false);
          setAttachmentFilename('');
          setSavedBillSource(null);
          setSavedBillXeroId(null);
          setSavedBillXeroModified(null);
          setPushStatus(null);
          setInitialBillData(null);
          // Every PO a line bills is on the copy, whatever the old links say.
          const lineIds = (bill.lines || []).map(l => parseInt(l.purchase_order_id, 10)).filter(n => n > 0);
          if (lineIds.length) setLinkedPOIds(prev => [...new Set([...prev, ...lineIds])]);
          setAlertDialog({
            isOpen: true, type: 'info',
            message: `A copy of voided bill ${bill.supplier_reference || bill.bill_number || ''}: the same supplier, POs, lines and prices, as a Draft. Check it (and attach the supplier's PDF again), then save.`
          });
        }
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: 'Failed to load bill: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Pre-fill the form from one or more POs. Used by both the legacy single-PO
  // "Bill this PO" entry point and the new bulk "Generate Bill" combined mode.
  // `append`: add these POs' lines to the bill being entered (Choose POs)
  // instead of starting it from them.
  // A PO line's VAT on the bill: the PO's, else the item's (utils/billReadLines).
  const poLineTax = (poTax, itemTax) => {
    const t = defaultTaxType({ poTax, itemTax });
    return { tax_type: t.value, tax_rate: getTaxRate(t.value) || 0, _taxFrom: t.from, _poTax: poTax, _itemTax: itemTax };
  };

  const loadFromPOs = async (poIds, items = products, { append = false } = {}) => {
    if (!append) setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bills/generate-from-pos`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ poIds, mode: 'combined' })
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        setAlertDialog({
          isOpen: true,
          message: data.error || 'Failed to load PO data',
          type: 'error'
        });
        return;
      }

      // combined mode returns a single object; defensive in case backend returned array
      const d = Array.isArray(data.data) ? data.data[0] : data.data;
      if (!d) return;

      // Cache the linked PO meta so the UI doesn't have to refetch each one
      if (Array.isArray(d.linked_po_meta)) {
        const map = {};
        for (const p of d.linked_po_meta) map[p.id] = p;
        setLinkedPODetailsMap(prev => (append ? { ...prev, ...map } : map));
      }
      // Marked as the PO's own lines, for reconciling with an uploaded invoice
      // (a PO-H gives one haulage line with no PO line key).
      const fromPOs = billLinesFromApi(d.line_items, items).map((l, i) => ({
        ...l,
        // Its VAT: the PO line's, else its item's purchase tax type (the
        // bill's joins in when an invoice line is matched to it —
        // utils/billLineReconcile.mergeLines). Both kept for the Tax cell.
        ...poLineTax(d.line_items?.[i]?.tax_type ?? null, items.find(p => p.id === l.inventory_item_id)?.purchase_tax_type ?? null),
        _fromPo: true,
        _poHaulageLine: d.line_items?.[i]?.kind === 'haulage' && !d.line_items?.[i]?.po_line_key,
        // A Direct Delivery PO's haulage line (after its product): never
        // matched automatically to a goods line of the invoice.
        ...(d.line_items?.[i]?.kind === 'haulage' && d.line_items?.[i]?.po_line_key ? { _poKind: 'haulage' } : {}),
      }));

      // What the PO says of the header, to offer in its fields (2026-10-16).
      const header = {
        supplier: d.account_id ? { id: String(d.account_id), name: d.supplier_name || '', xeroContactId: d.xero_contact_id || '' } : null,
        bill_date: d.bill_date || '', due_date: d.due_date || '', supplier_reference: d.supplier_reference || '',
        poNumbers: (d.linked_po_meta || []).map(p => p.purchase_order_number).filter(Boolean),
      };
      if (append) {
        setLinkedPOIds(prev => [...prev, ...(d.purchase_order_ids || []).filter(id => !prev.includes(id))]);
        setLineItems(prev => [...prev.filter(l => !lineIsEmpty(l)), ...fromPOs]);
        setPoHeader(prev => prev || header);
        return;
      }
      setPoHeader(header);

      if (d.account_id) setSelectedSupplierId(String(d.account_id));
      setSupplierName(d.supplier_name || '');
      setSupplierXeroContactId(d.xero_contact_id || '');
      // A child's PO defaults to its supplier group's parent: to be checked.
      supplierConfirmedRef.current = false;
      setSupplierCheck(supplierCheckFromPos(d));
      setLinkedPOIds(d.purchase_order_ids || []);
      setBillDateFrom(d.bill_date || new Date().toISOString().split('T')[0], d.bill_date ? 'po' : 'typed');
      setDueDate(d.due_date || '');
      setReference(d.reference || '');
      setSupplierReference(d.supplier_reference || '');
      setLineItems(fromPOs.length ? fromPOs : [blankLine()]);
    } catch (error) {
      setAlertDialog({ isOpen: true, message: 'Failed to load PO data: ' + error.message, type: 'error' });
    } finally {
      if (!append) setLoading(false);
    }
  };

  // Backwards-compat wrapper for the single-PO sessionStorage entry point
  const loadFromPO = (poId, items) => loadFromPOs([parseInt(poId, 10)], items);

  // Choose POs → their lines join the bill, each keeping its PO.
  const handleAddPOs = (rows) => {
    setShowPoPicker(false);
    const ids = rows.map(r => r.id).filter(id => !linkedPOIds.includes(id));
    if (ids.length) loadFromPOs(ids, products, { append: true });
  };

  // Taking a PO off the bill takes the lines that bill it too.
  const unlinkPoAndLines = (poId) => {
    const id = parseInt(poId, 10);
    removeLinkedPO(id);
    setPoEditTicks(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith(`${id}|`))));
    setSuppliedCopies(prev => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setLineItems(prev => {
      const kept = prev.filter(l => Number(l.purchase_order_id) !== id);
      return kept.length ? kept : [blankLine()];
    });
    setConfirmUnlinkPo(null);
  };

  // The bill's POs, for the grid's PO column.
  const poOptions = useMemo(() => linkedPOIds.map(id => ({
    value: id,
    label: linkedPODetailsMap[id]?.purchase_order_number || `PO #${id}`,
  })), [linkedPOIds, linkedPODetailsMap]);

  // Lines against the invoice's net total: what is left unallocated, and how
  // many lines bill no PO (one is allowed — a surcharge, or haulage from
  // before PO-Hs existed).
  const allocation = useMemo(() => {
    const real = lineItems.filter(l => !lineIsEmpty(l));
    const payload = billLinesToPayload(real, lineTotals);
    const linesNet = Math.round(payload.reduce((sum, l) => sum + (l.line_amount || 0), 0) * 100) / 100;
    const linesVat = Math.round(payload.reduce((sum, l) => sum + (l.tax_amount || 0), 0) * 100) / 100;
    const typed = invoiceNet === '' || invoiceNet === null ? null : parseFloat(String(invoiceNet).replace(/[£,\s]/g, ''));
    // Before VAT defaults to the lines' total (Jade, 2026-10-05) — shown
    // highlighted until the user checks it against the invoice.
    const netFromLines = !Number.isFinite(typed) && real.length > 0;
    const invoice = Number.isFinite(typed) ? typed : (netFromLines ? linesNet : null);
    const unallocated = invoice === null ? null : Math.round((invoice - linesNet) * 100) / 100;
    // Counted only once lines carry their PO at all: a bill saved before
    // lines had one (or keyed in Xero) is not held to it on an edit.
    const anyLineOnPo = real.some(l => l.purchase_order_id);
    // A job kept with no PO in "PO links from references" was chosen job by
    // job (a haulage link, or nothing to link) — not counted (2026-10-14);
    // nor, on an edit, a line saved with no PO and still without one (the
    // save that made it was checked).
    const savedNoPo = new Set((editSnapshot?.lines || []).filter(l => l && l.id && !l.purchase_order_id).map(l => `bill-${l.id}`));
    const noPoLines = linkedPOIds.length && anyLineOnPo
      ? real.filter(l => !l.purchase_order_id && !l._refNoPo && !savedNoPo.has(String(l.id))).length : 0;
    // A PO on the bill that no line bills would still be marked billed.
    const posWithoutLines = anyLineOnPo
      ? linkedPOIds.filter(id => !real.some(l => Number(l.purchase_order_id) === Number(id)))
      : [];
    // The lines' VAT against the invoice's VAT total, when one was read.
    const vatOff = invoiceVat === null || !real.length ? null : Math.round((linesVat - invoiceVat) * 100) / 100;
    return { linesNet, linesVat, vatOff, invoice, unallocated, noPoLines, posWithoutLines, netFromLines };
  }, [lineItems, invoiceNet, invoiceVat, linkedPOIds, editSnapshot]);
  const netNeedsCheck = allocation.netFromLines && netConfirmed !== allocation.linesNet;

  // ── An uploaded invoice reconciled with the bill's POs (2026-10-05) ──
  // Sure matches are made as the lines arrive (the invoice's, or the PO's).
  // (The effect that makes them is further down, beside the PO lines' facts:
  // a sure split of one invoice line over several PO lines is made there too.)

  // What is left to match or delete before saving.
  // Bulk Add Bills' Replace: the bill's own lines against the new document's,
  // whether or not it has POs.
  const reconcileActive = (!editMode && pdfLinesApplied && linkedPOIds.length > 0) || (bulkReplace && pdfLinesApplied);
  const reconcile = useMemo(() => reconcileState(lineItems, {
    active: reconcileActive,
    isEmpty: lineIsEmpty,
  }), [lineItems, reconcileActive]);

  const poNumberOf = (id) => linkedPODetailsMap[id]?.purchase_order_number || `PO #${id}`;
  // The PO lines no invoice line has yet, to match an invoice line to.
  // A Direct Delivery PO's haulage lines last, said as such — a product on
  // the invoice is the PO's product line, even when its price includes the
  // delivery (2026-10-16).
  const openPoLineOptions = useMemo(() => [...reconcile.notOnInvoice]
    .sort((a, b) => (a._poKind === 'haulage') - (b._poKind === 'haulage'))
    .map(l => ({
      value: l.id,
      label: `${l.purchase_order_number || linkedPODetailsMap[l.purchase_order_id]?.purchase_order_number || 'PO'} · ${l._poKind === 'haulage' ? `Direct Delivery haulage (${l.account_code || '320'}) · ` : ''}${lineLabel(l)}`,
    })), [reconcile.notOnInvoice, linkedPODetailsMap]);

  // Bill quantity against its PO line's (as changed here, if it was): more
  // than 0.5 T (bulk) or 1 bag apart is to be queried with the supplier;
  // less is the gentle note (utils/billQtyCheck).
  // What the bill says of each PO line: its lines that bill it, added up.
  const billQtyByPoLine = useMemo(() => {
    const out = {};
    for (const line of lineItems) {
      if (lineIsEmpty(line) || !line.purchase_order_id || !line.po_line_key) continue;
      const k = `${line.purchase_order_id}|${line.po_line_key}`;
      out[k] = Math.round(((out[k] || 0) + (numOf(line.quantity) || 0)) * 10000) / 10000;
    }
    return out;
  }, [lineItems]);

  // The bill's quantity, price and amount for each PO line, and what they
  // come to in the PO line's own unit — a bill per tonne against a PO in bags
  // (Jade, 2026-10-13; utils/billUnitConversion, the server's rule too).
  const billGroups = useMemo(() => billCostsByKey(billLinesToPayload(
    lineItems.filter(l => !lineIsEmpty(l) && l.purchase_order_id && l.po_line_key).map(withBillFigure), lineTotals
  )), [lineItems]);
  const unitWeightOf = (code) => findItemForLine(products, { code })?.unit_weight_kg ?? null;

  // ── Billed in another unit than its PO line (2026-10-22) ──
  // The bill line's own figures against the PO line as stored: the
  // conversion on evidence (utils/billUnitConversion — the server's rule),
  // and what "the PO takes the bill's units" would make of the PO line
  // (utils/poUnitsFromBill). Worked out from the line alone, so the PO edits
  // below can carry it.
  const unitsOf = (line) => {
    if (transactionType !== 'BILL' || lineIsEmpty(line) || !line.purchase_order_id || !line.po_line_key) return null;
    const found = findPoLine(linkedPODetailsMap, line);
    if (!found || found.po.po_type === 'haulage' || found.li.kind === 'haulage') return null;
    const ctx = poContext[found.po.id] || null;
    const supplied = ctx && !ctx.noSupplied ? (ctx.lines?.[line.po_line_key]?.supplied ?? null) : null;
    const several = lineItems.filter(l => !lineIsEmpty(l) && Number(l.purchase_order_id) === Number(found.po.id) && l.po_line_key === line.po_line_key).length > 1;
    // The invoice's own figures: as billed for a line put in the PO's units,
    // else what the invoice said where it was matched, else the line's.
    const as = line._asBilled;
    const src = as ? { quantity: as.quantity, unit_price: as.unit_amount, bill_unit: as.unit } : (line._bill || withBillFigure(line));
    const q = numOf(src.quantity);
    const p = numOf(src.unit_price);
    const conversion = several || !(q > 0) || !(p > 0) ? null : billUnitConversion({
      billQty: q, billCost: p, billAmount: Math.round(q * p * 100) / 100, billUnit: src.bill_unit ?? null,
      poCost: found.li.unitAmount, poQty: [found.li.quantity, supplied],
      lineWeight: found.li.unitWeightKg, itemWeight: unitWeightOf(found.li.itemCode),
      poWords: found.li.description || null,
    });
    const poNumber = found.po.purchase_order_number || `PO #${found.po.id}`;
    const poTake = conversion ? poUnitsFromBill({ conv: conversion, poLine: found.li, supplied, poNumber }) : null;
    return { found, ctx, supplied, several, conversion, poTake, poNumber, k: costKey(found.po.id, line.po_line_key) };
  };
  // The ticked edits, with "the PO takes the bill's units" where chosen and
  // still possible (the server works the maths out again from the bill).
  const poEdits = useMemo(() => {
    const out = {};
    for (const [poId, byKey] of Object.entries(poEditsTyped)) out[poId] = { ...byKey };
    for (const line of lineItems) {
      if (line._asBilled) continue;
      const u = poEditTicks[line.purchase_order_id && line.po_line_key ? costKey(line.purchase_order_id, line.po_line_key) : '']?.units === 'bill' ? unitsOf(line) : null;
      if (!u || !u.poTake?.ok) continue;
      const poId = u.found.po.id;
      const prev = out[poId]?.[line.po_line_key] || {};
      // Instead of a quantity typed for the PO: the units change carries its own.
      const { quantity: _q, ...rest } = prev;
      out[poId] = { ...(out[poId] || {}), [line.po_line_key]: { ...rest, units: { take: u.poTake } } };
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poEditsTyped, poEditTicks, lineItems, linkedPODetailsMap, poContext, products, transactionType]);

  const billInPoUnits = (poId, key, poQty, { line = null, item = null } = {}) => {
    const found = findPoLine(linkedPODetailsMap, { purchase_order_id: poId, po_line_key: key });
    const g = billGroups.get(costKey(poId, key));
    if (!found || !g) return null;
    const ctx = poContext[poId];
    const edit = transactionType === 'BILL' ? poEdits[poId]?.[key] : null;
    const eff = effectivePoLine(found.li, edit, ctx?.scope || null);
    const ordered = poQuantityFor(found.li, edit, ctx?.scope || null);
    // Being put in the bill's units: compared as it will be.
    const poLine = eff.relabelled ? { ...found.li, quantity: eff.quantity, unitAmount: eff.unitAmount, unitWeightKg: eff.unitWeightKg } : found.li;
    const check = billLineQuantityCheck({
      item: eff.relabelled ? null : item, line, poLine, itemWeight: unitWeightOf(found.li.itemCode),
      billQty: g.qty, billCost: g.cost, billAmount: g.amount, billUnit: g.unit ?? null, poCost: eff.unitAmount, poQty: poQty ?? ordered, ordered,
    });
    return { ...check, ordered, poCost: eff.unitAmount, billAmount: g.amount };
  };

  // ── One bill line over several PO lines (Cam, 2026-10-14; utils/billLineSplit) ──
  // What each PO line counts for the bill: supplied (else ordered), its cost and unit.
  const factsOf = (poId, key) => poLineFacts({
    po: linkedPODetailsMap[poId], key, ctx: poContext[poId] || null,
    edit: transactionType === 'BILL' ? poEdits[poId]?.[key] || null : null,
    itemWeightOf: unitWeightOf,
  });
  const itemOfLine = (line) => products.find(p => p.id === line?.inventory_item_id) || null;

  // Splitting is for a bill whose lines may still change here (a new bill, or
  // one entered here and not yet in Xero) — never a credit.
  const splitAllowed = transactionType === 'BILL' && poLinesEditable && !patchMode && linkedPOIds.length > 0;

  // Sure matches — and sure splits — are made as the lines arrive (the
  // invoice's, the PO's, or the PO lines' facts), highlighted to check.
  useEffect(() => {
    if ((editMode && !bulkReplace) || !pdfLinesApplied) return;
    if (!lineItems.some(isOpenPdfLine) || !lineItems.some(isOpenPoLine)) return;
    const { lines, matched } = autoMatch(lineItems);
    const { lines: next, split } = splitAllowed
      ? autoSplit(lines, { factsOf, itemOf: itemOfLine })
      : { lines, split: 0 };
    if (matched || split) setLineItems(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, pdfLinesApplied, editMode, bulkReplace, linkedPODetailsMap, poContext, splitAllowed]);
  const linkedPos = useMemo(() => linkedPOIds.map(id => linkedPODetailsMap[id]).filter(Boolean), [linkedPOIds, linkedPODetailsMap]);
  // The PO lines a bill line could be split over: the open ones while an
  // invoice is reconciled; otherwise the bill's POs' lines no line bills, and its own.
  const splitCandidatesFor = (line) => (reconcileActive
    ? gridCandidates(lineItems, factsOf)
    : freeCandidates(line, lineItems, linkedPos, factsOf, lineIsEmpty));
  // What to offer: an invoice line on no PO line (reconciling), or a bill
  // line whose quantity covers PO lines no line bills (Edit Bill, a typed line).
  const splitOfferList = useMemo(() => {
    if (!splitAllowed) return [];
    if (reconcileActive) {
      return splitOffers(lineItems, { factsOf, itemOf: itemOfLine })
        .map(offer => ({ offer, source: lineItems.find(l => l.id === offer.sourceId) }))
        .filter(o => o.source);
    }
    const out = [];
    for (const l of lineItems) {
      if (lineIsEmpty(l) || l._poHaulageLine || l._split || (l.purchase_order_id && !l.po_line_key)) continue;
      const offer = findSplit(l, freeCandidates(l, lineItems, linkedPos, factsOf, lineIsEmpty), { item: itemOfLine(l) });
      if (offer) out.push({ offer, source: l });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitAllowed, reconcileActive, lineItems, linkedPos, poContext, poEdits, products]);
  const splitsMade = useMemo(() => appliedSplits(lineItems), [lineItems]);
  // The offers' "Split it" buttons come and go with the lines.
  useSlideOutButton([splitOfferList.length]);
  // The split window: { sourceId, source, candidates, initial }.
  const [splitDialog, setSplitDialog] = useState(null);
  const openSplitDialog = (source, offer = null) => {
    const cands = splitCandidatesFor(source);
    // The line's own product first; the rest after (the user's choice, as a match is).
    const ordered = [...cands.filter(c => sameProduct(source, c)), ...cands.filter(c => !sameProduct(source, c))];
    setSplitDialog({ sourceId: source.id, source, candidates: ordered, initial: offer || findSplit(source, ordered, { item: itemOfLine(source) }) });
  };
  const doSplit = (split) => {
    const res = applySplit(lineItems, split);
    if (res.error) {
      setAlertDialog({ isOpen: true, message: `The line was not split: ${res.error}`, type: 'warning' });
      return false;
    }
    setLineItems(res.lines);
    return true;
  };

  // Is copying the bill's quantity into supplied offered for this PO line?
  // A PO-D line, nothing refusing it, billed here — with no supplied quantity,
  // or one the bill's differs from (Cam, 16 Oct 2026: "offer user to update
  // the PO's supplied qty with the Bill's qty").
  const copyOffered = (poId, key) => {
    const ctx = poContext[poId];
    const s = ctx?.lines?.[key];
    const billQ = billQtyByPoLine[`${poId}|${key}`];
    return transactionType === 'BILL' && !!ctx && !ctx.noSupplied && !!s && !s.billBlock && billQ !== undefined
      && (s.supplied == null || !sameValue('qty', billQ, s.supplied));
  };

  // Is the bill's quantity for this PO line more than the query limit from
  // what was ordered? Then copying it into supplied asks first.
  const copyNeedsConfirm = (poId, key) => {
    const po = linkedPODetailsMap[poId];
    const lines = Array.isArray(po?.line_items) ? po.line_items : [];
    const index = lines.findIndex((li, i) => poLineKey(li, i) === key);
    if (index < 0) return null;
    const li = lines[index];
    // In the PO line's unit: 1.00 T billed copies as 40 × 25kg bag.
    const check = billInPoUnits(poId, key, null, { item: findItemForLine(products, { code: li.itemCode }) || null });
    return check && check.level === 'query'
      ? { poId, key, number: po.purchase_order_number, what: li.description || li.itemCode || 'this line', billQty: check.billQty, ordered: check.ordered }
      : null;
  };
  const tickCopy = (poId, key, on) => {
    if (on) {
      const ask = copyNeedsConfirm(poId, key);
      if (ask) { setCopyConfirm(ask); return; }
    }
    setSuppliedCopies(prev => ({ ...prev, [poId]: { ...(prev[poId] || {}), [key]: on } }));
  };

  // ── Each line against its PO line, field by field (Cam, 2026-10-16) ──
  // Every cell offers the PO's value and the bill's where both exist
  // (utils/billLineFields); "Also update the PO to this" puts the cell's
  // value on the PO line when the bill is saved, through the paths that
  // already exist. Nothing here stops a save.
  const [costChoices, setCostChoices] = useState({});
  // The PO lines whose cost saving these lines would change (the server's
  // rule, utils/billCostWriteBack) — what "Also set the PO's cost" is about.
  const costChangesFor = (lines, goodsIds = linkedPOIds) => (transactionType === 'BILL'
    ? poCostChanges({
      lines: billLinesToPayload(lines.filter(l => !lineIsEmpty(l)), lineTotals).filter(l => goodsIds.includes(Number(l.purchase_order_id))),
      poById: linkedPODetailsMap,
      poEdits: poLinesEditable ? poEdits : {},
      scopes: Object.fromEntries(Object.entries(poContext).map(([id, c]) => [id, c?.scope || null])),
      xeroMade: Object.fromEntries(Object.entries(poContext).map(([id, c]) => [id, !!c?.xeroMade])),
      noSupplied: Object.fromEntries(Object.entries(poContext).map(([id, c]) => [id, !!c?.noSupplied])),
      orderLines: seeCosts ? Object.fromEntries(Object.entries(poContext).map(([id, c]) => [id, c?.lines || {}])) : null,
      itemWeightOf: unitWeightOf,
      supplied: suppliedFromContext(poContext),
    })
    : []);
  const costChanges = useMemo(() => costChangesFor(lineItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lineItems, transactionType, linkedPOIds, linkedPODetailsMap, poEdits, poContext, seeCosts, products, poLinesEditable]);
  const changeByKey = useMemo(() => new Map(costChanges.map(c => [c.key || costKey(c.purchase_order_id, c.po_line_key), c])), [costChanges]);
  // On an edit, what the bill said when it was opened: a cost kept different
  // then starts unticked (review, 2026-10-08).
  const previousBillCosts = useMemo(() => (editMode && editSnapshot ? billCostsByKey(editSnapshot.lines || []) : null), [editMode, editSnapshot]);
  const defaultCostUpdate = useMemo(() => defaultCostTicks(costChanges, { queried: isQueried, previousBillCosts }), [costChanges, isQueried, previousBillCosts]);
  // A Direct Delivery product whose bill price includes the haulage the PO
  // carries on its own line: the product's cost would count it twice, so
  // "Also set the PO's cost" starts unticked (2026-10-16).
  const ddHaulageKeys = useMemo(() => new Set(lineItems.filter(l => l._ddHaulageInPrice && l.purchase_order_id && l.po_line_key)
    .map(l => costKey(l.purchase_order_id, l.po_line_key))), [lineItems]);
  /** Will saving set this PO line's cost to the bill's? The cell's tick, else the default (on; off when Queried, or the price includes the PO's haulage line). */
  const costUpdateOn = (k, { queried = isQueried } = {}) => {
    // A cost that differs from the order's or the contract's (2026-10-28):
    // never set on the PO from a queried bill, and unticked unless ticked.
    const flagged = !!costChecks.get(k)?.check;
    if (flagged && queried) return false;
    if (costChoices[k] !== undefined) return !!costChoices[k];
    if (flagged) return false;
    if (ddHaulageKeys.has(k)) return false;
    return queried === isQueried ? !!defaultCostUpdate[k]
      : !!defaultCostTicks(costChanges, { queried, previousBillCosts })[k];
  };

  // The bill's lines on each PO line (several on one: compared together).
  const linesPerPoLine = useMemo(() => {
    const m = new Map();
    for (const l of lineItems) {
      if (lineIsEmpty(l) || !l.purchase_order_id || !l.po_line_key) continue;
      const k = costKey(l.purchase_order_id, l.po_line_key);
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [lineItems]);
  const savedPoIds = useMemo(() => new Set((editSnapshot?.lines || []).map(l => Number(l.purchase_order_id)).filter(Boolean)), [editSnapshot]);

  // A bill line on a goods PO line, with what the cells need to know.
  const lineCtx = (line) => {
    if (transactionType !== 'BILL' || lineIsEmpty(line) || !line.purchase_order_id || !line.po_line_key) return null;
    if (!linkedPOIds.includes(Number(line.purchase_order_id))) return null;
    const found = findPoLine(linkedPODetailsMap, line);
    if (!found || found.po.po_type === 'haulage' || found.li.kind === 'haulage') return null;
    const st = String(found.po.status || '').toUpperCase();
    if (st === 'DELETED' || st === 'VOIDED') return null;
    const ctx = poContext[found.po.id] || null;
    const k = costKey(found.po.id, line.po_line_key);
    const supplied = ctx && !ctx.noSupplied ? (ctx.lines?.[line.po_line_key]?.supplied ?? null) : null;
    const several = (linesPerPoLine.get(k) || 0) > 1;
    // Billed in another unit than the PO line (per kg against bags…), read on
    // evidence from the invoice's own figures (utils/billUnitConversion).
    const units = unitsOf(line);
    const src = line._bill || withBillFigure(line);
    const q = numOf(src.quantity);
    const p = numOf(src.unit_price);
    const conversion = line._asBilled ? null : (units?.conversion || null);
    const take = conversion ? convertToPoUnits({ quantity: q, unitPrice: p, conv: conversion }) : null;
    let unitsBlock = null;
    if (take && !take.ok) unitsBlock = take.reason;
    else if (take && !pushSettings.asBilledReady) unitsBlock = 'Not ready — the database update bill_line_as_billed.sql has not been run.';
    else if (take && !poLinesEditable) unitsBlock = 'This bill is in Xero already — its lines stay as they are.';
    const splitFamily = ctx?.lines?.[line.po_line_key]?.source === 'split_family';
    const billedElsewhere = st === 'BILLED' && !savedPoIds.has(Number(found.po.id));
    // The other way: the PO takes the bill's units (2026-10-22), greyed with
    // the reason — offered for a line put in the PO's units too (it goes back).
    const poTake = units?.conversion ? units.poTake : null;
    let poUnitsBlock = null;
    if (poTake) {
      const number = found.po.purchase_order_number || `PO #${found.po.id}`;
      const what = found.li.description || found.li.itemCode || 'this line';
      if (!poLinesEditable || patchMode) poUnitsBlock = 'This bill is in Xero already — its POs are not changed from it.';
      else if (ctx?.scope?.mode === 'xero_made') poUnitsBlock = ctx.scope.reason;
      else if (ctx?.scope?.invoiced) poUnitsBlock = `Its order is invoiced — the invoice states the quantity and price, so ${number}'s line keeps its units.`;
      else if (splitFamily) poUnitsBlock = `${number}'s line is shared by split orders — each order's units are its own.`;
      else if (billedElsewhere) poUnitsBlock = `${number} is already billed by another bill, which counts ${what} as it is.`;
      else if (!poTake.ok) poUnitsBlock = poTake.reason;
    }
    const poUnitsOn = !!poTake?.ok && !poUnitsBlock && poEditTicks[k]?.units === 'bill' && !line._asBilled;
    const unitsKeep = !!conversion && !line._asBilled && !poUnitsOn && poEditTicks[k]?.units === 'keep';
    return {
      po: found.po, li: found.li, ctx, k, supplied, several, conversion, take, unitsBlock,
      poTake, poUnitsBlock, poUnitsOn, unitsKeep,
      // xeroMade here = no supplied figure (made in Xero while the Xero PO sync is on — 2026-10-20).
      scope: ctx?.scope || null, xeroMade: !!ctx?.noSupplied,
      splitFamily,
      billedElsewhere,
      poNumber: found.po.purchase_order_number || `PO #${found.po.id}`,
    };
  };

  // The line's quantity in the PO line's unit (converted where it was billed
  // in another), for the quantity notes and the save's question — what the
  // line will save, the bill's unless the user chose otherwise.
  const billQtyOf = (line, c) => {
    if (line._asBilled) return numOf(line.quantity);
    // The PO is being put in the bill's units: both in the bill's.
    if (c.poUnitsOn) return numOf(line.quantity);
    if (c.conversion) return c.conversion.quantity;
    return numOf(line.quantity);
  };
  /**
   * The PO side in the unit the bill's quantity is compared in: as stored, or
   * — when the PO is being put in the bill's units — converted with it
   * (supplied ÷ the ratio too), and the unit kind those figures count.
   */
  const poSideOf = (line, c, item) => {
    if (c.poUnitsOn) {
      return { ordered: c.poTake.quantity, supplied: c.supplied === null ? null : c.poTake.supplied,
        kind: c.poTake.toUnit === 't' ? 'tonnes' : 'bags' };
    }
    const { kind } = c.conversion ? { kind: c.conversion.kind } : quantityUnitKind({ item, line, poLine: c.li });
    return { ordered: numOf(c.li.quantity), supplied: c.supplied, kind };
  };

  // ── A cost that differs from the order's or the contract's (Cam, 2026-10-28) ──
  // "make the cost field mandatory and not chosen by default … so that a bill
  // or order's cost price isn't accepted by users where there is a potential
  // discrepancy" (utils/billCostCheck — the server's rule too). Per PO line:
  // the bill's cost in the PO line's unit against the PO line's cost, the
  // order line's (billing-context `cost_check.order_cost`) and the contract
  // Pricing & Costing finds for the order line (`cost_check.contract`, per
  // the PO line's unit). `required`: the cost must be chosen before saving —
  // a bill that can still change its lines here, not Queried, and on Edit
  // Bill not answered on an earlier save.
  const costChecks = useMemo(() => {
    const out = new Map();
    if (transactionType !== 'BILL') return out;
    const groups = new Map();
    lineItems.forEach((l, i) => {
      if (lineIsEmpty(l) || !l.purchase_order_id || !l.po_line_key) return;
      const k = checkKey(l.purchase_order_id, l.po_line_key);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push({ l, n: i + 1 });
    });
    for (const [k, group] of groups) {
      const c = lineCtx(group[0].l);
      if (!c) continue;
      const facts = poContext[c.po.id]?.lines?.[group[0].l.po_line_key]?.cost_check || null;
      const figures = billLinesToPayload(group.map(({ l }) => ({ ...l, unit_price: billFigureOf(l) ?? '' })), lineTotals);
      const bill = billCostInPoUnit({
        lines: figures, li: c.li, supplied: c.xeroMade ? null : c.supplied, itemWeight: unitWeightOf(c.li.itemCode),
      });
      if (!bill) continue;
      const check = costCheck({ bill: bill.cost, po: c.li.unitAmount, order: facts?.order_cost ?? null, contract: facts?.contract || null });
      const fromServer = serverCostRequired.has(k);
      if (!check && !fromServer) continue;
      const answeredBefore = editMode && !!recordedCostChoices[k];
      const required = !isQueried && poLinesEditable && !patchMode && (fromServer || (!!check && !answeredBefore));
      const kg = numOf(c.li.unitWeightKg) ?? unitWeightOf(c.li.itemCode);
      out.set(k, {
        k, check, required, fromServer, answeredBefore, c, facts, kg,
        conversion: bill.conversion,
        // A cost per the PO line's unit, in the unit the line is in now
        // (still in the bill's: × the conversion's ratio).
        toLine: (v) => (v === null || v === undefined ? null : (bill.conversion ? r4c(v * bill.conversion.ratio) : r4c(v))),
        lineIds: group.map(x => x.l.id),
        lineNumbers: group.map(x => x.n),
        text: check ? costCheckText(check, {
          kg, poNumber: c.poNumber, orderNumber: facts?.order_number || null, deliveryDate: facts?.delivery_date || null,
          billAsBilled: bill.conversion ? `${costMoney(bill.asBilled)} as billed` : null,
        }) : null,
        why: check ? costCheckWhy(check) : null,
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, transactionType, linkedPOIds, linkedPODetailsMap, poContext, products, isQueried, poLinesEditable, patchMode, editMode, recordedCostChoices, serverCostRequired, poEditTicks, pushSettings.asBilledReady]);
  /**
   * What a bill line's price is per (2026-10-28, units clear on every price):
   * as billed — a tonne or a kg the invoice printed — else its PO line's unit;
   * null = the grid's own reading (the item's). utils/unitPriceLabel facts.
   */
  const billLinePriceFacts = (line) => {
    const u = String(line?.bill_unit || '').toLowerCase();
    if (u === 't' || u === 'kg') return { unit: u };
    const found = line?.purchase_order_id && line?.po_line_key ? findPoLine(linkedPODetailsMap, line) : null;
    if (found && found.li && found.li.kind !== 'haulage' && found.po?.po_type !== 'haulage') {
      return { unitWeightKg: found.li.unitWeightKg, itemUnitWeightKg: unitWeightOf(found.li.itemCode) };
    }
    return null;
  };
  // "/25kg bag" — the PO line's unit; "/T", "/kg" — the bill's as billed, else the PO line's.
  const poPerOf = (c) => perUnitOf({ unitWeightKg: c.li.unitWeightKg, itemUnitWeightKg: unitWeightOf(c.li.itemCode) }).per;
  const billPerOf = (line, c) => {
    const u = String(line?._asBilled?.unit || line?.bill_unit || '').toLowerCase();
    return u === 't' || u === 'kg' ? perUnitOf({ unit: u }).per : poPerOf(c);
  };
  const costInfoOf = (line) => (line && line.purchase_order_id && line.po_line_key
    ? costChecks.get(checkKey(line.purchase_order_id, line.po_line_key)) || null : null);
  // Has this line's cost been chosen (a pick, or a price typed)?
  const costChosen = (line) => !!line._costPick && line.unit_price !== '' && line.unit_price !== null && line.unit_price !== undefined;
  // The lines whose cost is still to choose: Save waits for them.
  const costChoiceMissing = useMemo(() => {
    const numbers = [];
    lineItems.forEach((l, i) => {
      const info = costInfoOf(l);
      if (info && info.required && !costChosen(l)) numbers.push(i + 1);
    });
    return numbers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, costChecks]);
  // A flagged line's cost starts empty — its price kept aside — and comes
  // back when it no longer needs choosing (the bill Queried, the PO line
  // changed): never chosen by default.
  useEffect(() => {
    let changed = false;
    const next = lineItems.map(l => {
      const info = costInfoOf(l);
      const required = !!info?.required;
      // (A price that arrives on an emptied line — the invoice's matched onto
      // the PO's — is the bill's figure now: kept aside and emptied again.)
      if (required && !l._costPick && l.unit_price !== '' && l.unit_price !== null && l.unit_price !== undefined) {
        changed = true;
        return { ...l, _billPrice: l.unit_price, _costBlanked: true, unit_price: '' };
      }
      if (!required && l._costBlanked && !l._costPick) {
        changed = true;
        const { _costBlanked: _b, _billPrice: kept, ...rest } = l;
        // A price that arrived since stands; else the one kept aside comes back.
        const has = l.unit_price !== '' && l.unit_price !== null && l.unit_price !== undefined;
        return { ...rest, unit_price: has ? l.unit_price : (kept ?? '') };
      }
      return l;
    });
    if (changed) setLineItems(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costChecks]);
  /** Put a cost on every line of this PO line (a card pick or a note's action). */
  const pickCost = (info, source, value) => {
    setLineItems(prev => prev.map(l => (info.lineIds.includes(l.id) ? withCostPick(l, source, value) : l)));
  };
  /** Query the bill from a line's note: the line keeps the cost as invoiced, and no PO cost changes. */
  const queryFromCost = (info) => {
    if (isQueried || patchMode) return;
    if (!(editMode && approvedInXero)) {
      setLineItems(prev => prev.map(l => (info.lineIds.includes(l.id) ? withoutCostPick(l) : l)));
    }
    markQueried();
  };

  // Put one cell's value on the line.
  const patchLine = (id, changes) => setLineItems(prev => prev.map(l => (l.id === id ? { ...l, _highlight: false, ...changes } : l)));
  const takeCell = (line, col, value, side) => {
    if (col === 'item') {
      const item = side === 'bill' && line._bill?.inventory_item_id
        ? products.find(p => p.id === line._bill.inventory_item_id) || null
        : findItemForLine(products, { code: value });
      const changes = { product_code: item ? (item.item_code || value) : value, inventory_item_id: item?.id ?? null };
      // A different item brings its words (Cam, 2026-10-28 — utils/pickedItemWords):
      // the PO's item takes the PO line's own words for it (what a bill line
      // on a PO line starts from), any other the item's purchase description,
      // else its name. The PO's and the bill's words stay one click away on
      // the Description cell.
      if (item && isItemChange(line, item)) {
        const c = side === 'po' ? lineCtx(line) : null;
        const poWords = c
          ? lineFieldSides({ line, li: c.li, supplied: c.supplied, conversion: c.conversion, ddSuffix: !!line._ddSuffix }).desc.po?.value
          : null;
        Object.assign(changes, poWords ? { product_name: poWords } : wordsOnItemPick('bill', line, item));
      }
      patchLine(line.id, changes);
    } else if (col === 'desc') patchLine(line.id, { product_name: value });
    else if (col === 'qty') patchLine(line.id, { quantity: formatDecimalInput(value) });
    // A cost taken from the card is the line's choice (the cost check, 2026-10-28).
    else if (col === 'price') setLineItems(prev => prev.map(l => (l.id === line.id ? withCostPick(l, side === 'po' ? 'po' : 'bill', value) : l)));
    else if (col === 'account') patchLine(line.id, { account_code: value || '' });
    else if (col === 'tax') patchLine(line.id, { tax_type: value || null, tax_rate: getTaxRate(value) || 0 });
  };
  const tickPoEdit = (k, field, on) => setPoEditTicks(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [field]: on } }));
  // The three ways for a line billed in another unit (2026-10-22): the bill
  // in the PO's units, the PO in the bill's, or both kept as they are.
  const unitsChoice = (k, v) => setPoEditTicks(prev => ({ ...prev, [k]: { ...(prev[k] || {}), units: v, ...(v === 'bill' ? { qty: false } : {}) } }));
  // A cost chosen for the line goes with a units change (it was in the old
  // unit): the bill's price comes back first, and the cost is chosen again.
  const toPoUnits = (line, c) => {
    if (!c.take?.ok || c.unitsBlock) return;
    setLineItems(prev => prev.map(l => (l.id === line.id ? convertLine(withoutCostPick(l), c.take) : l)));
    unitsChoice(c.k, null);
  };
  const toBillUnits = (line, c = null) => {
    setLineItems(prev => prev.map(l => (l.id === line.id ? unconvertLine(withoutCostPick(l)) : l)));
    if (c) unitsChoice(c.k, 'keep');
  };
  const poToBillUnits = (line, c) => {
    if (!c.poTake?.ok || c.poUnitsBlock) return;
    if (line._asBilled) setLineItems(prev => prev.map(l => (l.id === line.id ? unconvertLine(withoutCostPick(l)) : l)));
    unitsChoice(c.k, 'bill');
  };
  const markQueried = () => {
    if (isQueried || patchMode) return;
    if (editMode && approvedInXero) { setConfirmQueryApproved(true); return; }
    setIsQueried(true);
    setStatus('Draft');
  };
  // The maths of a line put in the PO's units (or about to be), in one line.
  const unitsMaths = (line, c) => {
    if (line._asBilled) {
      const as = line._asBilled;
      return unitsMathsText({ ok: true, asBilled: as, quantity: numOf(line.quantity), unit: line.bill_unit || 'pack', unitPrice: numOf(line.unit_price), amount: as.line_amount }, { kg: as.kg });
    }
    return c?.take?.ok ? unitsMathsText(c.take, c.conversion) : '';
  };

  const CELL_LABEL = { item: 'Item', desc: 'Description', qty: 'Quantity', price: 'Unit price', account: 'Account', tax: 'Tax type' };
  /**
   * The Tax cell's card (2026-10-16): the PO line's VAT, the item's purchase
   * VAT, the bill's (its own VAT %, or the invoice's VAT total as one rate) —
   * defaulted in that order (utils/billReadLines.defaultTaxType). Any line,
   * on a PO line or not (a haulier's charge on a PO-H has no PO tax).
   */
  const taxChoice = (line) => {
    if (transactionType !== 'BILL' || lineIsEmpty(line)) return null;
    const sides = [];
    if (line.purchase_order_id && hasTax(line._poTax)) {
      sides.push({ key: 'po', tag: 'PO', value: String(line._poTax).toUpperCase(), hint: poNumberOf(line.purchase_order_id) });
    }
    const item = line.inventory_item_id ? products.find(p => p.id === line.inventory_item_id) : null;
    if (item && hasTax(item.purchase_tax_type)) {
      sides.push({ key: 'item', tag: 'Item', value: String(item.purchase_tax_type).toUpperCase(), hint: `${item.item_code || 'the item'}'s purchase VAT` });
    }
    if (line._billTax?.value) {
      sides.push({ key: 'bill', tag: 'Bill', value: String(line._billTax.value).toUpperCase(), hint: line._billTax.reason || undefined });
    }
    if (!sides.length) return null;
    const current = String(line.tax_type || '').toUpperCase();
    const inUse = sides.find(x => x.value === current);
    const differs = new Set(sides.map(x => x.value)).size > 1;
    // Worked out from the invoice's VAT total alone: shown, so it is checked.
    const inferred = !!inUse && inUse.key === 'bill' && line._billTax?.from === 'invoice' && sides.length === 1;
    if (!differs && !inferred && inUse) return null;
    return {
      label: CELL_LABEL.tax,
      choices: sides.map(x => ({ key: x.key, tag: x.tag, text: getTaxTypeLabel(x.value), hint: x.hint, onPick: () => takeCell(line, 'tax', x.value, x.key) })),
      active: inUse ? inUse.key : null,
      differs: differs || !inUse,
      forceMarker: inferred,
      other: { label: 'Choose another…' },
    };
  };
  /**
   * A line whose cost differs from the order's or the contract's (2026-10-28):
   * the price cell's card — the bill's cost, the PO's, the order's, the
   * contract's (each in the unit the line is in now), or type another — and
   * nothing chosen until the user chooses (`required`).
   */
  const costCard = (line, c, info, extras = []) => {
    const facts = info.facts || {};
    const per = perUnitText(info.kg);
    // What the line's price is per now (each figure below is in it).
    const linePer = perUnitOf(billLinePriceFacts(line) || { unitWeightKg: c.li.unitWeightKg, itemUnitWeightKg: unitWeightOf(c.li.itemCode) }).per;
    const poUnitHint = (v) => (info.conversion && v !== null ? `${costMoney(v)}${per} on ${c.poNumber}'s unit` : undefined);
    const billNow = billFigureOf(line);
    const poCost = numOf(c.li.unitAmount) > 0 ? numOf(c.li.unitAmount) : null;
    const orderCost = numOf(facts.order_cost) > 0 ? numOf(facts.order_cost) : null;
    const contract = facts.contract && numOf(facts.contract.cost) > 0 ? facts.contract : null;
    const choices = [];
    if (billNow !== null) {
      choices.push({ key: 'bill', tag: 'Bill', text: `${costMoney(billNow)}${linePer} — ${line._bill || line._fromPdf || line._asBilled ? "the invoice's" : 'as on the line'}`,
        hint: info.conversion ? `${costMoney(info.check?.bill ?? null)}${per} on ${c.poNumber}'s unit` : undefined,
        onPick: () => pickCost(info, 'bill', billNow) });
    }
    const samePoOrder = poCost !== null && orderCost !== null && Math.abs(poCost - orderCost) < 0.00005;
    if (poCost !== null) {
      choices.push({ key: 'po', tag: 'PO', text: `${costMoney(info.toLine(poCost))}${linePer} — ${samePoOrder ? `the order's cost (${[c.poNumber, facts.order_number].filter(Boolean).join(', ')})` : `${c.poNumber}'s cost`}`,
        hint: poUnitHint(poCost), onPick: () => pickCost(info, 'po', info.toLine(poCost)) });
    }
    if (orderCost !== null && !samePoOrder) {
      choices.push({ key: 'order', tag: 'Order', text: `${costMoney(info.toLine(orderCost))}${linePer} — ${facts.order_number || 'the order'}'s cost`,
        hint: poUnitHint(orderCost), onPick: () => pickCost(info, 'order', info.toLine(orderCost)) });
    }
    if (contract) {
      choices.push({ key: 'contract', tag: 'Contract', text: `${costMoney(info.toLine(contract.cost))}${linePer} — ${contractName(contract)}'s cost`,
        hint: [contract.detail, facts.delivery_date ? `in force on ${formatDate(facts.delivery_date)}` : null, poUnitHint(contract.cost)].filter(Boolean).join(' · ') || undefined,
        onPick: () => pickCost(info, 'contract', info.toLine(contract.cost)) });
    }
    const chosen = costChosen(line);
    const required = info.required && !chosen;
    let active = null;
    if (chosen && line._costPick !== 'typed') active = line._costPick;
    else if (!required && !chosen) {
      const hit = choices.find(x => sameValue('price', line.unit_price, x.key === 'bill' ? billNow
        : x.key === 'po' ? info.toLine(poCost) : x.key === 'order' ? info.toLine(orderCost) : info.toLine(contract?.cost)));
      active = hit ? hit.key : null;
    }
    return {
      label: CELL_LABEL.price, differs: true, forceMarker: true, required, markerText: 'Choose',
      other: { label: 'Type another' }, active, choices,
      extra: <>
        {info.why && <div className="field-choice-note is-blocked">{info.why}</div>}
        {isQueried && info.check && <div className="field-choice-note">The bill is queried — the line keeps the cost as invoiced, and {c.poNumber}&apos;s cost is not changed.</div>}
        {info.conversion && <div className="field-choice-note">{conversionCostText(info.conversion)}</div>}
        {extras}
      </>,
    };
  };

  /** A line's note for a cost that differs from the order's or the contract's (2026-10-28). */
  const costFlag = (line, c, info) => {
    const facts = info.facts || {};
    const chosen = info.lineIds.every(id => { const l = lineItems.find(x => x.id === id); return l && costChosen(l); });
    const poCost = numOf(c.li.unitAmount) > 0 ? numOf(c.li.unitAmount) : null;
    const orderCost = numOf(facts.order_cost) > 0 ? numOf(facts.order_cost) : poCost;
    const contract = facts.contract && numOf(facts.contract.cost) > 0 ? facts.contract : null;
    const billNow = billFigureOf(line);
    const words = { bill: "the bill's cost", po: `${c.poNumber}'s cost`, order: "the order's cost", contract: `${contractName(contract)}'s cost`, typed: 'a cost typed in' };
    const head = info.text || "This line's cost must be chosen — the bill, order and contract costs differ";
    let text;
    if (isQueried && info.check) text = `${head}. The bill is queried — the line keeps the cost as invoiced (${costMoney(billNow)}) and ${c.poNumber}'s cost is not changed.`;
    else if (chosen) text = `${head} — ${line._costPick === 'typed' ? `${costMoney(line.unit_price)} typed in` : `${words[line._costPick] || 'a cost'} chosen (${costMoney(line.unit_price)})`}.`;
    else if (info.answeredBefore && !info.required) text = `${head} — chosen on an earlier save (${words[recordedCostChoices[info.k]] || recordedCostChoices[info.k]}).`;
    else text = `${head}. ${info.why || 'Choose the cost this line takes — or query the bill.'}`;
    // Queried: the line keeps the cost as invoiced — nothing to choose.
    const canAct = poLinesEditable && !patchMode && !isQueried;
    const isPick = (src) => chosen && line._costPick === src;
    const actions = [];
    if (canAct) actions.push({ key: 'query', label: 'Query the bill', icon: 'help', onClick: () => queryFromCost(info) });
    if (canAct && billNow !== null) actions.push({ key: 'bill', label: "Accept the bill's cost", icon: 'check', disabled: isPick('bill'), onClick: () => pickCost(info, 'bill', billNow) });
    // The order's cost only where it is not the bill's (the same figure twice is no choice).
    if (canAct && orderCost !== null && (!info.check || costsDiffer(orderCost, info.check.bill))) actions.push({ key: 'order', label: "Use the order's cost", icon: 'receipt_long', disabled: isPick(orderCost === poCost ? 'po' : 'order'),
      onClick: () => pickCost(info, orderCost === poCost ? 'po' : 'order', info.toLine(orderCost)) });
    if (canAct && contract) actions.push({ key: 'contract', label: "Use the contract's cost", icon: 'handshake', disabled: isPick('contract'), onClick: () => pickCost(info, 'contract', info.toLine(contract.cost)) });
    return {
      key: 'cost-check', tone: info.required && !chosen ? 'warn' : 'info', icon: info.required && !chosen ? 'price_change' : 'price_check',
      text, actions,
    };
  };

  /** The cell's card: the PO's value, the bill's, your own — and "Also update the PO to this". */
  const cellChoice = (line, col) => {
    if (col === 'tax') return taxChoice(line);
    const c = lineCtx(line);
    if (!c) return null;
    const sides = lineFieldSides({ line, li: c.li, supplied: c.supplied, conversion: c.conversion, ddSuffix: !!line._ddSuffix });
    const f = sides[col];
    if (!f) return null;
    const picker = col === 'item' || col === 'account' || col === 'tax';
    const other = { label: picker ? 'Choose another…' : 'Type another' };
    const blockArgs = { editable: poLinesEditable && !patchMode, scope: c.scope, splitFamily: c.splitFamily, supplied: c.supplied,
      billedElsewhere: c.billedElsewhere, several: c.several, unconverted: !!c.conversion && !line._asBilled, poNumber: c.poNumber };
    const tickRow = (field, label, on, onChange, reason) => (
      <div key={field}>
        <TickCheckbox id={`po-update-${line.id}-${field}`} checked={!!on && !reason} disabled={!!reason}
          onChange={(e) => onChange(typeof e === 'boolean' ? e : !!e?.target?.checked)} label={label} />
        {reason && <div className="field-choice-note is-blocked">{reason}</div>}
      </div>
    );
    const extras = [];
    // The PO's cost from the bill (the write-back — on by default).
    if (col === 'price') {
      const change = changeByKey.get(c.k);
      if (change) {
        const on = costUpdateOn(c.k);
        const ol = change.order_line_cost !== undefined && change.order_line_moves
          ? ` (its order line ${change.order_line_cost === null ? 'not set' : money(change.order_line_cost)} → ${money(change.order_line_cost_new)})` : '';
        const label = change.order_line_only
          ? `Also set the order line's cost to ${money(change.order_line_cost_new)} (${c.poNumber} has it already)`
          : `Also set ${c.poNumber}'s cost to ${money(change.bill_cost)}${change.cost_maths ? ` (${change.cost_maths})` : ''}${ol}`;
        const queriedBlock = isQueried && costChecks.get(c.k)?.check
          ? `The bill is queried — ${c.poNumber}'s cost is not changed from it.` : null;
        extras.push(tickRow('cost', label, on, (v) => setCostChoices(prev => ({ ...prev, [c.k]: v })), queriedBlock));
        if (ddHaulageKeys.has(c.k)) extras.push(<div key="dd-cost" className="field-choice-note">The bill&apos;s price includes the delivery {c.poNumber} carries on its own haulage line — unticked so the product&apos;s cost does not count it twice.</div>);
        if (change.xero_made && !change.order_line_only) extras.push(<div key="xero" className="field-choice-note">Made in Xero — the new cost is sent to Xero after the bill is saved.</div>);
      }
    }
    // The PO's quantity / words from the line (off unless ticked).
    if (col === 'qty' && !f.units && !sameValue('qty', line.quantity, c.li.quantity)) {
      const reason = poUpdateBlock('qty', blockArgs);
      extras.push(tickRow('qty', `Also change ${c.poNumber}'s quantity to ${formatQuantity(line.quantity)}`, poEditTicks[c.k]?.qty, (v) => tickPoEdit(c.k, 'qty', v), reason));
    }
    if (col === 'desc' && f.po && !sameValue('desc', line.product_name, f.po.value)) {
      const reason = poUpdateBlock('desc', blockArgs);
      extras.push(tickRow('desc', `Also put these words on ${c.poNumber}'s line`, poEditTicks[c.k]?.desc, (v) => tickPoEdit(c.k, 'desc', v), reason));
    }
    if (col === 'item' && f.differs) extras.push(<div key="item" className="field-choice-note is-blocked">{poUpdateBlock('item', blockArgs)}</div>);

    // The bill's cost differs from the order's or the contract's (2026-10-28):
    // the card offers each, and a cost must be chosen — nothing by default.
    const costInfo = col === 'price' ? costChecks.get(c.k) : null;
    if (costInfo) return costCard(line, c, costInfo, extras);

    // Billed in another unit: quantity and price go together — the PO's
    // units (the maths done) or the bill's own.
    if (f.units && (col === 'qty' || col === 'price')) {
      const maths = unitsMaths(line, c);
      if (col === 'qty') {
        const as = line._asBilled;
        const kg = as?.kg ?? c.conversion?.kg ?? null;
        const poText = as
          ? `${quantityInUnit(numOf(line.quantity), line.bill_unit || 'pack', kg)} at ${money(line.unit_price)} — the bill in the PO's units`
          : (c.take?.ok ? `${quantityInUnit(c.take.quantity, c.take.unit, kg)} at ${money(c.take.unitPrice)} — the bill in the PO's units` : "The bill in the PO's units");
        const billFigures = as
          ? `${quantityInUnit(as.quantity, as.unit, kg)} at ${money(as.unit_amount)}`
          : `${quantityInUnit(numOf(line.quantity), line.bill_unit || null, kg)} at ${money(line.unit_price)}`;
        const poTake = c.poTake;
        const poBlock = c.poUnitsBlock;
        const poNow = poTake?.ok ? quantityInUnit(poTake.before.quantity, poTake.fromUnit, poTake.fromKg) : formatQuantity(c.li.quantity);
        const choices = [
          { key: 'po', tag: 'PO', text: poText, disabled: !as && !!c.unitsBlock, title: !as ? c.unitsBlock || undefined : undefined, onPick: () => toPoUnits(line, c) },
          { key: 'bill', tag: 'Bill', text: `${billFigures} — as billed, ${c.poNumber} kept as it is (${poNow})`, onPick: () => toBillUnits(line, c) },
        ];
        if (poTake) {
          choices.push({ key: 'po-units', tag: 'To PO',
            text: poTake.ok ? `${billFigures} — as billed, and ${c.poNumber} in the bill's units (${poUnitsShortText(poTake)})` : `${c.poNumber} in the bill's units`,
            hint: poTake.ok ? `${poTake.text}${c.li.orderLineId ? '; its order line follows' : ''}${c.ctx?.xeroMade ? XERO_COPY_FOLLOWS : ''}` : undefined,
            disabled: !!poBlock, title: poBlock || undefined, onPick: () => poToBillUnits(line, c) });
        }
        return {
          label: CELL_LABEL.qty, differs: true, active: as ? 'po' : (c.poUnitsOn ? 'po-units' : 'bill'), forceMarker: true, other, choices,
          extra: <>{maths && <div className="field-choice-note">{maths}</div>}
            {c.unitsBlock && !as && <div className="field-choice-note is-blocked">The bill in the PO&apos;s units: {c.unitsBlock}</div>}
            {poTake && poBlock && <div className="field-choice-note is-blocked">{c.poNumber} in the bill&apos;s units: {poBlock}</div>}</>,
        };
      }
      // The PO being put in the bill's units: its cost in them is the PO side.
      const poCost = c.poUnitsOn ? c.poTake.unitAmount : numOf(c.li.unitAmount);
      const asB = line._asBilled;
      const billPrice = asB && numOf(line.quantity) > 0
        ? Math.round((numOf(asB.line_amount) / numOf(line.quantity)) * 10000) / 10000
        : numOf(line._bill?.unit_price ?? line.unit_price);
      const priceActive = poCost > 0 && sameValue('price', line.unit_price, poCost) ? 'po'
        : (sameValue('price', line.unit_price, billPrice) ? 'bill' : null);
      const samePer = !!asB || c.poUnitsOn;
      return {
        label: CELL_LABEL.price, differs: true, forceMarker: true, other,
        active: priceActive,
        choices: [
          { key: 'po', tag: 'PO', text: poCost > 0 ? `${money(poCost)}${c.poUnitsOn ? billPerOf(line, c) : poPerOf(c)}${c.poUnitsOn ? ` — ${c.poNumber}'s, in the bill's units` : ''}` : 'None (£0)', disabled: !samePer || !(poCost > 0),
            title: !samePer ? "The bill is in another unit — choose whose units on the quantity first" : (!(poCost > 0) ? 'The PO line has no cost' : undefined),
            onPick: () => takeCell(line, 'price', poCost, 'po') },
          { key: 'bill', tag: 'Bill', text: `${money(billPrice)}${asB ? poPerOf(c) : billPerOf(line, c)} — ${asB ? "the bill's, in the PO's units" : 'as billed'}`, onPick: () => takeCell(line, 'price', billPrice, 'bill') },
        ],
        extra: <>{maths && <div className="field-choice-note">{maths}</div>}{extras}</>,
      };
    }

    if (col === 'desc' && line._ddSuffix) {
      extras.push(<div key="dd" className="field-choice-note">{c.poNumber} is Direct Delivery and the bill&apos;s price includes the delivery{line._ddHaulageInPrice ? ` (its haulage line's ${money(directDeliveryInPrice({ po: c.po, li: c.li, billPrice: billFigureOf(line) }).perUnit || 0)} a unit)` : ''}.</div>);
    }
    if (!f.differs && !extras.length) return null;
    const choices = [];
    const poHint = col === 'qty' && c.xeroMade ? 'made in Xero — no supplied figure' : undefined;
    if (f.po) choices.push({ key: 'po', tag: 'PO', text: f.po.text, hint: poHint, onPick: () => takeCell(line, col, f.po.value, 'po') });
    // A Direct Delivery product whose price includes the delivery: the PO's words without the note too.
    if (col === 'desc' && f.po?.plain) choices.push({ key: 'po-plain', tag: 'PO', text: f.po.plain, hint: 'without "(includes direct delivery)"', onPick: () => takeCell(line, col, f.po.plain, 'po') });
    if (f.bill) choices.push({ key: 'bill', tag: 'Bill', text: f.bill.text, onPick: () => takeCell(line, col, f.bill.value, 'bill') });
    const active = col === 'desc' && f.po?.plain && !f.active && sameValue('desc', line.product_name, f.po.plain) ? 'po-plain' : f.active;
    return {
      label: CELL_LABEL[col], choices, active, differs: f.differs || (col === 'desc' && !!f.po?.plain), other, forceMarker: extras.length > 0,
      extra: extras.length ? <>{extras}</> : null,
    };
  };

  // Gentle notes under the lines, each with its one-click actions. None of
  // them stops a save (Cam, 2026-10-16: "less restricted, just gentle guiding").
  const lineFlags = useMemo(() => {
    const flags = {};
    const add = (id, f) => { (flags[id] = flags[id] || []).push(f); };
    for (const line of lineItems) {
      const c = lineCtx(line);
      if (!c) continue;
      // A cost that differs from the order's or the contract's (2026-10-28):
      // said on the PO line's first bill line, with its one-click choices.
      const ci = costChecks.get(c.k);
      if (ci && ci.lineIds[0] === line.id) add(line.id, costFlag(line, c, ci));
      // Billed in another unit: the three ways, each with its maths (2026-10-22)
      // — the bill in the PO's units, the PO in the bill's, or both as they are.
      if (line._asBilled || c.conversion) {
        const which = line._asBilled ? 'bill-to-po' : (c.poUnitsOn ? 'po-to-bill' : (c.unitsKeep ? 'keep' : null));
        const conv = c.conversion || unitsOf(line)?.conversion || null;
        const billMaths = unitsMaths(line, c);
        const change = changeByKey.get(c.k);
        const keepMaths = conv ? conversionCostText(conv) : '';
        const ways = [
          { key: 'bill-to-po', label: "Bill in the PO's units", icon: 'south_east',
            text: billMaths || (c.unitsBlock ? '' : "The bill line stored as the PO counts it."),
            block: line._asBilled ? null : c.unitsBlock, onClick: () => toPoUnits(line, c) },
          { key: 'po-to-bill', label: "PO in the bill's units", icon: 'north_west',
            text: c.poTake?.ok ? `${c.poTake.text}${c.li.orderLineId ? '; its order line follows' : ''}${c.ctx?.xeroMade ? XERO_COPY_FOLLOWS : ''}` : '',
            block: c.poTake ? c.poUnitsBlock : 'Not offered for this line.', onClick: () => poToBillUnits(line, c) },
          { key: 'keep', label: 'Keep both as they are', icon: 'sync_alt',
            text: `The bill stays as billed and ${c.poNumber} as it is${keepMaths ? ` — compared per the PO's unit: ${keepMaths}` : ''}${change && !change.order_line_only ? `; "Also set the PO's cost" sets ${money(change.bill_cost)}` : ''}.`,
            block: null, onClick: () => toBillUnits(line, c) },
        ];
        add(line.id, { key: 'units', tone: which ? 'info' : 'warn', icon: 'swap_horiz',
          text: which
            ? `Billed in another unit than ${c.poNumber} — ${{
              'bill-to-po': "the bill is put in the PO's units",
              'po-to-bill': `${c.poNumber} is put in the bill's units when the bill is saved`,
              keep: 'both kept as they are',
            }[which]}.`
            : `Billed in another unit than ${c.poNumber}${conv ? ` (${conversionQtyText(conv)})` : ''} — choose:`,
          control: (
            <span className="bill-units-ways">
              {ways.map(w => (
                <span key={w.key} className={`bill-units-way${which === w.key ? ' is-active' : ''}${w.block ? ' is-blocked' : ''}`}>
                  <button type="button" className="order-grid-flag-action" disabled={!!w.block}
                    aria-pressed={which === w.key} title={w.block || undefined} onClick={which === w.key ? undefined : w.onClick}>
                    <span className="material-symbols-rounded" aria-hidden="true">{which === w.key ? 'check' : w.icon}</span>
                    {w.label}
                  </button>
                  <span className="bill-units-way-text">{w.block ? w.block : w.text}</span>
                </span>
              ))}
            </span>
          ) });
      }
      // The bill's quantity against what was supplied (or ordered, made in Xero),
      // in one unit whichever way the units go.
      const billQ = billQtyOf(line, c);
      if (billQ === null || c.several) continue;
      const item = products.find(p => p.id === line.inventory_item_id) || null;
      const side = poSideOf(line, c, item);
      const { kind } = side;
      const unitWord = (n) => (kind === 'bags' || kind === 'units' ? formatCount(n) : `${formatQuantity(n)}${kind === 'tonnes' ? ' T' : ''}`);
      const queryAction = isQueried ? null : { key: 'query', label: 'Mark bill Queried', icon: 'help', disabled: patchMode, onClick: markQueried };
      // The PO's supplied quantity, one click away (the bill's is the default).
      const takePoQty = side.supplied !== null && (!c.conversion || c.poUnitsOn) && !line._asBilled
        ? { key: 'po-qty', label: "Use the PO's supplied qty", icon: 'upload', onClick: () => patchLine(line.id, { quantity: formatDecimalInput(side.supplied) }) }
        : null;
      const copyingNow = !!suppliedCopies[c.po.id]?.[line.po_line_key];
      const copyAction = copyOffered(c.po.id, line.po_line_key)
        ? { key: 'copy', label: copyingNow ? 'Undo' : `Set ${c.poNumber}'s supplied qty to the bill's`, icon: copyingNow ? 'undo' : 'inventory',
          onClick: () => tickCopy(c.po.id, line.po_line_key, !copyingNow) }
        : null;
      if (c.xeroMade) {
        const chk = quantityCheck({ billQty: billQ, poQty: side.ordered, kind });
        if (chk.level !== 'none') {
          add(line.id, { key: 'qty', tone: chk.level === 'query' ? 'query' : 'warn', icon: 'report',
            text: `Bill ${unitWord(billQ)} · ${c.poNumber} ${unitWord(side.ordered)} ordered (made in Xero — no supplied figure here)`,
            actions: [queryAction].filter(Boolean) });
        }
        continue;
      }
      if (side.supplied !== null) {
        if (sameValue('qty', billQ, side.supplied)) continue;
        const chk = quantityCheck({ billQty: billQ, poQty: side.supplied, kind });
        add(line.id, { key: 'qty', tone: chk.level === 'query' ? 'query' : (chk.level === 'none' ? 'info' : 'warn'), icon: copyingNow ? 'inventory' : 'report',
          text: copyingNow
            ? `${unitWord(billQ)} will be recorded as supplied on ${c.poNumber} when the bill is saved (${unitWord(side.supplied)} recorded now).`
            : `Bill ${unitWord(billQ)} · ${unitWord(side.supplied)} supplied on ${c.poNumber}`,
          actions: [takePoQty, copyAction, copyingNow ? null : queryAction].filter(Boolean) });
        continue;
      }
      // No supplied quantity recorded: the bill's can be recorded — never automatic.
      const s = c.ctx?.lines?.[line.po_line_key];
      if (!c.ctx || !s) continue;
      const ordered = side.ordered;
      const far = quantityCheck({ billQty: billQ, poQty: ordered, kind });
      const copying = !!suppliedCopies[c.po.id]?.[line.po_line_key];
      const offered = copyOffered(c.po.id, line.po_line_key);
      add(line.id, { key: 'supplied', tone: far.level === 'query' ? 'query' : 'info', icon: copying ? 'inventory' : 'help_outline',
        text: copying
          ? `${unitWord(billQ)} will be recorded as supplied on ${c.poNumber} when the bill is saved (${unitWord(ordered)} ordered).`
          : `No supplied quantity on ${c.poNumber} yet — the bill says ${unitWord(billQ)}, ${unitWord(ordered)} ordered.${s.billBlock ? ` ${s.billBlock}` : ''}`,
        actions: [
          offered ? { key: 'copy', label: copying ? 'Undo' : "Use the bill's quantity as supplied", icon: copying ? 'undo' : 'inventory',
            onClick: () => tickCopy(c.po.id, line.po_line_key, !copying) } : null,
          copying ? null : queryAction,
        ].filter(Boolean) });
    }
    // An invoice line on no PO line, a PO line not on the invoice: noted, with
    // the match or the removal one click away.
    for (const l of reconcile.notOnPo) {
      add(l.id, { key: 'not-on-po', tone: 'warn', icon: 'link_off', text: 'On the invoice, not on a PO line.',
        control: openPoLineOptions.length ? (
          <SearchableDropdown value={null} compact placeholder="Match to a PO line…"
            onChange={(poLineId) => poLineId && setLineItems(prev => matchLine(prev, l.id, poLineId))}
            options={[{ value: null, label: 'Match to a PO line…' }, ...openPoLineOptions]} />
        ) : null,
        actions: splitAllowed && reconcile.notOnInvoice.length >= 2
          ? [{ key: 'split', label: 'Split across PO lines', icon: 'call_split', onClick: () => openSplitDialog(l) }] : [] });
    }
    for (const l of reconcile.notOnInvoice) {
      // A Direct Delivery haulage line whose product's bill price includes the delivery.
      const ddIn = l._poKind === 'haulage' && lineItems.some(x => x._ddSuffix && Number(x.purchase_order_id) === Number(l.purchase_order_id)
        && x.po_line_key === `ol:${String(l.po_line_key || '').replace(/^haulage:/, '')}`);
      add(l.id, { key: 'not-on-invoice', tone: ddIn ? 'info' : 'warn', icon: 'playlist_remove',
        text: ddIn
          ? `${poNumberOf(l.purchase_order_id)}'s Direct Delivery haulage — not a line of its own on the invoice: its price includes the delivery. Remove it from the bill.`
          : `On ${poNumberOf(l.purchase_order_id)}, not on the invoice.`,
        actions: [{ key: 'remove', label: 'Remove from the bill', icon: 'delete', onClick: () => setLineItems(prev => {
          const kept = prev.filter(x => x.id !== l.id);
          return kept.length ? kept : [blankLine()];
        }) }] });
    }
    // A code that is none of our items (a supplier's own, read from their
    // invoice): Xero is sent an item only when the code is one of ours, so
    // the line would go without it — said here, before it goes (Cam,
    // 30 Oct 2026 — bill I_00881855 went with NWF's 2404177 and no item).
    if (products.length) {
      const known = new Set(products.map(p => String(p.item_code || '').trim().toUpperCase()));
      for (const line of lineItems) {
        const code = String(line.product_code || '').trim();
        if (!code || line.inventory_item_id || known.has(code.toUpperCase())) continue;
        add(line.id, { key: 'code-not-item', tone: 'warn', icon: 'barcode',
          text: `${code} isn't one of our items, so this line would go to Xero without one — choose our item in the Item cell, or clear the code.`,
          actions: [{ key: 'clear-code', label: 'Clear the code', icon: 'backspace', disabled: patchMode,
            onClick: () => patchLine(line.id, { product_code: '', inventory_item_id: null }) }] });
      }
    }
    return Object.keys(flags).length ? flags : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, linkedPOIds, linkedPODetailsMap, poContext, products, suppliedCopies, isQueried, patchMode, reconcile, openPoLineOptions, splitAllowed, pushSettings.asBilledReady, poLinesEditable, linesPerPoLine, savedPoIds, poEditTicks, changeByKey, costChecks, recordedCostChoices]);

  // Where a bill line meets its PO line the quantity and price are the
  // bill's (Cam, 16 Oct 2026). A product on a Direct Delivery PO whose
  // price includes the delivery takes " (includes direct delivery)" after
  // the PO's words, once (the user can take it off) — utils/billLineFields.
  useEffect(() => {
    if (transactionType !== 'BILL') return;
    let changed = false;
    const next = lineItems.map(l => {
      if (!l._pdfMatched || l._ddChecked) return l;
      const found = findPoLine(linkedPODetailsMap, l);
      if (!found) return l;
      changed = true;
      const po = found.po;
      const olId = found.li?.orderLineId;
      // The bill's own haulage line for this PO: one on its haulage line, or a charge line.
      const separateHaulage = lineItems.some(x => x.id !== l.id && !lineIsEmpty(x) && !(x._fromPo && !x._pdfMatched)
        && ((Number(x.purchase_order_id) === Number(po.id) && olId != null && x.po_line_key === `haulage:${olId}`)
          || ((x._pdfMatched || x._fromPdf) && isChargeLine(x.product_name))));
      // The bill's own price, even while the cost is emptied for choosing (2026-10-28).
      const dd = directDeliveryInPrice({ po, li: found.li, billPrice: billFigureOf(l), separateHaulage });
      if (!dd.includes) return { ...l, _ddChecked: true };
      return { ...l, _ddChecked: true, _ddSuffix: true, _ddHaulageInPrice: dd.haulageInPrice, product_name: withDdSuffix(l.product_name) };
    });
    if (changed) setLineItems(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, linkedPODetailsMap, transactionType]);

  // Where each line's values came from, for the save's audit row (`line_choices`).
  const lineChoicesNow = () => {
    const rows = new Map();
    for (const line of lineItems) {
      const c = lineCtx(line);
      if (!c) continue;
      const sides = lineFieldSides({ line, li: c.li, supplied: c.supplied, conversion: c.conversion, ddSuffix: !!line._ddSuffix });
      const choices = { ...(rows.get(c.k)?.choices || {}) };
      const fieldOf = { item: 'item', desc: 'description', qty: 'quantity' };
      for (const [col, field] of Object.entries(fieldOf)) {
        if (sides[col].differs && sides[col].active === 'po') choices[field] = 'po';
      }
      if (poEditTicks[c.k]?.qty && !poUpdateBlock('qty', { editable: poLinesEditable, scope: c.scope, splitFamily: c.splitFamily, supplied: c.supplied, billedElsewhere: c.billedElsewhere, several: c.several })) choices.quantity = 'bill';
      if (poEditTicks[c.k]?.desc && !poUpdateBlock('desc', { editable: poLinesEditable, scope: c.scope, several: c.several })) choices.description = 'bill';
      const change = changeByKey.get(c.k);
      if (change) choices.cost = costUpdateOn(c.k) ? 'bill' : 'keep';
      else if (sides.price.differs && sides.price.active === 'po') choices.cost = 'po';
      if (line._asBilled) choices.units = 'po';
      else if (c.poUnitsOn) choices.units = 'bill';
      else if (c.unitsKeep) choices.units = 'keep';
      // Which figure the line's cost was taken from (the cost check, 2026-10-28).
      if (costChosen(line)) choices.price = line._costPick;
      rows.set(c.k, { purchase_order_id: c.po.id, po_line_key: line.po_line_key, purchase_order_number: c.poNumber,
        description: c.li.description || line.product_name, choices });
    }
    return lineChoicesFor([...rows.values()]);
  };

  // Possible duplicates, looked for as the bill is typed in: same supplier and
  // invoice number, or same supplier, total and date (GET /api/bills/duplicates).
  // The save asks again on the server; this is so nobody types a whole bill
  // that is already in.
  useEffect(() => {
    if (!selectedSupplierId || (!supplierReference.trim() && !billDate)) { setDuplicates([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const supplier = suppliers.find(x => String(x.id) === String(selectedSupplierId));
        const qs = new URLSearchParams({
          account_id: String(selectedSupplierId),
          supplier_reference: supplierReference.trim(),
          transaction_type: transactionType,
        });
        const xc = supplier?.xero_contact_id || supplierXeroContactId;
        if (xc) qs.set('xero_contact_id', xc);
        const { total } = calculateTotals();
        if (total) qs.set('total', String(total));
        if (billDate) qs.set('bill_date', billDate);
        if (editingBillId) qs.set('exclude_id', String(editingBillId));
        const res = await fetch(`${API_URL}/api/bills/duplicates?${qs}`, { credentials: 'include' });
        const data = await res.json();
        if (!cancelled) setDuplicates(data.success ? (data.duplicates || []) : []);
      } catch {
        if (!cancelled) setDuplicates([]);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupplierId, supplierXeroContactId, supplierReference, billDate, transactionType, editingBillId, lineItems]);

  // Picking an item on a bill with no supplier yet suggests one from the
  // item's code (100→Mole Valley, 2→NWF, K/P→ForFarmers). Header-level, so a
  // supplier already chosen is never replaced.
  /*
   * An item picked on a line, with no supplier chosen yet (Cam, 2026-11-02):
   * the items' own supplier when every line's item names the same ONE
   * supplier; else, when no line's item names a supplier at all, the old
   * item-code guess — never a supplier group's member, whose group's
   * suppliers are pinned for the person to pick instead. The lines are the
   * ones on screen with the picked item in its row.
   */
  // A line's item (the bill's list carries each item's supplier — item_supplier).
  const lineItemOf = (line) => (line?.inventory_item_id
    ? products.find(p => p.id === line.inventory_item_id) || null : null);

  const handleItemPicked = (item, index) => {
    if (selectedSupplierId) return;
    const items = lineItems.map((l, i) => (i === index ? item : lineItemOf(l)));
    const fill = (s) => {
      setSelectedSupplierId(String(s.id));
      setSupplierName(s.company_name || '');
      setSupplierXeroContactId(s.xero_contact_id || '');
    };
    const fromItems = headerSupplierFill(items, { current: selectedSupplierId, offered: suppliers });
    if (fromItems) {
      const s = suppliers.find(x => Number(x.id) === fromItems);
      if (s) fill(s);
      return;
    }
    if (items.some(i => itemSupplierOf(i))) return;
    const suggested = suggestedSupplierFor(item, suppliers);
    if (!suggested) return;
    if (guessAllowed(item, suggested.id, supplierLists.groupsMap)) fill(suggested);
    else setCodeGuessPins(groupPins(suggested.id, supplierLists.groupsMap));
  };

  // Lines as they will be saved — rounded to the penny, net line amounts — so
  // the totals sent with them add up to exactly what is in them.
  const calculateTotals = () => {
    const payload = billLinesToPayload(lineItems.filter(l => !lineIsEmpty(l)), lineTotals);
    const subTotal = Math.round(payload.reduce((sum, l) => sum + l.line_amount, 0) * 100) / 100;
    const totalTax = Math.round(payload.reduce((sum, l) => sum + l.tax_amount, 0) * 100) / 100;
    return { subTotal, totalTax, total: Math.round((subTotal + totalTax) * 100) / 100 };
  };

  // Lines on the linked purchase orders, for the Item picker's side panel.
  const linkedLines = useMemo(() => linkedPOIds.flatMap(id =>
    (linkedPODetailsMap[id]?.line_items || []).map(li => ({
      product_code: li.itemCode || '',
      product_name: li.description || '',
      quantity: li.quantity ?? null
    }))
  ), [linkedPOIds, linkedPODetailsMap]);

  // Follow a just-saved bill on its way to Xero for a little while (the server
  // pushes it after answering the save); the worker's sweep carries on after.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const followPush = async (billId) => {
    for (let i = 0; i < 15 && mountedRef.current; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await fetch(`${API_URL}/api/bills/${billId}/push-status`, { credentials: 'include' });
        const data = await res.json();
        if (!data.success || !mountedRef.current) return;
        setPushStatus(data);
        if (!['queued', 'pushing'].includes(data.push_status) || data.sending_enabled === false) return;
      } catch {
        return;
      }
    }
  };

  // Send now (after the supplier's PDF is uploaded, so it goes with the bill).
  const sendNow = async (billId) => {
    setPushStatus(prev => ({ ...(prev || {}), push_status: 'pushing' }));
    try {
      const res = await fetch(`${API_URL}/api/bills/${billId}/push-to-xero`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (data.push && mountedRef.current) setPushStatus(data.push);
      else if (res.status === 409 && mountedRef.current) followPush(billId);
    } catch {
      if (mountedRef.current) followPush(billId);
    }
  };

  // `opts.reason`: the one-line reason for changing a bill approved in Xero.
  // Edit Bill: take a haulage link off the saved bill straight away
  // (DELETE /:id/haulage-links/:poId). The PO's cost and status never change.
  const unlinkHaulageNow = async (po) => {
    if (!editingBillId) return;
    setHaulageBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/bills/${editingBillId}/haulage-links/${po.id}`, {
        method: 'DELETE', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || `Could not take ${po.purchase_order_number || 'the PO'} off`);
      setHaulagePOs(prev => prev.filter(p => Number(p.id) !== Number(po.id)));
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'error', onCloseCallback: null });
    } finally {
      setHaulageBusy(false);
    }
  };

  // The PO lines to ask about on save (utils/billQtyOnSave): each goods PO
  // line the bill bills, its quantity (in the PO line's unit) against what
  // was supplied — or none recorded.
  const qtySaveQuestions = (lines, goodsIds) => {
    const rows = [];
    const seen = new Set();
    for (const line of lines) {
      if (!goodsIds.includes(Number(line.purchase_order_id))) continue;
      const c = lineCtx(line);
      if (!c || seen.has(c.k)) continue;
      seen.add(c.k);
      const s = c.ctx?.lines?.[line.po_line_key];
      if (!c.ctx || (!s && !c.xeroMade)) continue;
      const billQ = c.several ? numOf(billQtyByPoLine[`${c.po.id}|${line.po_line_key}`]) : billQtyOf(line, c);
      const item = products.find(p => p.id === line.inventory_item_id) || null;
      // In one unit whichever way the units go (the PO's, or the bill's when
      // the PO is being put in them).
      const side = poSideOf(line, c, item);
      const { kind } = side;
      const unitWord = (n) => (kind === 'bags' || kind === 'units' ? formatCount(n) : `${formatQuantity(n)}${kind === 'tonnes' ? ' T' : ''}`);
      const ordered = side.ordered;
      rows.push({
        key: c.k, poId: c.po.id, poLineKey: line.po_line_key, poNumber: c.poNumber,
        what: withoutAdditiveNames(c.li.description || line.product_name, c.li.additives),
        billQty: billQ, supplied: side.supplied, ordered, xeroMade: c.xeroMade,
        copyBlock: copyOffered(c.po.id, line.po_line_key) ? null
          : (s?.billBlock || (c.several ? null : "The PO's supplied qty is not set from this bill")),
        farFromOrdered: billQ !== null && ordered !== null ? quantityCheck({ billQty: billQ, poQty: ordered, kind }).level : 'none',
        answered: !!suppliedCopies[c.po.id]?.[line.po_line_key] || isQueried || !!qtyLeft[c.k],
        format: unitWord,
      });
    }
    return qtyQuestions(rows, { queried: isQueried, canQuery: !patchMode && !(editMode && approvedInXero) });
  };
  // "Save with these choices": the copies ticked, the bill Queried if asked,
  // the rest left — then the save, carrying them before the state re-renders.
  const answerQtySave = () => {
    const q = qtySave;
    setQtySave(null);
    if (!q) return;
    const { copies, markQueried, left } = qtyAnswers(q.questions, q.answers);
    let nextCopies = suppliedCopies;
    if (copies.length) {
      nextCopies = { ...suppliedCopies };
      for (const c of copies) nextCopies[c.poId] = { ...(nextCopies[c.poId] || {}), [c.poLineKey]: c.confirmed ? 'confirmed' : true };
      setSuppliedCopies(nextCopies);
    }
    if (left.length) setQtyLeft(prev => ({ ...prev, ...Object.fromEntries(left.map(k => [k, true])) }));
    if (markQueried) { setIsQueried(true); setStatus('Draft'); }
    const { resolvedConflicts, xeroValues, confirmations, opts } = q.args;
    handleSaveBill(resolvedConflicts, xeroValues, confirmations, {
      ...(opts || {}), qtyAnswered: true, suppliedCopiesNow: nextCopies, queriedNow: markQueried || isQueried,
    });
  };

  const handleSaveBill = async (resolvedConflicts = null, xeroValues = null, confirmations = {}, opts = {}) => {
    // Its PDF read as a credit note and nobody said which it is (Cam, 30 Oct
    // 2026 — FSCN0019792): asked before anything is sent, never saved
    // silently as a bill.
    if (!opts.creditReadAnswered && saveNeedsCreditAnswer(creditReadState, { transactionType, editMode, bulk: !!bulk })) {
      setCreditReadDialog({ stage: 'save', saveArgs: { resolvedConflicts, xeroValues, confirmations, opts } });
      return;
    }
    if (!selectedSupplierId) {
      setAlertDialog({ isOpen: true, message: 'Please select a supplier', type: 'warning' });
      return;
    }
    if (transactionType === 'CREDIT' && !String(supplierReference || '').trim()) {
      setAlertDialog({ isOpen: true, type: 'warning', message: "Enter the supplier's credit note number (Reference). Xero files the credit under it, and it is how a second copy is spotted." });
      return;
    }
    // A credit in Xero that never had one (keyed there) may be edited without
    // choosing: its lines stay as Xero has them (2026-10-30). The server asks
    // when a new item line needs it.
    if (transactionType === 'CREDIT' && !creditKind && !(patchMode && !editSnapshot?.credit_kind)) {
      setAlertDialog({ isOpen: true, type: 'warning', message: 'Say what this credit is for: Goods returned (its items go to Xero and the stock goes down) or Price correction (the account only — stock is not touched).' });
      return;
    }
    // A line must name something — an item or a description. A blank row left
    // behind in a grid that always offers one is an accident, not a line.
    // A haulage offer just accepted (opts.haulageMove): this save already
    // carries the move, before the state it was also written to re-renders.
    const moved = opts.haulageMove
      ? moveToHaulage({ goodsIds: linkedPOIds, haulageIds: haulagePOs.map(p => Number(p.id)), lines: lineItems }, opts.haulageMove)
      : null;
    const goodsPOIds = moved ? moved.goodsIds : linkedPOIds;
    const haulagePOIds = moved ? moved.haulageIds : haulagePOs.map(p => Number(p.id));
    // A row left empty is simply not saved — the grid always offers one more.
    const realLines = (moved ? moved.lines : lineItems).filter(l => !lineIsEmpty(l));
    if (realLines.length === 0) {
      setAlertDialog({ isOpen: true, message: 'Add at least one line with an item or a description.', type: 'warning' });
      return;
    }
    // A typed line has no item to bring an account, and Xero will not take a
    // bill line without one — it used to be quietly filed under 429.
    const noAccount = realLines.filter(l => !l.inventory_item_id && !l.account_code).length;
    if (noAccount > 0) {
      setAlertDialog({
        isOpen: true,
        type: 'warning',
        message: `${noAccount} typed line${noAccount > 1 ? 's need' : ' needs'} an account. Choose one in the Account column, then save again.`
      });
      return;
    }
    // A cost that differs from the order's or the contract's must be chosen,
    // or the bill queried (Cam, 2026-10-28 — the one hard stop on a difference).
    if (transactionType === 'BILL' && costChoiceMissing.length) {
      setAlertDialog({ isOpen: true, type: 'warning', message: costChoiceRequiredText(costChoiceMissing) });
      return;
    }
    // Nothing else here stops a save (Cam, 2026-10-16): a bill that differs
    // from its PO, lines that do not add up to the invoice's figure, an
    // invoice line on no PO line — each is a note on the screen, and the user
    // decides. The server's own refusals and questions still apply.

    // A bill in Xero edited by patch: the server reads Xero's copy and refuses
    // when Xero has moved on (XERO_AHEAD), so the old conflict check is not
    // asked. Approved: say what changes in Xero, and ask why.
    const xeroChangesNow = () => describeBillEditInXero(editSnapshot, {
      bill_date: billDate, due_date: dueDate || null, supplier_reference: supplierReference, reference,
      credit_kind: transactionType === 'CREDIT' ? creditKind : undefined,
      lines: billLinesToPayload(realLines, lineTotals),
    });
    if (patchMode && editCtx?.needs_reason && !opts.reason) {
      const changes = xeroChangesNow();
      if (changes.length) {
        setReasonPrompt({ changes, resolvedConflicts, xeroValues, confirmations, opts });
        return;
      }
    }

    // If editing a Xero bill with no prior conflict resolution, check for conflicts first
    if (editMode && savedBillXeroId && !resolvedConflicts && !patchMode) {
      try {
        const checkRes = await fetch(
          `${API_URL}/api/bills/${editingBillId}/conflict-check?since=${encodeURIComponent(savedBillXeroModified || '')}`,
          { credentials: 'include' }
        );
        const checkData = await checkRes.json();

        if (checkData.hasConflict && checkData.conflicts?.length > 0) {
          const userChanged = new Set();
          if (initialBillData) {
            const normalize = (v) => (v === null || v === undefined || v === '') ? '' : String(v);
            const currentData = { bill_date: billDate, due_date: dueDate, reference, supplier_reference: supplierReference, sub_total: calculateTotals().subTotal };
            for (const key of Object.keys(initialBillData)) {
              if (normalize(currentData[key]) !== normalize(initialBillData[key])) {
                userChanged.add(key);
              }
            }
          }
          setConflictData({ ...checkData, userChangedFields: userChanged });
          setShowConflictModal(true);
          return;
        }
      } catch (err) {
        console.warn('Conflict check failed, proceeding with save:', err.message);
      }
    }

    // The bill's quantities against what its POs say was supplied (Cam,
    // 16 Oct 2026): asked once, in one window, before saving — set the PO's
    // supplied qty to the bill's, leave it and mark the bill Queried, or
    // leave it. × goes back to the bill. Lines answered on their note are
    // not asked again.
    if (transactionType === 'BILL' && !opts.qtyAnswered && poLinesEditable && !patchMode) {
      const questions = qtySaveQuestions(realLines, goodsPOIds);
      if (questions.length) {
        setQtySave({ questions, answers: {}, args: { resolvedConflicts, xeroValues, confirmations, opts } });
        return;
      }
    }
    // What the window just answered, before the state it also set re-renders.
    const queriedNow = opts.queriedNow ?? isQueried;
    const copiesNow = opts.suppliedCopiesNow || suppliedCopies;

    // A supplier credit never changes a PO's cost (Cam, 2026-10-08): it is a
    // one-off amount, and allocating it reduces what is owed on the bill.
    if (transactionType === 'CREDIT' && opts.poCostUpdates === undefined) {
      opts = { ...opts, poCostUpdates: [] };
    }

    // Which PO lines take the bill's cost: what the price cells say (ticked
    // by default, unticked on a Queried bill — and changeable in the cell).
    // Every line saving would change is answered, so the server is never left
    // to guess; none when nothing would change.
    if (opts.poCostUpdates === undefined) {
      opts = {
        ...opts,
        poCostUpdates: costChangesFor(realLines, goodsPOIds).map(c => {
          const k = c.key || costKey(c.purchase_order_id, c.po_line_key);
          return { purchase_order_id: Number(c.purchase_order_id), po_line_key: c.po_line_key, update: costUpdateOn(k, { queried: queriedNow }) };
        }),
      };
    }

    setSaving(true);
    try {
      const supplier = suppliers.find(s => s.id.toString() === selectedSupplierId);
      const { subTotal, totalTax, total } = calculateTotals();

      const url = editMode
        ? `${API_URL}/api/bills/${editingBillId}`
        : `${API_URL}/api/bills`;
      const method = editMode ? 'PUT' : 'POST';
      // With a PDF still to upload, the screen asks for the push itself once
      // it is there, so the PDF goes to Xero with the bill. A new bill from
      // Bulk Add Bills: the server attaches the item's pages before its push.
      const serverAttaches = !!bulk && !editMode;
      const deferPush = !!pendingFile && !serverAttaches;
      // A supplier credit goes as positive figures (Jade, 2026-10-13): one
      // read or typed negative is turned over (utils/creditSigns — the
      // server does the same).
      let payloadLines = billLinesToPayload(realLines, lineTotals)
        .map(l => (transactionType === 'CREDIT' ? { ...l, purchase_order_id: null, po_line_key: null } : l));
      let money = { sub_total: subTotal, total_tax: totalTax, total };
      if (transactionType === 'CREDIT') {
        const { doc } = positiveCreditDocument({ line_items: payloadLines, ...money });
        payloadLines = doc.line_items;
        money = { sub_total: doc.sub_total, total_tax: doc.total_tax, total: doc.total };
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          account_id: parseInt(selectedSupplierId),
          xero_contact_id: supplier?.xero_contact_id || supplierXeroContactId || null,
          supplier_name: supplier?.company_name || supplierName,
          // A supplier credit: the POs and bills it relates to (references).
          purchase_order_ids: transactionType === 'CREDIT' ? creditPoRefs.map(p => Number(p.id)) : goodsPOIds,
          ...(transactionType === 'CREDIT' ? { credit_bill_ids: creditBillRefs.map(b => Number(b.id)), credit_kind: creditKind } : {}),
          // Raised against one bill: clear its query and allocate to it, as
          // the user answered (2026-10-13). Not once the credit is in Xero.
          ...(transactionType === 'CREDIT' && !billInXero ? {
            against_bill_id: againstBillId, clear_bill_query: clearBillQuery, allocate_to_bill: allocateToBill
          } : {}),
          // New bills only: an edit keeps the bill's haulage links (the bill
          // window's POs changes them).
          ...(!editMode && haulagePOIds.length && transactionType !== 'CREDIT' ? { haulage_po_ids: haulagePOIds } : {}),
          bill_date: billDate,
          // A credit has no due date (Jade, 2026-10-13).
          ...(transactionType === 'CREDIT' ? {} : { due_date: dueDate || null }),
          reference: reference || null,
          supplier_reference: supplierReference || null,
          status: queriedNow ? 'Draft' : status,
          transaction_type: transactionType,
          is_credit: ['CREDIT', 'OVERPAYMENT', 'PREPAYMENT'].includes(transactionType),
          is_queried: queriedNow,
          notes: notes || null,
          // This system's own (never sent to Xero); only once their migration
          // has run, and not on a supplier credit.
          ...(pushSettings.localFieldsReady && transactionType !== 'CREDIT'
            ? { po_reference: poReference.trim() || null, is_stock: isStock } : {}),
          ...money,
          line_items: payloadLines,
          // Changes to the POs' lines typed here — a bill, new or not yet in Xero;
          // cost only on a PO made in Xero or whose order is delivered.
          ...(poLinesEditable && transactionType === 'BILL' ? {
            po_edits: poEditsPayload(poEdits, linkedPODetailsMap, Object.fromEntries(Object.entries(poContext).map(([id, c]) => [id, c?.scope || null])))
              .filter(e => goodsPOIds.includes(Number(e.purchase_order_id))),
            supplied_copies: suppliedCopiesPayload(copiesNow, copyOffered)
              .filter(e => goodsPOIds.includes(Number(e.purchase_order_id)))
          } : {}),
          confirmations,
          defer_push: deferPush,
          // Which PO lines take the bill's cost — the user's answer; none
          // when nothing would change (never automatic).
          po_cost_updates: opts.poCostUpdates || [],
          // Which side each field was taken from, for the audit row (2026-10-15).
          // (Every bill edited here, not only one that can change its POs: a
          // cost changed on a flagged line needs its choice — 2026-10-28.)
          ...(transactionType === 'BILL' && !patchMode ? { line_choices: lineChoicesNow() } : {}),
          // The invoice changes the user agreed to (InvoiceProposalDialog).
          ...(opts.invoiceChanges ? { invoice_changes: opts.invoiceChanges } : {}),
          ...(opts.reason ? { edit_reason: opts.reason } : {}),
          // Dated in a month that should be locked in Xero: the person said go on.
          ...(opts.priorMonthConfirmed ? { confirm_prior_month: true } : {}),
          // A PO made in Xero changed there since: the person chose to send ours over it.
          ...(opts.xeroNewerConfirmed ? { xero_newer_confirmed: opts.xeroNewerConfirmed } : {}),
          // Bulk Add Bills: the batch item this saves (once) or replaces from.
          ...(bulk?.item?.id ? { bulk_item_id: bulk.item.id } : {}),
          ...(resolvedConflicts && { resolvedConflicts, xeroValues })
        })
      });

      const result = await response.json();

      // Dated in a month that should be locked in Xero by now (Cam, 15 Oct
      // 2026): asked; Save anyway sends the same save again (and every later
      // question keeps the answer, in opts), Go back saves nothing.
      if (response.status === 409 && isPriorMonthConfirm(result) && !opts.priorMonthConfirmed) {
        if (await priorMonth.ask(result)) {
          await handleSaveBill(resolvedConflicts, xeroValues, confirmations, { ...opts, priorMonthConfirmed: true });
        } else {
          setReasonPrompt(null);
        }
        return;
      }

      // The server's questions: a possible duplicate, or a PO already billed.
      // Answered yes, the same save goes again with the answer.
      if (response.status === 409 && ['POSSIBLE_DUPLICATE', 'NEEDS_CONFIRMATION'].includes(result.code)) {
        setReasonPrompt(null);
        setSaveQuestion({
          code: result.code,
          duplicates: result.duplicates || [],
          needsConfirmation: result.needsConfirmation || [],
          answer: result.code === 'POSSIBLE_DUPLICATE' ? { duplicate: true } : { already_billed: true },
          resolvedConflicts, xeroValues, confirmations, opts,
        });
        return;
      }
      // Approved in Xero although this screen did not know: ask why, then again.
      if (response.status === 400 && result.code === 'EDIT_REASON_REQUIRED' && !opts.reason) {
        setReasonPrompt({ changes: xeroChangesNow(), resolvedConflicts, xeroValues, confirmations, opts });
        return;
      }
      // A supplied quantity copied onto an order invoiced on the old quantity
      // (not gone out): the invoice changes it needs, to apply or go back.
      if (isInvoiceQuestion(response.status, result)) {
        setReasonPrompt(null);
        setInvoiceQuestion({
          proposals: result.proposals || [], notices: result.notices || [], stale: !!result.stale,
          resolvedConflicts, xeroValues, confirmations, opts,
        });
        return;
      }
      if (response.status === 409 && ['SUPPLIED_REFUSED', 'SUPPLIED_NEEDS_CONFIRMATION'].includes(result.code)) {
        setAlertDialog({
          isOpen: true, type: 'error',
          message: `The supplied quantities could not be copied, so nothing was saved: ${result.error}`,
        });
        return;
      }
      // A PO made in Xero whose lines this save changes, changed in Xero since
      // it was read: ours goes over it only if the person says so.
      if (response.status === 409 && result.code === 'XERO_PO_NEWER' && result.xero_newer) {
        setReasonPrompt(null);
        setXeroNewerQuestion({ info: result.xero_newer, message: result.error, resolvedConflicts, xeroValues, confirmations, opts });
        return;
      }
      if (response.status >= 409 && ['XERO_PO_SYNC_REFUSED', 'XERO_PO_BILLED_IN_XERO'].includes(result.code)) {
        setAlertDialog({
          isOpen: true, type: 'error',
          message: `The PO changes could not be sent to Xero, so nothing was saved: ${result.error}`,
        });
        return;
      }
      if (response.status === 409 && result.code === 'PO_EDIT_REFUSED') {
        setAlertDialog({
          isOpen: true, type: 'error',
          message: `The PO changes could not be saved, so nothing was: ${result.error}`,
        });
        return;
      }
      // A line whose cost differs from the order's or the contract's, with no
      // cost chosen (the server's check, 2026-10-28): those lines now ask.
      if (response.status === 409 && result.code === 'COST_CHOICE_REQUIRED') {
        setReasonPrompt(null);
        const keys = (result.lines || []).map(l => costKey(l.purchase_order_id, l.po_line_key));
        setServerCostRequired(prev => new Set([...prev, ...keys]));
        const detail = (result.lines || []).map(l => l.cost_text).filter(Boolean);
        setAlertDialog({
          isOpen: true, type: 'warning',
          message: `Nothing was saved. ${result.error}${detail.length ? `\n\n${detail.map(t => `• ${t}`).join('\n')}` : ''}`,
        });
        return;
      }
      // Another supplier's PO made in Xero among the goods POs: this bill may be
      // its third-party haulage instead (utils/billHaulageLink).
      const offered = response.status === 409 ? haulageOfferFrom(result) : null;
      if (offered) {
        setReasonPrompt(null);
        if (editMode) {
          setAlertDialog({
            isOpen: true, type: 'warning',
            message: `${offered.message} To link it as third-party haulage, take it off here and use POs in the bill window (Haulage for a PO).`,
          });
          return;
        }
        setHaulageOffer({ ...offered, resolvedConflicts, xeroValues, confirmations, opts });
        return;
      }
      if (response.status === 409 && result.code === 'HAULAGE_REFUSED') {
        setAlertDialog({
          isOpen: true, type: 'error',
          message: (result.refused || []).map(r => r.message).filter(Boolean).join(' ') || result.error,
        });
        return;
      }
      if (response.status === 409 && result.code === 'PO_REFUSED') {
        setAlertDialog({
          isOpen: true, type: 'error',
          message: (result.refused || []).map(r => r.message).filter(Boolean).join(' ') || result.error,
        });
        return;
      }

      if (response.ok && result.success) {
        setReasonPrompt(null);
        const pushQueued = result.push_status === 'queued' && pushSettings.enabled !== false;
        const billId = editMode ? editingBillId : result.bill.id;
        // The supplier's PDF goes up before the window closes.
        let pdfNotAttached = null;
        if (pendingFile && !serverAttaches) {
          pdfNotAttached = await uploadBillAttachment(billId, pendingFile);
          if (!pdfNotAttached) setPendingFile(null);
        }
        // Saving never waited for Xero: with a PDF just uploaded the push is
        // asked for now, so the PDF goes with it; otherwise the server has
        // started it. Either way it carries on after the window has closed
        // (and the sweep after that) — the list shows where it is.
        if (pushQueued && deferPush) sendNow(billId);

        // The window closes and the list behind it loads afresh. Anything to
        // say first — Xero's warning, or quantities that differ from the PO
        // (nothing changed because of them) — is said, then it closes.
        const notes = [];
        if (pdfNotAttached) notes.push(`The bill is saved, but the supplier's PDF could not be attached (${pdfNotAttached}). Open the bill and attach the PDF again.`);
        if (editMode && result.xeroWarning) notes.push(result.xeroWarning);
        // PO Reference / Stock are saved apart from the edit (never Xero's).
        if (result.local_fields_error) notes.push(`The bill is saved, but its PO Reference / Stock could not be: ${result.local_fields_error}`);
        const qtyNote = quantityDifferencesMessage(result.quantity_differences);
        if (qtyNote) notes.push(qtyNote);
        const invoiceNote = invoiceUpdateMessage(result.invoice_update, { where: '"Update invoice from supplied qty…" in the bill window' });
        if (invoiceNote) notes.push(invoiceNote);
        const differNote = stillDifferMessage(result.cost_write_back);
        if (differNote) notes.push(differNote);
        // A credit raised against a bill: its query, and the allocation to come.
        const againstNote = againstBillResultMessage(result.against_bill);
        if (againstNote) notes.push(againstNote);
        if (serverAttaches && result.attachment && !result.attachment.ok) {
          notes.push('The bill is saved, but its PDF could not be attached — attach it again from the Bulk Add Bills summary, or from the bill window.');
        }
        const close = () => {
          if (bulk) bulk.onSaved(result);
          else if (onNavigate) onNavigate(returnTo);
        };
        if (notes.length) {
          // Only the credit's good news (query cleared, allocation to come): not a warning.
          const ab = result.against_bill;
          const goodNews = notes.length === 1 && againstNote && ab && !ab.error && ab.query_cleared?.ok !== false
            && (ab.allocate || !ab.code || ab.code === 'NOT_CHOSEN');
          setAlertDialog({ isOpen: true, type: goodNews ? 'success' : 'warning', message: notes.join('\n\n'), onCloseCallback: close });
        } else {
          close();
        }
      } else {
        throw new Error(result.error || 'Failed to save bill');
      }
    } catch (error) {
      setReasonPrompt(null);
      setAlertDialog({ isOpen: true, message: 'Failed to save bill: ' + error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Apply OCR-suggested values to the form fields
  // `opts.asCredit`: applied as a supplier credit — the screen is (or has
  // just been switched to, in this same handler) Add Supplier Credit.
  const applyOcrResult = (read, hasText, meta = {}, opts = {}) => {
    const applied = [];
    const filled = new Set();
    const asCredit = opts.asCredit ?? (transactionType === 'CREDIT');
    // A supplier credit's figures are positive (Jade, 2026-10-13): a credit
    // note printed negative is turned over before anything is filled, so the
    // lines, the net and the total all agree.
    const creditRead = asCredit || read?.document_type === 'credit_note';
    const s = creditRead ? positiveCreditDocument(read).doc : read;
    // A credit has no due date. A credit note kept as a bill (Cam, 30 Oct
    // 2026 — FSCN0019792) keeps the due date read: Xero approves no bill
    // without one. Bulk Add Bills' review as before.
    const noDue = asCredit || (!!bulk && creditRead);

    // What the invoice says of the header, to offer beside the PO's (2026-10-16).
    setBillHeader({
      supplier: s.supplier_match?.account_id ? {
        id: String(s.supplier_match.account_id), name: s.supplier_match.company_name || '', xeroContactId: s.supplier_match.xero_contact_id || '',
        printed: s.supplier_match.printed_name || '', groupIds: (s.supplier_match.group_account_ids || []).map(String),
      } : null,
      bill_date: s.bill_date ? openBillDate(s.bill_date, firstAllowedDate).date : '',
      due_date: noDue ? '' : (s.due_date || ''),
      supplier_reference: s.supplier_reference || '',
    });
    if (s.bill_date)          { setBillDateFrom(s.bill_date, 'supplier'); applied.push('Bill date');     filled.add('billDate'); }
    if (s.due_date && !noDue) { setDueDate(s.due_date);                applied.push('Due date');        filled.add('dueDate'); }
    // The extractor's supplier_reference is the SUPPLIER'S INVOICE NUMBER, and
    // it belongs in supplier_reference: that is the field the "Reference" input
    // edits and the one pushed to Xero as the bill's invoice number
    // (billService.pushBillToXero). It was being put in `reference` instead —
    // a field with no input, which Xero only sees when no PO is linked — so the
    // invoice number never reached Xero on a PO-linked bill. A PO may already
    // have filled this field with the supplier's order number; the invoice
    // number is the document being billed, so it wins.
    if (s.supplier_reference) { setSupplierReference(s.supplier_reference); applied.push('Invoice number'); filled.add('supplierReference'); }
    // The invoice's net total, for the unallocated check — only when none typed.
    if (s.sub_total != null && s.sub_total !== '' && invoiceNet === '') {
      setInvoiceNet(String(s.sub_total)); applied.push('Net total'); filled.add('invoiceNet');
    }

    // The supplier (Cam, 2026-10-07; utils/billSupplierDefault): the one the
    // invoice NAMES, matched to a contact — not the PO's. When it matches no
    // contact, the PO's supplier (its supplier group's parent for a child's
    // PO), marked to be checked. A supplier the user (or a saved bill)
    // settled is kept; a different supplier from the PO's is the reconciler's.
    // A PO not linked because it was already billed plays no part (Cam,
    // 2026-10-14): the invoice's own supplier, or none for the user to choose
    // (utils/billPoReference.withoutPoLink).
    const poForSupplier = s.matched_po
      || (linkedPOIds.length && selectedSupplierId
        ? { account_id: selectedSupplierId, supplier_name: supplierName, xero_contact_id: supplierXeroContactId }
        : null);
    const sup = supplierConfirmedRef.current ? null : supplierFromUpload(s.supplier_match, poForSupplier);
    if (sup && sup.accountId) {
      setSelectedSupplierId(sup.accountId);
      setSupplierName(sup.name);
      setSupplierXeroContactId(sup.xeroContactId);
      setSupplierCheck(sup.needsCheck ? sup.hint : '');
      if (!sup.needsCheck) {
        applied.push(sup.source === 'invoice' ? 'Supplier' : `Supplier (from ${s.matched_po?.purchase_order_number || 'the PO'})`);
        filled.add('supplier');
      }
    }

    if (s.matched_po && asCredit) {
      // A credit quoting one of our POs: what it relates to, never its lines.
      const m = s.matched_po;
      setCreditPoRefs(prev => (prev.some(p => Number(p.id) === Number(m.id)) ? prev
        : [...prev, { id: Number(m.id), purchase_order_number: m.purchase_order_number, xero_purchase_order_id: m.xero_purchase_order_id || null }]));
    } else if (s.matched_po) {
      // The PO detected from the upload brings its lines, for the invoice's
      // lines to be reconciled with (a new bill; a PO already on it has them).
      if (!editMode && !linkedPOIds.includes(Number(s.matched_po.id))) {
        loadFromPOs([Number(s.matched_po.id)], products, { append: true });
      } else {
        addLinkedPO(s.matched_po.id);
      }
      // Deliberately NOT setReference(<PO number>): Reference is the supplier's
      // invoice number, not our PO number. Filling it with ours meant every bill
      // reached Xero citing our document instead of theirs.
    }

    // Line items from inventory lookup. Highlighted until someone touches the
    // row, and flagged when the reader found no matching item. Tax is derived
    // from each line's tax type rather than arriving as 0.
    // A line the reader listed twice (utils/billReadLines): left out when the
    // invoice's net says so — the server does this on every read since
    // 16 Oct; a read kept from before (Bulk Add Bills) is checked here too.
    const readNotes = [];
    let readLines = Array.isArray(s.line_items) ? s.line_items : [];
    let refJobs = s.reference_links?.jobs || null;
    if (readLines.length > 1) {
      const rep = dropRepeatedLines(readLines, { subTotal: s.sub_total ?? null });
      if (rep.dropped.length) {
        readLines = rep.lines;
        refJobs = refJobs ? jobsWithoutLines(refJobs, rep.dropped) : refJobs;
      }
      if (rep.note && !String(meta.notes || '').includes(rep.note.slice(0, 40))) readNotes.push(rep.note);
    }
    // The invoice's VAT total, against the lines' (shown beside Before VAT).
    setInvoiceVat(s.total_tax != null && s.total_tax !== '' && Number.isFinite(Number(s.total_tax)) ? Number(s.total_tax) : null);
    if (readLines.length > 0) {
      // Each line's VAT (Cam, 16 Oct 2026): its item's purchase tax type, else
      // what the bill says — the line's own VAT %, or the invoice's VAT total
      // as one rate of its net (utils/billReadLines). A read from before the
      // server worked the bill's out is worked out here.
      const fresh = readLines.some(l => l && 'bill_tax_type' in l)
        ? null : billTaxOf(readLines, { subTotal: s.sub_total ?? null, totalTax: s.total_tax ?? null });
      if (fresh?.note && !String(meta.notes || '').includes(fresh.note.slice(0, 40))) readNotes.push(fresh.note);
      // Marked as the invoice's, to be reconciled with the PO's lines.
      // Each keeps what its job quoted (`_ref`), for "PO links from references".
      const newItems = billLinesFromApi(readLines, products, { fromPdf: true })
        .map((l, i) => {
          const r = readLines[i] || {};
          const ref = refFieldsOf(r);
          const bt = fresh ? fresh.taxes[i]
            : (r.bill_tax_type ? { value: r.bill_tax_type, from: r.bill_tax_from || 'line', reason: r.bill_tax_reason || '' } : null);
          const item = l.inventory_item_id ? products.find(p => p.id === l.inventory_item_id) : null;
          // No item here: an older read's own tax type stands in for the bill's.
          const tax = defaultTaxType({ itemTax: item ? item.purchase_tax_type : null,
            billTax: bt ? bt.value : (!item && fresh && hasTax(r.tax_type) ? r.tax_type : null) });
          return {
            ...l, _fromPdf: !editMode, ...(ref ? { _ref: ref } : {}),
            tax_type: tax.value, tax_rate: getTaxRate(tax.value) || 0, _taxFrom: tax.from, _billTax: bt || null,
          };
        });
      // A bill of jobs (a haulier's, 2026-10-16): each job's document, and an
      // EXTH item on each of its charge lines (utils/billJobLines).
      // Only a bill of jobs (two or more jobs, every line a charge): a goods
      // bill quoting our POs (MVFS "Ref: PO-D10189") brings those POs on
      // instead, its lines matched to theirs and left as read (2026-10-16).
      let added = newItems;
      if (!creditRead && refJobs?.length) {
        const jobs = refJobs;
        if (isBillOfJobs(readLines, jobs)) {
          const state = { jobs, ids: newItems.map(l => l.id), choices: initialJobChoices(jobs, { billId: editMode ? editingBillId : null }) };
          const plan = applyJobs(newItems, jobs, state.choices, state.ids, { items: products, reconciling: !editMode });
          added = plan.lines;
          setJobLinks(state);
          syncJobLinks(state, added, plan);
        } else if (!editMode) {
          const loads = goodsBillPoLoads(jobs).filter(id => !linkedPOIds.includes(id) && id !== Number(s.matched_po?.id));
          if (loads.length) loadFromPOs(loads, products, { append: true });
        }
      }
      setLineItems(prev => [...prev.filter(l => !lineIsEmpty(l)), ...added]);
      if (!editMode) setPdfLinesApplied(true);
      applied.push(`${readLines.length} line item${readLines.length > 1 ? 's' : ''}`);
    }

    setOcrFilledFields(filled);
    setOcrApplied({
      fields: applied,
      total: s.total || null,
      noText: !hasText,
      noFields: hasText && applied.length === 0,
      // What the reader itself wants checked — an ambiguous date, lines that
      // don't sum, a credit note. Shown verbatim rather than reduced to a
      // status, because the specific doubt is the useful part.
      notes: [meta.notes || '', ...readNotes, unmatchedSupplierNote(s.supplier_match) || ''].filter(Boolean).join(' '),
      confidence: meta.confidence || null
    });
  };

  // A read lands here (an upload, or a Bulk Add Bills item): its values go
  // on the form, and where the PO says otherwise each field offers both
  // (2026-10-16 — no window before the form).
  const takeRead = (s, hasText, meta = {}) => applyOcrResult(s, hasText, meta);

  // The PO the invoice quotes is already billed for its goods by another
  // bill (Cam, 2026-10-14; utils/billPoReference): asked first, once the scan
  // is read — never linked again without a word. A PO already on this bill,
  // or a supplier credit (whose POs are references only), is not asked about.
  const routeRead = (s, hasText, meta = {}) => {
    // A new bill whose PDF reads as a credit note (Cam, 30 Oct 2026): asked
    // first — nothing from it is put on the form until answered. Editing a
    // bill: only said (a bill can't become a credit). Never in Bulk Add Bills.
    const credit = creditReadAction(s, { transactionType, editMode, bulk: !!bulk });
    if (credit === 'ask') {
      setCreditReadState({ s, hasText, meta, answer: null });
      setCreditReadDialog({ stage: 'upload' });
      return;
    }
    setCreditReadState(null);
    setCreditReadEditNote(credit === 'note');
    routeBillRead(s, hasText, meta);
  };
  const routeBillRead = (s, hasText, meta = {}) => {
    const billed = transactionType === 'CREDIT' ? null
      : poBilledElsewhere(s?.matched_po, { billId: editMode ? editingBillId : null });
    if (billed && !linkedPOIds.includes(Number(s.matched_po.id))) {
      setBilledPoQuestion({ s, hasText, meta, billed });
      return;
    }
    takeRead(s, hasText, meta);
  };

  // ── A PDF that reads as a credit note, on a new bill (Cam, 30 Oct 2026) ──
  // Lines that came from a PO (and, once the read was applied as a bill, the
  // read's own) give way to the credit's lines read again: a credit's lines
  // are the credit note's. Without a PO on the form, an applied read's lines
  // stay as they are (they may have been edited).
  const creditSwitchRelines = (st, { applied }) => {
    const readLines = Array.isArray(st?.s?.line_items) && st.s.line_items.length > 0;
    if (!readLines) return false;
    const poOnForm = linkedPOIds.length > 0 || lineItems.some(l => !lineIsEmpty(l) && (l.purchase_order_id || l._fromPo));
    return !applied || poOnForm;
  };
  // "Enter as a supplier credit": the screen becomes Add Supplier Credit in
  // place — the same PDF (still staged), supplier, credit number, date and
  // lines; the POs on the form become what the credit relates to (as Type:
  // Credit Note does); "Credit is for" is left for the user to choose.
  const enterAsCredit = (st, { applied }) => {
    const relines = creditSwitchRelines(st, { applied });
    const refs = linkedPOIds.map(id => ({
      id, purchase_order_number: linkedPODetailsMap[id]?.purchase_order_number || `PO #${id}`,
      xero_purchase_order_id: linkedPODetailsMap[id]?.xero_purchase_order_id || null
    }));
    if (refs.length) setCreditPoRefs(prev => [...prev, ...refs.filter(r => !prev.some(p => Number(p.id) === Number(r.id)))]);
    clearLinkedPOs();
    setPoHeader(null);
    setPoEditTicks({});
    setSuppliedCopies({});
    setCostChoices({});
    setQtyLeft({});
    setJobLinks(null);
    jobPoIdsRef.current = { goods: [], haulage: [] };
    setBilledPoQuestion(null);
    const base = lineItems
      .filter(l => !lineIsEmpty(l))
      .filter(l => !(relines && (l.purchase_order_id || l._fromPo || (applied && l._fromPdf))))
      .map(l => ({ ...l, purchase_order_id: null, po_line_key: null }));
    setLineItems(base.length ? base : [blankLine()]);
    setTransactionType('CREDIT');
    setDueDate('');
    if (!applied || relines) {
      // What the user settled in the header before saving is kept.
      const keep = applied ? { supplierReference, billDate } : null;
      if (relines) setPdfLinesApplied(false);
      const readForCredit = relines ? st.s : { ...st.s, line_items: [] };
      applyOcrResult(readForCredit, st.hasText,
        { ...(st.meta || {}), notes: [CREDIT_READ_SWITCHED_NOTE, st.meta?.notes || ''].filter(Boolean).join(' ') },
        { asCredit: true });
      if (keep?.supplierReference) setSupplierReference(keep.supplierReference);
      if (keep?.billDate) setBillDate(keep.billDate);
    } else {
      setOcrApplied(prev => (prev ? { ...prev, notes: [CREDIT_READ_SWITCHED_NOTE, prev.notes || ''].filter(Boolean).join(' ') } : prev));
    }
  };
  // The question's answer: 'credit', 'bill', or the cancel — 'later' when the
  // PDF was just read (it stays a bill and the save asks), 'back' at save.
  const answerCreditRead = (choice) => {
    const d = creditReadDialog;
    const st = creditReadState;
    setCreditReadDialog(null);
    if (!d || !st) return;
    const applied = d.stage === 'save';
    if (choice === 'credit') {
      setCreditReadState({ ...st, answer: 'credit' });
      enterAsCredit(st, { applied });
      return;
    }
    if (choice === 'bill') setCreditReadState({ ...st, answer: 'bill' });
    if (!applied) {
      routeBillRead(st.s, st.hasText, st.meta);
      return;
    }
    if (choice === 'bill') {
      const a = d.saveArgs || {};
      handleSaveBill(a.resolvedConflicts ?? null, a.xeroValues ?? null, a.confirmations || {}, { ...(a.opts || {}), creditReadAnswered: true });
    }
    // 'back': nothing is saved.
  };
  const creditReadWords = (() => {
    if (!creditReadDialog || !creditReadState) return null;
    const read = creditReadState.s || {};
    const atSave = creditReadDialog.stage === 'save';
    const formSupplier = suppliers.find(x => String(x.id) === String(selectedSupplierId))?.company_name || supplierName;
    return creditReadQuestion({
      number: (atSave && supplierReference) || read.supplier_reference || '',
      supplier: (atSave && formSupplier) || readSupplierName(read),
      stage: creditReadDialog.stage,
      relines: atSave && creditSwitchRelines(creditReadState, { applied: true })
    });
  })();

  // The answer: 'review' — no link, review the bill; 'stock' — no link, the
  // PO number kept as the PO Reference and the bill marked Stock; 'keep' —
  // linked as before (the save then asks "a partial or second bill?").
  const answerBilledPo = (choice) => {
    const q = billedPoQuestion;
    setBilledPoQuestion(null);
    if (!q) return;
    if (choice === 'keep') { takeRead(q.s, q.hasText, q.meta); return; }
    if (choice === 'stock') {
      setPoReference(q.billed.po?.purchase_order_number || '');
      setIsStock(true);
    }
    const note = choice === 'stock'
      ? `${billedPoSentence(q.billed)} Not linked — kept as the PO Reference and marked Stock.`
      : `${billedPoSentence(q.billed)} Not linked — review the bill.`;
    takeRead(withoutPoLink(q.s), q.hasText, { ...q.meta, notes: [note, q.meta?.notes || ''].filter(Boolean).join(' ') });
  };

  // ── A bill of jobs: each job's document (2026-10-14; made simple 2026-10-16) ──
  // Why "PO links from references" is greyed, or null. Lines change only on a
  // new bill or one entered here and not yet in Xero.
  const refLinksBlock = !poLinesEditable || patchMode
    ? 'This bill is in Xero — its lines are not changed here'
    : (!selectedSupplierId ? 'Choose the supplier first' : suggestBlock(lineItems, lineIsEmpty));

  // The jobs' documents on the bill: their PO-Hs (goods links), the Xero POs
  // they carried (third-party haulage links) — and off again when a job's
  // choice moves away. On Edit Bill a haulage link is made at once, as
  // "Haulage for a PO" does.
  const syncJobLinks = (state, lines, plan = null) => {
    const goods = [...new Set(lines.filter(l => l._job && l.purchase_order_id).map(l => Number(l.purchase_order_id)))];
    const haulage = [];
    for (const j of state.jobs || []) {
      const t = chosenTarget(j, state.choices[j.job_key]);
      if (t && t.link === 'haulage' && !haulage.some(h => h.id === Number(t.po_id))) {
        haulage.push({ id: Number(t.po_id), purchase_order_number: t.purchase_order_number, xero_purchase_order_id: t.xero_purchase_order_id || null, contact_name: t.contact_name || null });
      }
    }
    const prev = jobPoIdsRef.current || { goods: [], haulage: [] };
    const others = lines.filter(l => !l._job);
    const dropGoods = prev.goods.filter(id => !goods.includes(id) && !others.some(l => Number(l.purchase_order_id) === id));
    const dropHaulage = prev.haulage.filter(id => !haulage.some(h => h.id === id));
    if (dropGoods.length || goods.length) {
      setLinkedPOIds(ids => [...ids.filter(id => !dropGoods.includes(Number(id))), ...goods.filter(id => !ids.includes(id))]);
      if (dropGoods.length) setLinkedPODetailsMap(m => { const next = { ...m }; for (const id of dropGoods) delete next[id]; return next; });
    }
    const added = haulage.filter(h => !prev.haulage.includes(h.id));
    if (editMode && editingBillId) {
      // A saved bill: made / taken off at once (POST / DELETE /:id/haulage-links).
      (async () => {
        const failed = [];
        for (const po of added) {
          try {
            const res = await fetch(`${API_URL}/api/bills/${editingBillId}/haulage-links`, {
              method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ purchase_order_id: po.id, reason: 'The job on the bill quotes it' })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data.success === false) throw new Error(data.error || 'refused');
            setHaulagePOs(hs => (hs.some(h => Number(h.id) === po.id) ? hs : [...hs, po]));
          } catch (e) { failed.push(`${po.purchase_order_number}: ${e.message}`); }
        }
        for (const id of dropHaulage) {
          const po = haulagePOs.find(h => Number(h.id) === id);
          if (po) await unlinkHaulageNow(po);
        }
        if (failed.length) setAlertDialog({ isOpen: true, type: 'warning', message: `Not linked as haulage — ${failed.join('; ')}` });
      })();
    } else {
      setHaulagePOs(hs => [...hs.filter(h => !dropHaulage.includes(Number(h.id))), ...added.filter(x => !hs.some(h => Number(h.id) === x.id))]);
    }
    const loads = (plan?.loadPoIds || []).filter(id => !linkedPOIds.includes(id));
    if (loads.length) loadFromPOs(loads, products, { append: true });
    jobPoIdsRef.current = { goods, haulage: haulage.map(h => h.id) };
  };

  // A job's document (or Bag / Bulk) chosen on its heading: every job is
  // applied again — the bill's usual pack can follow — keeping what the user
  // changed by hand on the lines.
  const chooseJob = (key, patch) => {
    if (!jobLinks) return;
    const state = { ...jobLinks, choices: { ...jobLinks.choices, [key]: { ...(jobLinks.choices[key] || {}), ...patch } } };
    setJobLinks(state);
    const plan = applyJobs(lineItems, state.jobs, state.choices, state.ids, { items: products, reconciling: !editMode && pdfLinesApplied });
    setLineItems(plan.lines);
    syncJobLinks(state, plan.lines, plan);
  };

  // "PO links from references" for lines already on screen (typed, or saved).
  const suggestRefLinks = async () => {
    const req = referenceRequest(lineItems, lineIsEmpty);
    setRefLinksBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/bills/reference-links`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lines: req.lines,
          account_id: selectedSupplierId ? Number(selectedSupplierId) : null,
          xero_contact_id: supplierXeroContactId || null,
          bill_id: editMode && editingBillId ? Number(editingBillId) : null,
          bill_date: billDate || null,
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || 'The PO links could not be worked out');
      if (!data.jobs?.length) {
        setAlertDialog({ isOpen: true, type: 'info', message: 'No line quotes an order, PO or invoice — nothing to suggest.' });
        return;
      }
      // A goods bill: its quoted POs only — its lines are not made EXTH charges.
      if (!isBillOfJobs(req.ids.map(id => lineItems.find(l => l.id === id)), data.jobs)) {
        const loads = goodsBillPoLoads(data.jobs, { billId: editMode ? editingBillId : null }).filter(id => !linkedPOIds.includes(id));
        if (loads.length) loadFromPOs(loads, products, { append: true });
        setAlertDialog({ isOpen: true, type: 'info', message: loads.length
          ? `The lines quote ${loads.length === 1 ? 'a PO' : `${loads.length} POs`} — brought on to match the lines to.`
          : 'No PO of this supplier is quoted that is not on the bill already.' });
        return;
      }
      const state = { jobs: data.jobs, ids: req.ids, choices: initialJobChoices(data.jobs, { billId: editMode ? editingBillId : null }) };
      const plan = applyJobs(lineItems, state.jobs, state.choices, state.ids, { items: products, reconciling: !editMode && pdfLinesApplied });
      setJobLinks(state);
      setLineItems(plan.lines);
      syncJobLinks(state, plan.lines, plan);
    } catch (e) {
      setAlertDialog({ isOpen: true, type: 'error', message: e.message });
    } finally {
      setRefLinksBusy(false);
    }
  };

  // Parse PDF client-side: calls /parse-pdf, auto-fills form, stores File for later upload
  const handlePDFSelect = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setAlertDialog({ isOpen: true, message: 'Only PDF files are supported.', type: 'warning' });
      return;
    }

    setPendingFile(file);
    setOcrApplied(null);
    // A new upload: the last one's credit-note question is no longer its.
    setCreditReadState(null);
    setCreditReadDialog(null);
    setCreditReadEditNote(false);
    // A bill already in Xero: the PDF is attached as it is. Reading it would
    // fill fields and add lines to a bill Xero already holds — every one of
    // them a change sent to Xero that nobody typed.
    if (patchMode) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setParsingPDF(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      // Add Supplier Credit: the reader returns the credit's figures positive.
      if (transactionType === 'CREDIT') formData.append('credit', '1');
      const response = await fetch(`${API_URL}/api/bills/parse-pdf`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      const result = await response.json();

      if (response.ok && result.success) {
        routeRead(result.suggested || {}, result.has_text,
          { notes: result.notes, confidence: result.confidence });
      }
    } catch (error) {
      console.warn('PDF parse failed:', error.message);
      // Non-fatal — file is still staged, just no auto-fill
    } finally {
      setParsingPDF(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Upload a File to an existing bill record (called after save)
  // Returns null when the PDF is on the bill, else why not. Tried twice: a
  // PDF that fails to attach used to be lost without a word (Cam, 16 Oct —
  // bill 44012 saved on 14 Oct with no PDF).
  const uploadBillAttachment = async (billId, file) => {
    let why = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_URL}/api/bills/${billId}/attachment`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        if (res.ok) return null;
        const body = await res.json().catch(() => ({}));
        why = body.error || `the server answered ${res.status}`;
      } catch (e) {
        why = e.message || 'the upload did not reach the server';
      }
    }
    return why;
  };

  const handleRemoveAttachment = async () => {
    if (pendingFile) {
      // Not yet uploaded — just clear the staged file
      setPendingFile(null);
      setOcrApplied(null);
      setOcrFilledFields(new Set());
      setCreditReadEditNote(false);
      return;
    }
    if (!editingBillId || !attachmentFilename) return;
    try {
      const response = await fetch(`${API_URL}/api/bills/${editingBillId}/attachment`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setAttachmentFilename('');
      } else {
        throw new Error(result.error || 'Failed to remove attachment');
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: 'Failed to remove attachment: ' + error.message, type: 'error' });
    }
  };

  const handleCancel = () => {
    if (bulk) bulk.onCancel();
    else if (onNavigate) onNavigate(returnTo);
  };

  // Reset under the lines. A new bill goes back to blank; an edit goes back to
  // the bill as it is saved, rather than turning into a new one. In Bulk Add
  // Bills, back to what was read from the item.
  const handleReset = () => {
    setConfirmReset(false);
    if (bulk) {
      resetForm();
      startFromBulk(products);
      return;
    }
    if (editMode && editingBillId) loadBillForEdit(editingBillId);
    else resetForm();
  };

  // Always asks first.
  const requestReset = () => setConfirmReset(true);

  const resetForm = () => {
    setEditMode(false);
    setEditingBillId(null);
    setSelectedSupplierId('');
    setSupplierName('');
    setSupplierXeroContactId('');
    setSupplierCheck('');
    supplierConfirmedRef.current = false;
    clearLinkedPOs();
    setBillDate(new Date().toISOString().split('T')[0]);
    setDueDate('');
    setReference('');
    setSupplierReference('');
    setStatus('Authorised');
    // Add Supplier Credit stays a credit when reset.
    setTransactionType(prev => (prev === 'CREDIT' && !editMode ? 'CREDIT' : 'BILL'));
    setIsQueried(false);
    setNotes('');
    setPoReference('');
    setIsStock(false);
    setBilledPoQuestion(null);
    setCreditReadState(null);
    setCreditReadDialog(null);
    setCreditReadEditNote(false);
    setLineItems([blankLine()]);
    setInvoiceNet('');
    setInvoiceVat(null);
    setPushStatus(null);
    setDuplicates([]);
    setSavedBillSource(null);
    setSavedBillXeroId(null);
    setPendingFile(null);
    setParsingPDF(false);
    setOcrApplied(null);
    setOcrFilledFields(new Set());
    setAttachmentFilename('');
    setPendingSupplierUpdate(null);
    setLockNotice(null);
    setNetConfirmed(null);
    setPdfLinesApplied(false);
    setPoEditTicks({});
    setCostChoices({});
    setRecordedCostChoices({});
    setServerCostRequired(new Set());
    setJobLinks(null);
    jobPoIdsRef.current = { goods: [], haulage: [] };
    setQtyLeft({});
    setQtySave(null);
    setPoHeader(null);
    setBillHeader(null);
    setSuppliedCopies({});
    setCreditPoRefs([]);
    setCreditBillRefs([]);
    setCreditKind(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
  };

  // Remove a field from the OCR highlight set when the user edits it
  const clearOcrField = (fieldName) =>
    setOcrFilledFields(prev => { const n = new Set(prev); n.delete(fieldName); return n; });

  // ── The header's two sides, offered in each field (2026-10-16) ──
  // A supplier in the PO's supplier group IS the PO's supplier (Mole Valley
  // Feed Solutions and its depots): the invoice's named one is used and never
  // offered as a difference. An unrelated one is offered in the field — the
  // PO's stays until the user picks the invoice's.
  const setSupplierTo = (sup) => {
    if (!sup) return;
    setSelectedSupplierId(String(sup.id));
    setSupplierName(sup.name || '');
    setSupplierXeroContactId(sup.xeroContactId || '');
    supplierConfirmedRef.current = true;
    setSupplierCheck('');
    clearOcrField('supplier');
  };
  const headerChoice = (field) => {
    if (patchMode || !poHeader || !billHeader) return null;
    if (field === 'supplier') {
      const po = poHeader.supplier;
      const bill = billHeader.supplier;
      if (!po || !bill) return null;
      const clash = supplierClash({ account_id: bill.id, company_name: bill.name, group_account_ids: bill.groupIds }, { accountId: po.id, name: po.name });
      if (!clash) return null;
      return {
        label: 'Supplier', differs: true, other: { label: 'Choose another…' },
        active: String(selectedSupplierId) === String(po.id) ? 'po' : (String(selectedSupplierId) === String(bill.id) ? 'bill' : null),
        choices: [
          { key: 'po', tag: 'PO', text: po.name, hint: poHeader.poNumbers?.length ? `${poHeader.poNumbers.join(', ')}'s supplier` : undefined, onPick: () => setSupplierTo(po) },
          { key: 'bill', tag: 'Bill', text: bill.name, hint: bill.printed && bill.printed !== bill.name ? `“${bill.printed}” on the invoice` : 'named on the invoice', onPick: () => setSupplierTo(bill) },
        ],
      };
    }
    const keys = { billDate: 'bill_date', dueDate: 'due_date', supplierReference: 'supplier_reference' };
    const k = keys[field];
    const po = poHeader[k];
    const bill = billHeader[k];
    if (!po || !bill || String(po).trim() === String(bill).trim()) return null;
    const current = { billDate, dueDate, supplierReference }[field];
    const set = {
      billDate: (v, side) => setBillDateFrom(v, side === 'po' ? 'po' : 'supplier'),
      dueDate: (v) => setDueDate(v),
      supplierReference: (v) => setSupplierReference(v),
    }[field];
    const isDate = field !== 'supplierReference';
    const show = (v) => (isDate ? formatDate(v) : v);
    return {
      label: { billDate: 'Bill date', dueDate: 'Due date', supplierReference: 'Reference' }[field], differs: true,
      other: { label: isDate ? 'Choose another date' : 'Type another' },
      active: String(current) === String(po) ? 'po' : (String(current) === String(bill) ? 'bill' : null),
      choices: [
        { key: 'po', tag: 'PO', text: show(po), hint: field === 'billDate' ? "the PO's date" : undefined, onPick: () => set(po, 'po') },
        { key: 'bill', tag: 'Bill', text: show(bill), hint: field === 'billDate' ? "the invoice's date" : undefined, onPick: () => set(bill, 'bill') },
      ],
    };
  };
  /** A header field, with its two sides when the PO and the invoice differ. */
  const withHeaderChoice = (field, node, { picker = false } = {}) => {
    const c = headerChoice(field);
    return c ? <FieldChoice {...c} openOnFocus={!picker}>{node}</FieldChoice> : node;
  };

  // What saving will also do to the POs (each chosen in its cell), and the
  // gentle notes: lines that do not come to the invoice's figure, a PO with no
  // line on the bill, a quantity to look at. Nothing here stops a save.
  const saveSummary = (() => {
    const does = [];
    const notes = [];
    if (transactionType === 'BILL') {
      const costs = costChanges.filter(c => costUpdateOn(c.key || costKey(c.purchase_order_id, c.po_line_key)));
      const kept = costChanges.length - costs.length;
      if (costs.length) does.push(`set ${costs.length} PO cost${costs.length === 1 ? '' : 's'} to the bill's${kept ? ` (${kept} kept as ${kept === 1 ? 'it is' : 'they are'})` : ''}`);
      else if (kept) notes.push(`${kept} PO cost${kept === 1 ? ' differs' : 's differ'} from the bill and ${kept === 1 ? 'is' : 'are'} kept as ${kept === 1 ? 'it is' : 'they are'} — tick "Also set the PO's cost" in the price cell to change ${kept === 1 ? 'it' : 'them'}.`);
      const copies = suppliedCopiesPayload(suppliedCopies, copyOffered).filter(e => linkedPOIds.includes(Number(e.purchase_order_id))).length;
      if (copies) does.push(`record the bill's quantity as supplied on ${copies} line${copies === 1 ? '' : 's'}`);
      const edits = poLinesEditable ? poEditsPayload(poEdits, linkedPODetailsMap, Object.fromEntries(Object.entries(poContext).map(([id, c]) => [id, c?.scope || null]))).length : 0;
      if (edits) does.push(`change ${edits} PO line${edits === 1 ? '' : 's'} (quantity or words)`);
    }
    if (allocation.unallocated !== null && Math.abs(allocation.unallocated) >= 0.005 && !allocation.netFromLines) {
      notes.push(`The lines come to ${formatCurrency(allocation.linesNet)} and the invoice to ${formatCurrency(allocation.invoice)} before VAT (${formatCurrency(Math.abs(allocation.unallocated))} ${allocation.unallocated > 0 ? 'unallocated' : 'over'}) — the bill is saved as its lines say.`);
    }
    // The lines' VAT against the invoice's VAT total: a penny or two is each
    // line's VAT rounded on its own, as Xero works it; more is a line on the
    // wrong VAT (2026-10-16).
    if (allocation.vatOff !== null && Math.abs(allocation.vatOff) >= 0.005) {
      const small = Math.abs(allocation.vatOff) <= 0.02;
      notes.push(`The lines' VAT comes to ${formatCurrency(allocation.linesVat)} and the invoice's to ${formatCurrency(invoiceVat)} (${formatCurrency(Math.abs(allocation.vatOff))} ${allocation.vatOff > 0 ? 'more' : 'less'})${small ? ' — rounding: each line\'s VAT is worked to the penny, as Xero does.' : ' — check each line\'s VAT.'}`);
    }
    if (allocation.posWithoutLines.length && !(editMode && savedBillSource === 'xero')) {
      const nums = allocation.posWithoutLines.map(poNumberOf);
      notes.push(`${nums.join(', ')} ${nums.length === 1 ? 'has' : 'have'} no line on this bill, and ${nums.length === 1 ? 'is' : 'are'} marked billed when it is saved.`);
    }
    return { does, notes };
  })();

  // The first line of a job's delivery address: the contact's own, else the
  // bill's "To" after the customer's name.
  const deliveryLine1 = (job, target) => {
    if (target?.customer_delivery_line1) return target.customer_delivery_line1;
    const parts = String(job?.deliver_to || '').split(',').map(p => p.trim()).filter(Boolean);
    return parts.length > 1 ? parts[1] : null;
  };

  // A haulier's job, as a heading over its charge lines (2026-10-16): date ·
  // their job number · what it quoted → our document (choose another, or No
  // PO) · how sure · delivered to · Bag / Bulk.
  const jobHeading = (line, index) => {
    if (!jobLinks || !line._job) return null;
    const before = index > 0 ? lineItems[index - 1] : null;
    if (before && before._job?.key === line._job.key) return null;
    const job = jobLinks.jobs.find(j => j.job_key === line._job.key);
    if (!job) return null;
    const choice = jobLinks.choices[job.job_key] || {};
    const target = chosenTarget(job, choice);
    const warn = target ? billedWarning(target, { billId: editMode ? editingBillId : null }) : null;
    const pack = jobPack(job, choice, billPackDefault(jobLinks.jobs, jobLinks.choices));
    const options = jobDocOptions(job);
    const undecided = choice.po_id === null || choice.po_id === undefined;
    const theirs = /^line-\d+$/.test(String(job.job_key)) ? null : job.job_key;
    return (
      <div className="bill-job-head">
        <span className="bill-job-date">{job.date ? formatDate(job.date) : '—'}</span>
        {theirs && <span className="bill-job-theirs" title="The haulier's own job number">{theirs}</span>}
        <span className={`bill-job-quoted${job.references?.length ? '' : ' is-none'}`}>
          {job.references?.length ? <>quoted <strong>{job.references.join(', ')}</strong></> : 'no reference'}
        </span>
        <span className="material-symbols-rounded bill-job-arrow">arrow_forward</span>
        <div className="bill-job-doc">
          <SearchableDropdown
            compact
            value={undecided ? null : choice.po_id}
            onChange={(v) => chooseJob(job.job_key, { po_id: v ?? NO_PO })}
            options={options}
            placeholder={options.length > 1 ? 'Choose our document…' : 'No match'}
            searchPlaceholder="Search…"
            disabled={!poLinesEditable || patchMode}
          />
        </div>
        {/* How sure is the chosen document's badge in the picker; otherwise said here. */}
        {(undecided || !target) && (
          <span className={`bill-job-conf conf-${undecided ? 'possible' : 'none'}`} title={job.reason}>
            {undecided ? 'Check' : 'No PO'}
          </span>
        )}
        {target && target.customer_account_id ? (
          // The customer is a contact here: its code (opens the contact), its
          // name, and the first line of its delivery address under it.
          <span className="bill-job-to bill-job-contact" title={job.deliver_to || target.customer_name || ''}>
            <span className="bill-job-contact-top">
              <EntityLink type="contact" id={target.customer_account_id} variant="badge">
                {target.customer_account_no || 'Contact'}
              </EntityLink>
              <span className="bill-job-contact-name">{target.customer_name}</span>
            </span>
            {deliveryLine1(job, target) && <span className="bill-job-contact-line">{deliveryLine1(job, target)}</span>}
          </span>
        ) : (job.deliver_to && <span className="bill-job-to" title={job.deliver_to}>{job.deliver_to}</span>)}
        <div className="bill-job-pack" title="Bag or Bulk haulage — the EXTH item its lines are booked to">
          <Dropdown
            compact
            value={pack}
            onChange={(v) => chooseJob(job.job_key, { pack: v })}
            options={[{ value: 'bag', label: 'Bag' }, { value: 'bulk', label: 'Bulk' }]}
            disabled={!poLinesEditable || patchMode}
          />
        </div>
        {warn && <span className="bill-job-warn">{warn}</span>}
      </div>
    );
  };

  const pdfPreviewUrl = pdfObjectUrl || (editingBillId && attachmentFilename ? `${API_URL}/api/bills/${editingBillId}/attachment` : null);

  if (loading) {
    return <div className="create-bill"><LoadingSpinner /></div>;
  }

  return (
    <div className="create-bill document-form">

      <div className="page-header" style={{ paddingBottom: "var(--spacing-sm)" }}>
        {/* The page title (Add / Edit Bill, Add / Edit Supplier Credit) comes
            from Dashboard; the note on uploading first sits beside it (Cam,
            2026-10-16). */}
        <div className="workflow-info">
          <span className="info">
            {isCreditMode
              ? "Upload the supplier's credit note PDF first — the date, number and lines will be filled in where possible (as positive figures)."
              : "Upload the supplier's PDF invoice first — dates and references will be auto-filled where possible."}
          </span>
        </div>
        <div className="header-actions">
          <button data-read-safe className="btn-round btn-danger" onClick={handleCancel}
            title={bulk ? 'Back to the Bulk Add Bills summary — nothing on this bill is saved' : undefined}>
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">close</span>
            </div>
            <span className="btn-label">{bulk ? 'Back to summary' : 'Cancel'}</span>
          </button>
        </div>
      </div>

      <div className="page-scroll-content">
      <div className="create-bill-layout">
      <div className="bill-form-container">

        {/* PDF Attachment Section — one row, no title (Cam, 2026-10-16): the
            round upload button, what it takes, then the file staged or saved.
            The note on uploading first is beside the page title. */}
        <div className="form-section bill-attachment-section">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handlePDFSelect(file);
            }}
          />

          <div className="attachment-upload-row">
            <button
              type="button"
              className="btn-round"
              onClick={() => fileInputRef.current?.click()}
              disabled={parsingPDF}
              title={pendingFile || attachmentFilename ? 'Replace PDF' : 'Upload PDF'}
              aria-label={pendingFile || attachmentFilename ? 'Replace PDF' : 'Upload PDF'}
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">upload_file</span>
              </div>
            </button>
            <span className="attachment-hint">PDF only · max 20 MB</span>

            {/* Staged / saved file display */}
            {(pendingFile || attachmentFilename) ? (
              <div className="attachment-current">
                <span className="material-symbols-rounded">picture_as_pdf</span>
                <span className="attachment-name">
                  {pendingFile ? pendingFile.name : attachmentFilename}
                  {pendingFile && <span className="attachment-staged-badge">staged</span>}
                </span>
                {attachmentFilename && editingBillId && (
                  <a
                    href={`${API_URL}/api/bills/${editingBillId}/attachment`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary btn-sm"
                  >
                    <span className="material-symbols-rounded">download</span>
                    View
                  </a>
                )}
                <button className="btn-icon" title="Remove" onClick={handleRemoveAttachment}>
                  <span className="material-symbols-rounded">delete</span>
                </button>
              </div>
            ) : null}
          </div>

          {/* OCR strip — processing state while parsingPDF, result state after */}
          {(parsingPDF || ocrApplied) && (
            <div className={`ocr-applied-strip${ocrApplied && (ocrApplied.noText || ocrApplied.noFields) ? ' ocr-applied-strip--warn' : ''}`}>
              {parsingPDF ? (
                <>
                  <LoadingSpinner type="spinner" size="sm" />
                  <span>Reading PDF...</span>
                </>
              ) : (
                <>
                  <div style={{ display: "flex", gap: "var(--spacing-sm)", alignItems: "center" }}>
                    <span className="material-symbols-rounded">
                      {ocrApplied.noText ? 'document_scanner' : ocrApplied.noFields ? 'info' : 'auto_fix_high'}
                    </span>
                    <span>
                      {ocrApplied.noText
                        ? 'Scanned PDF — no text could be extracted. Please fill in the fields manually.'
                        : ocrApplied.noFields
                          ? 'PDF read but no fields could be auto-detected. Please fill in the fields manually.'
                          : <>Auto-filled from PDF: <strong>{ocrApplied.fields.join(', ')}</strong>{ocrApplied.total ? ` · PDF total: ${formatCurrency(ocrApplied.total)}` : ''}</>
                      }
                    </span>
                  </div>
                  <button className="btn-icon" onClick={() => { setOcrApplied(null); setOcrFilledFields(new Set()); }}>
                    <span className="material-symbols-rounded">close</span>
                  </button>
                </>
              )}
            </div>
          )}

          {/* What the reader itself flagged — an ambiguous date, lines that
              don't sum to the sub-total, a code it couldn't match. Separate
              from the strip above, which reports what was filled in: this is
              what to check before saving. */}
          {!parsingPDF && ocrApplied?.notes && (
            <div className={`ocr-notes-strip${ocrApplied.confidence === 'low' ? ' ocr-notes-strip--warn' : ''}`}>
              <span className="material-symbols-rounded">
                {ocrApplied.confidence === 'low' ? 'warning' : 'fact_check'}
              </span>
              <span>
                {ocrApplied.confidence === 'low' && <strong>Low confidence. </strong>}
                {ocrApplied.notes}
              </span>
            </div>
          )}
        </div>

        {/* Bill Details Section */}
        <div className="form-section">
          <h2 style={{ marginBottom: "var(--spacing-lg)" }}>{isCreditMode ? 'Supplier Credit Details' : 'Bill Details'}</h2>
          {editMode && editCtx && editCtx.mode !== 'local' && (
            <div className="doc-notice warn">
              <span className="material-symbols-rounded">cloud_sync</span>
              <span>
                {patchMode
                  ? `This ${isCreditMode ? 'supplier credit' : 'bill'} is ${editCtx.posted ? 'approved ' : ''}in Xero. Only what you change here is sent to Xero — every other line, item code and tracking tag stays as Xero has it — and Xero's copy is checked afterwards; if it does not match, nothing is saved. The supplier, type and status cannot change here (status has its own button).`
                    + (isCreditMode ? ' What it relates to is kept here only.' : '')
                    + (editCtx.needs_reason ? ' You will be asked for a reason.' : '')
                  : editCtx.block
                    ? `${editCtx.block}.`
                    : 'This bill is in Xero.'}
              </span>
            </div>
          )}
          {creditReadEditNote && !isCreditMode && (
            <div className="doc-notice warn credit-read-note">
              <span className="material-symbols-rounded">assignment_return</span>
              <span>{CREDIT_READ_EDIT_NOTE}</span>
            </div>
          )}

          {/* One row, supplier first as on every create screen: who, what kind,
              their reference, the dates, the POs it settles, status. */}
          <div className={`form-grid bill-details-row${isCreditMode ? ' credit-details-row' : ''}`}>
            <div className={`form-group${ocrFilledFields.has('supplier') ? ' ocr-filled' : ''}${supplierCheck ? ' supplier-needs-check' : ''}`}>
              <label>Supplier *</label>
              <div className="doc-contact-pick">
              <div className="doc-contact-field">
              {withHeaderChoice('supplier', <SearchableDropdown
                value={selectedSupplierId}
                onChange={(value) => {
                  setSelectedSupplierId(value);
                  clearOcrField('supplier');
                  // Picked by the user: settled, no longer to be checked.
                  supplierConfirmedRef.current = !!value;
                  setSupplierCheck('');
                  if (value) {
                    const supplier = suppliers.find(s => s.id.toString() === value);
                    if (supplier) {
                      if (editMode) {
                        setPendingSupplierUpdate({ name: supplier.company_name, xeroContactId: supplier.xero_contact_id || '' });
                      } else {
                        setSupplierName(supplier.company_name);
                        setSupplierXeroContactId(supplier.xero_contact_id || '');
                      }
                    }
                  } else {
                    setPendingSupplierUpdate(null);
                    if (!editMode) {
                      setSupplierName('');
                      setSupplierXeroContactId('');
                    }
                  }
                }}
                // each line's item's supplier (or its group's suppliers) pinned first
                options={pinSupplierOptions([
                  { value: '', label: 'Select supplier...' },
                  ...suppliers.map(s => withStoreTag(s, {
                    value: s.id.toString(),
                    badge: s.account_no || '',
                    label: s.company_name,
                    searchText: `${s.account_no || ''} ${s.company_name} ${s.email || ''}`
                  }))
                ], [...pinnedSupplierIds(lineItems.map(lineItemOf)), ...codeGuessPins])}
                placeholder="Type to search suppliers..."
                disabled={patchMode}
              />, { picker: true })}
              </div>
              {selectedSupplierId && (
                <button
                  type="button"
                  data-read-safe
                  className="btn-round view-contact"
                  onClick={() => setShowSupplierModal(true)}
                  title="View supplier details"
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">visibility</span>
                  </div>
                </button>
              )}
              </div>
              <SupplierCheckHint
                hint={supplierCheck}
                canConfirm={!!selectedSupplierId}
                onConfirm={() => { supplierConfirmedRef.current = true; setSupplierCheck(''); }}
              />
              {pendingSupplierUpdate && (
                <button
                  type="button"
                  className="address-update-link"
                  onClick={() => {
                    setSupplierName(pendingSupplierUpdate.name);
                    setSupplierXeroContactId(pendingSupplierUpdate.xeroContactId);
                    setPendingSupplierUpdate(null);
                  }}
                >
                  <span className="material-symbols-rounded">sync</span>
                  Update to {pendingSupplierUpdate.name}
                </button>
              )}
            </div>

            <div className="form-group bill-field-type">
              <label>Type</label>
              <Dropdown
                value={transactionType}
                onChange={changeType}
                options={[
                  { value: 'BILL', label: 'Bill' },
                  { value: 'CREDIT', label: 'Credit Note' },
                  { value: 'OVERPAYMENT', label: 'Overpayment' },
                  { value: 'PREPAYMENT', label: 'Prepayment' }
                ]}
                disabled={(editMode && savedBillSource === 'xero') || patchMode}
              />
            </div>
            <div className={`form-group bill-field-ref${ocrFilledFields.has('supplierReference') ? ' ocr-filled' : ''}`}>
              <label>Reference{isCreditMode && <span className='required-field'> *</span>}</label>
              {withHeaderChoice('supplierReference', <Input
                type="text"
                value={supplierReference}
                onChange={(e) => { setSupplierReference(e.target.value); clearOcrField('supplierReference'); }}
                placeholder={isCreditMode ? 'Supplier credit note no.' : 'Supplier invoice no.'}
              />)}
            </div>
            <div className={`form-group doc-field-date${ocrFilledFields.has('billDate') ? ' ocr-filled' : ''}`}>
              <label>{isCreditMode ? 'Credit Date' : 'Bill Date'} <span className='required-field'>*</span></label>
              {withHeaderChoice('billDate', <DateInput
                min={firstAllowedDate}
                value={billDate}
                onChange={(e) => { setBillDateFrom(e.target.value, 'typed'); clearOcrField('billDate'); }}
                required
              />)}
            </div>
            {/* A credit has no due date (Jade, 2026-10-13). */}
            {!isCreditMode && (
            <div className={`form-group doc-field-date${ocrFilledFields.has('dueDate') ? ' ocr-filled' : ''}`}>
              <label>Due Date <span className='required-field'>*</span></label>
              {withHeaderChoice('dueDate', <DateInput
                value={dueDate}
                onChange={(e) => { setDueDate(e.target.value); clearOcrField('dueDate'); }}
              />)}
            </div>
            )}
            {!isCreditMode && (
            <div className="form-group bill-po-choose">
              <label>POs</label>
              {/* The three ways to link documents, side by side (Cam,
                  2026-10-15) — wrapping only when the window is too narrow. */}
              <div className="bill-po-buttons">
              {/* PO-D, PO-H and Xero-made PO- alike: their lines come onto the
                  bill, each keeping its PO. A weekly haulage bill: tick the
                  week's PO-Hs and split the haulier's figure across them. */}
              <div>
                <button
                  type="button"
                  className="btn-slideout"
                  onClick={() => setShowPoPicker(true)}
                  disabled={!selectedSupplierId}
                  title={selectedSupplierId ? "Choose this supplier's purchase orders" : 'Choose the supplier first'}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">playlist_add</span>
                  </div>
                  <span className="btn-label">{linkedPOIds.length ? 'Add POs' : 'Choose POs'}</span>
                </button>
              </div>
              {/* A haulier's bill for another supplier's PO made in Xero
                  (orders made here use their PO-H), or the supplier's own
                  separate haulage bill. On Add Bill the link is saved with the
                  bill; on Edit Bill it is made at once, as in the bill window. */}
              {(
                <div>
                  <button
                    type="button"
                    className="btn-slideout"
                    onClick={() => setShowHaulagePicker(true)}
                    disabled={transactionType !== 'BILL'}
                    title={transactionType === 'BILL'
                      ? "This bill is the haulage for a PO made in Xero — another supplier's, or the supplier's own separate haulage bill. The PO's cost and status are not changed"
                      : 'Only a bill can be the haulage for a PO'}
                  >
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">local_shipping</span>
                    </div>
                    <span className="btn-label">Haulage for a PO</span>
                  </button>
                </div>
              )}
              {/* A bill of several jobs, each quoting its order (SO-D, PO-H,
                  a bare number, an INV- from before cutover): suggest the PO
                  each job's lines link to (2026-10-14). */}
              <div>
                <button
                  type="button"
                  className="btn-slideout"
                  onClick={suggestRefLinks}
                  disabled={!!refLinksBlock || refLinksBusy || transactionType !== 'BILL'}
                  title={transactionType !== 'BILL' ? 'Only a bill links its lines to POs'
                    : (refLinksBlock || "Suggest the PO each job's lines link to, from the order, PO or invoice it quotes")}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">add_link</span>
                  </div>
                  <span className="btn-label">PO links from references</span>
                </button>
              </div>
              </div>
            </div>
            )}
            {/* This system's own (Cam, 2026-10-14): Stock, then the PO number
                the invoice quoted, kept when that PO is not linked — never
                sent to Xero, so editable on a bill already there. Between the
                POs' buttons and Status (Cam, 2026-10-16); the PO Reference
                is for a Stock bill, so it is greyed until Stock is on (a value
                already held is kept and saved as it is). */}
            {!isCreditMode && (
              <>
                <div className="form-group bill-stock-toggle-group">
                  <label htmlFor="bill-is-stock" title={pushSettings.localFieldsReady
                    ? 'Goods bought for our own stock, not for a customer order. Kept in this system only — never sent to Xero.'
                    : LOCAL_FIELDS_NOT_READY}>Stock</label>
                  <div className="checkbox-container">
                    <div className="check light">
                      <input
                        type="checkbox"
                        id="bill-is-stock"
                        checked={isStock}
                        disabled={!pushSettings.localFieldsReady}
                        onChange={(e) => setIsStock(e.target.checked)}
                      />
                      <label htmlFor="bill-is-stock"></label>
                    </div>
                  </div>
                </div>
                <div className="form-group bill-po-reference-group">
                  <label htmlFor="bill-po-reference">PO Reference</label>
                  <Input
                    id="bill-po-reference"
                    type="text"
                    value={poReference}
                    onChange={(e) => setPoReference(e.target.value.slice(0, 100))}
                    placeholder="Other PO#"
                    disabled={!pushSettings.localFieldsReady || !isStock}
                    title={poReferenceTitle(pushSettings.localFieldsReady, isStock)}
                  />
                </div>
              </>
            )}
            <div className="form-group bill-field-status">
              <label>Status</label>
              {/* Goes to Xero as chosen: Authorised (the default) approved,
                  Draft as a draft. Queried holds it at Draft. */}
              <SearchableDropdown
                value={isQueried ? 'Draft' : status}
                onChange={(value) => setStatus(value)}
                disabled={isQueried || patchMode}
                options={[
                  // Approved (stored Authorised) or Draft — nothing else on a
                  // new bill (Jade, 2026-10-05). An edit of a Submitted bill
                  // keeps showing it.
                  ...BILL_STATUS_CHOICES,
                  ...(editMode && status === 'Submitted' ? [{ value: 'Submitted', label: 'Submitted' }] : [])
                ]}
                searchPlaceholder="Search status..."
              />
              {isQueried && <span className="field-hint">A queried bill is always a draft, here and in Xero.</span>}
            </div>
            <div className="form-group queried-toggle-group">
              <label>Queried</label>
              <div className="checkbox-container">
                <div className="check light">
                  <input
                    type="checkbox"
                    id="is-queried-toggle"
                    checked={isQueried}
                    disabled={patchMode}
                    onChange={(e) => {
                      const on = e.target.checked;
                      // Approved in Xero: querying it voids it there and sends
                      // it again as a Draft — ask first; No leaves it unticked.
                      if (on && editMode && approvedInXero) { setConfirmQueryApproved(true); return; }
                      setIsQueried(on);
                      // Queried is a draft; cleared, it goes back to Authorised.
                      setStatus(on ? 'Draft' : 'Authorised');
                    }}
                  />
                  <label htmlFor="is-queried-toggle"></label>
                </div>
              </div>
            </div>
            {/* A supplier credit's second row (Jade, 2026-10-13): what it is
                for, and what it relates to — one list of POs and bills with
                one way to add to it — and, raised against one bill, what
                saving it does with that bill. */}
            {isCreditMode && (
              <>
              <div className="form-group credit-kind-group">
                <label id="credit-kind-label">Credit is for <span className='required-field'>*</span></label>
                {/* The user's choice on every credit, never defaulted
                    (2026-10-08): it decides whether its lines reach Xero
                    with their items (stock goes down) or the account only. */}
                <div className={`credit-kind-choice${!creditKind ? ' unchosen' : ''}`} role="radiogroup" aria-labelledby="credit-kind-label">
                  {CREDIT_PURPOSES.map(p => (
                    <button
                      key={p.value}
                      type="button"
                      role="radio"
                      aria-checked={creditKind === p.value}
                      className={`credit-kind-option${creditKind === p.value ? ' selected' : ''}`}
                      disabled={billInXero && !patchMode}
                      title={billInXero && !patchMode
                        ? 'Fixed here while editing credits in Xero is not possible — its lines went there accordingly'
                        : patchMode
                          ? `${p.hint} — changing it takes the item codes off (or puts them back on) its lines in Xero`
                          : p.hint}
                      onClick={() => setCreditKind(p.value)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <span className="field-hint">
                  {creditKind ? CREDIT_PURPOSES.find(p => p.value === creditKind).hint : 'Choose one: it decides whether the stock goes down in Xero.'}
                </span>
              </div>
                <div className="form-group credit-refs">
                  <label id="credit-refs-label">Relates to</label>
                  <div className="credit-ref-chips" aria-labelledby="credit-refs-label">
                    {creditPoRefs.map(po => (
                      <span key={`po-${po.id}`} className="credit-ref-chip">
                        <span className="material-symbols-rounded">description</span>
                        <EntityLink type="purchase_order" id={po.xero_purchase_order_id}><strong>{po.purchase_order_number || `PO #${po.id}`}</strong></EntityLink>
                        <button type="button" className="btn-icon" title={`Take ${po.purchase_order_number || 'the PO'} off`}
                                onClick={() => setCreditPoRefs(prev => prev.filter(p => Number(p.id) !== Number(po.id)))}>
                          <span className="material-symbols-rounded">close</span>
                        </button>
                      </span>
                    ))}
                    {creditBillRefs.map(b => (
                      <span key={`bill-${b.id}`} className="credit-ref-chip">
                        <span className="material-symbols-rounded">receipt_long</span>
                        <EntityLink type="bill" id={b.id}><strong>{b.supplier_reference || b.bill_number || `Bill #${b.id}`}</strong></EntityLink>
                        <button type="button" className="btn-icon" title="Take this bill off"
                                onClick={() => setCreditBillRefs(prev => prev.filter(p => Number(p.id) !== Number(b.id)))}>
                          <span className="material-symbols-rounded">close</span>
                        </button>
                      </span>
                    ))}
                    <div className="credit-ref-add">
                      {/* One way to add: the supplier's POs (the PO picker,
                          billed ones too) first, then its bills. */}
                      <CreditBillPicker
                        accountId={selectedSupplierId ? Number(selectedSupplierId) : null}
                        xeroContactId={selectedSupplierId ? creditSupplierXeroId : null}
                        selected={creditBillRefs}
                        onAdd={(b) => setCreditBillRefs(prev => (prev.some(p => Number(p.id) === Number(b.id)) ? prev : [...prev, b]))}
                        extraOptions={CHOOSE_POS_OPTIONS}
                        onExtra={() => setShowCreditPoPicker(true)}
                        placeholder="Add a PO or bill…"
                      />
                    </div>
                  </div>
                  <span className="field-hint">A reference only — a credit never bills a PO or changes a supplied quantity.</span>
                </div>
                <CreditAgainstBill
                  bill={againstBill}
                  several={creditBillRefs.length > 1}
                  preview={againstPreview}
                  saved={savedAutoAllocation}
                  inXero={billInXero}
                  creditTotal={calculateTotals().total}
                  clearQuery={clearBillQuery}
                  onClearQuery={setClearBillQuery}
                  allocate={allocateToBill}
                  onAllocate={setAllocateToBill}
                />
              </>
            )}
          </div>

          {/* A date in a locked period, moved to the first open day — said
              here, in the window, never in a dialog. */}
          {lockNotice && !editMode && (
            <div className="bill-lock-notice" role="status">
              <span className="material-symbols-rounded">lock_clock</span>
              <span>{lockedDateNotice(lockNotice)}</span>
            </div>
          )}
        </div>

        {/* Bills already in the system that look like this one. The save asks
            again before it lets a second copy in. */}
        {duplicates.length > 0 && (
          <div className="form-section bill-duplicates-strip" role="alert">
            <span className="material-symbols-rounded">content_copy</span>
            <div>
              <strong>This may already be in the system:</strong>
              <ul>
                {duplicates.map(d => (
                  <li key={d.id}>
                    {d.supplier_reference || '(no invoice number)'} · {formatCurrency(d.total)} · {d.bill_date ? new Date(d.bill_date).toLocaleDateString('en-GB') : '—'}
                    {' — '}{d.source === 'xero' ? 'keyed in Xero' : 'entered here'}, {d.status}
                    {d.reason === 'invoice_number' ? ' (same invoice number)' : ' (same total and date)'}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Where this bill is on its way to Xero (bills entered here). */}
        {editMode && savedBillSource === 'local' && pushStatus && (
          <div className="form-section">
            <BillPushStatus
              billId={editingBillId}
              status={{ ...pushStatus, sending_enabled: pushSettings.enabled }}
              onChange={setPushStatus}
            />
          </div>
        )}

        {/* Line Items Section */}
        <div className="form-section">
          <div className="bill-lines-head">
            <h2>Line Items</h2>
            {/* What this bill is for (2026-10-16): its POs, and the POs it is
                the third-party haulage for — each clickable, each can come off. */}
            {(linkedPOIds.length > 0 || haulagePOs.length > 0) && (
              <div className="bill-po-chips" aria-label="POs on this bill">
                {linkedPOIds.length > 0 && <span className="bill-po-chips-label">For</span>}
                {(showAllPoChips || linkedPOIds.length <= PO_CHIPS_SHOWN ? linkedPOIds : linkedPOIds.slice(0, PO_CHIPS_SHOWN)).map(id => {
                  const meta = linkedPODetailsMap[id] || {};
                  const num = meta.purchase_order_number || `PO #${id}`;
                  return (
                    <span key={id} className="bill-po-chip">
                      {/* PurchaseOrderModal keys on the XERO id (the silent-404 trap in CLAUDE.md). */}
                      <EntityLink type="purchase_order" id={meta.xero_purchase_order_id}><strong>{num}</strong></EntityLink>
                      {meta.status && <span className="bill-po-chip-status">{String(meta.status).toLowerCase()}</span>}
                      {meta.contact_name && String(meta.account_id || '') !== String(selectedSupplierId) && (
                        <span className="bill-po-chip-supplier" title="The PO's own supplier">{meta.contact_name}</span>
                      )}
                      <button type="button" className="btn-icon" title={`Take ${num}, and its lines, off the bill`} onClick={() => setConfirmUnlinkPo(id)}>
                        <span className="material-symbols-rounded">close</span>
                      </button>
                    </span>
                  );
                })}
                {linkedPOIds.length > PO_CHIPS_SHOWN && (
                  <button type="button" className="bill-po-chips-more" onClick={() => setShowAllPoChips(v => !v)}>
                    {showAllPoChips ? 'Fewer' : `+${linkedPOIds.length - PO_CHIPS_SHOWN} more`}
                  </button>
                )}
                {haulagePOs.length > 0 && <span className="bill-po-chips-label" title="This bill is the haulage for these POs — their cost and status are not changed">Haulage for</span>}
                {haulagePOs.map(po => (
                  <span key={`h-${po.id}`} className="bill-po-chip is-haulage">
                    <EntityLink type="purchase_order" id={po.xero_purchase_order_id}><strong>{haulageForLabel(po)}</strong></EntityLink>
                    {po.contact_name && <span className="bill-po-chip-supplier">{po.contact_name}</span>}
                    <button type="button" className="btn-icon" data-write-action title={`Take ${po.purchase_order_number} off`} disabled={haulageBusy}
                            onClick={() => (editMode && editingBillId ? unlinkHaulageNow(po) : setHaulagePOs(prev => prev.filter(p => Number(p.id) !== Number(po.id))))}>
                      <span className="material-symbols-rounded">close</span>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* One invoice line covering several PO lines of its product (Cam,
              2026-10-14): offered here, or made when sure — and undoable. */}
          {(splitOfferList.length > 0 || splitsMade.length > 0) && (
            <div className="bill-split" role="region" aria-label="Bill lines split across PO lines">
              {splitOfferList.map(({ offer, source }) => (
                <div key={`offer-${offer.sourceId}`} className="bill-split-row is-offer">
                  <span className="material-symbols-rounded">call_split</span>
                  <span className="bill-split-what">{offerText(offer, source)}</span>
                  <div className="bill-split-actions slideout">
                    <div>
                      <button type="button" className="btn-slideout" onClick={() => doSplit(offer)}
                        title="One line per PO line, each with its share of the quantity at the invoice's price">
                        <div className="icon-wrapper"><span className="material-symbols-rounded">call_split</span></div>
                        <span className="btn-label">Split it</span>
                      </button>
                    </div>
                    <button type="button" className="btn-icon" title="Choose the PO lines and quantities"
                      aria-label="Choose the PO lines and quantities" onClick={() => openSplitDialog(source, offer)}>
                      <span className="material-symbols-rounded">tune</span>
                    </button>
                  </div>
                </div>
              ))}
              {splitsMade.map(g => (
                <div key={g.group} className="bill-split-row is-made">
                  <span className="material-symbols-rounded">call_split</span>
                  <span className="bill-split-what">
                    {lineLabel(g.original)} split across {g.lines.map(l => `${l.purchase_order_number || linkedPODetailsMap[l.purchase_order_id]?.purchase_order_number || 'a PO'} (${formatQuantity(l.quantity)})`).join(', ')}
                    {g.lines.some(l => l._split?.priceAdjusted) ? ' — one price moved at the 4th decimal place so the lines add up to the invoice line to the penny' : ''}
                    {g.lines.some(l => l._split?.vatAdjusted) ? ' — a penny of VAT on the largest line so the VAT adds up to the invoice line\'s' : ''}
                    . Check it.
                  </span>
                  <div className="bill-split-actions">
                    <button type="button" className="btn-icon" title="Undo the split — the invoice line and its PO lines back as they were"
                      aria-label="Undo the split" onClick={() => setLineItems(prev => unsplitLine(prev, g.group))}>
                      <span className="material-symbols-rounded">undo</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* A supplier credit's figures are positive (Jade, 2026-10-13): a
              minus typed on a credit is taken off when the figure is left
              (never while it is being typed), the price carrying any
              deduction (utils/creditSigns). */}
          <div className="bill-lines-wrap"
               onBlur={isCreditMode ? () => setLineItems(prev => positiveCreditGridLines(prev)) : undefined}>
          <OrderLinesGrid
            variant="bill"
            priceUnitFacts={billLinePriceFacts}
            lines={lineItems}
            onChange={(next) => setLineItems(prev => withTypedCosts(prev, keepReaderUnits(prev, typeof next === 'function' ? next(prev) : next)))}
            inventoryItems={products}
            previousItems={previousItems}
            onRemovePrevious={removePrevious}
            suppliers={suppliers}
            supplierAccountId={selectedSupplierId ? parseInt(selectedSupplierId, 10) : null}
            accounts={accounts}
            linkedLines={linkedLines}
            onAddItem={setAddItemForRow}
            onItemPicked={handleItemPicked}
            poOptions={linkedPOIds.length ? poOptions : null}
            lineFlags={lineFlags}
            cellChoice={cellChoice}
            pricePlaceholder={(line) => (costInfoOf(line)?.required && !costChosen(line) ? 'Choose a cost' : null)}
            groupHeader={jobLinks ? jobHeading : null}
            footerRight={<LineTotalsPanel lines={lineItems} perLine />}
          />
          </div>

          {/* The supplier's figure against the lines — information, not a
              gate (Cam, 2026-10-16): what is unallocated is said, and the bill
              saves as its lines say. */}
          <div className={`bill-allocation${allocation.unallocated !== null && Math.abs(allocation.unallocated) >= 0.005 ? ' is-off' : ''}${allocation.unallocated !== null && Math.abs(allocation.unallocated) < 0.005 ? ' is-balanced' : ''}`}>
            {/* Before VAT starts at the lines' total (Jade, 2026-10-05) and is
                highlighted until it is checked against the supplier's invoice:
                type the invoice's figure, or tick it. */}
            <div className={`form-group bill-net-field${ocrFilledFields.has('invoiceNet') ? ' ocr-filled' : ''}${netNeedsCheck ? ' needs-check' : ''}`}>
              <label htmlFor="bill-invoice-net">
                Before VAT
              </label>
              <div className="bill-net-input">
                <Input
                  id="bill-invoice-net"
                  type="text"
                  inputMode="decimal"
                  value={invoiceNet !== '' ? invoiceNet : (allocation.netFromLines ? allocation.linesNet.toFixed(2) : '')}
                  onChange={(e) => { setInvoiceNet(e.target.value); clearOcrField('invoiceNet'); }}
                  placeholder="The invoice's total before VAT"
                />
                <button
                  type="button"
                  className={`btn-icon${netNeedsCheck ? '' : ' btn-disabled'}`}
                  disabled={!netNeedsCheck}
                  title={netNeedsCheck ? "It matches the supplier's invoice" : 'Checked'}
                  onClick={() => setNetConfirmed(allocation.linesNet)}
                >
                  <span className="material-symbols-rounded">check</span>
                </button>
              </div>
              {netNeedsCheck && (
                <span className="bill-net-hint">From the lines — check this against the supplier's invoice.</span>
              )}
            </div>
            {/* The figures together, so a narrow form wraps them as one. */}
            <div className="bill-allocation-figures">
            <div className="bill-allocation-figure">
              <span>Lines</span>
              <strong>{formatCurrency(allocation.linesNet)}</strong>
            </div>
            <div className="bill-allocation-figure is-unallocated">
              <span>Unallocated</span>
              <strong>{allocation.unallocated === null ? '—' : formatCurrency(allocation.unallocated)}</strong>
            </div>
            {/* The lines' VAT beside the invoice's VAT total (2026-10-16). */}
            {invoiceVat !== null && (
              <div className={`bill-allocation-figure is-vat${allocation.vatOff !== null && Math.abs(allocation.vatOff) >= 0.005 ? ' is-off' : ''}`}
                title={allocation.vatOff !== null && Math.abs(allocation.vatOff) >= 0.005
                  ? `The lines' VAT is ${formatCurrency(Math.abs(allocation.vatOff))} ${allocation.vatOff > 0 ? 'more' : 'less'} than the invoice's`
                  : "The lines' VAT is the invoice's"}>
                <span>VAT · invoice {formatCurrency(invoiceVat)}</span>
                <strong>{formatCurrency(allocation.linesVat)}</strong>
              </div>
            )}
            </div>
          </div>

          {/* What saving will also do, and anything worth a second look —
              said, never asked (2026-10-16). */}
          {transactionType === 'BILL' && costChoiceMissing.length > 0 && (
            <div className="bill-save-summary is-blocked" role="alert">
              <div className="bill-save-summary-row is-blocked">
                <span className="material-symbols-rounded">price_change</span>
                <span>{costChoiceRequiredText(costChoiceMissing)}</span>
              </div>
            </div>
          )}
          {(saveSummary.does.length > 0 || saveSummary.notes.length > 0) && (
            <div className="bill-save-summary" role="status">
              {saveSummary.does.length > 0 && (
                <div className="bill-save-summary-row">
                  <span className="material-symbols-rounded">task_alt</span>
                  <span>Saving will also {saveSummary.does.join(' · ')}.</span>
                </div>
              )}
              {saveSummary.notes.map(n => (
                <div key={n} className="bill-save-summary-row is-note">
                  <span className="material-symbols-rounded">info</span>
                  <span>{n}</span>
                </div>
              ))}
            </div>
          )}

          {/* Under the lines: notes left, reset and save right. */}
          <div className="doc-footer">
            <div className="doc-footer-main">
              <label htmlFor="bill-notes">Notes</label>
              <textarea
                id="bill-notes"
                className="doc-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this bill..."
                rows={2}
              />
            </div>
            <div className="doc-footer-actions">
              <button className="btn-slideout" onClick={requestReset} disabled={saving}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">replay</span>
                </div>
                <span className="btn-label">{editMode ? 'Discard Changes' : 'Reset'}</span>
              </button>
              <button className="btn-slideout btn-slideout-primary" onClick={() => handleSaveBill()}
                disabled={saving || (transactionType === 'BILL' && costChoiceMissing.length > 0)}
                title={transactionType === 'BILL' && costChoiceMissing.length ? costChoiceRequiredText(costChoiceMissing) : undefined}>
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded ${saving ? 'spinning' : ''}`}>
                    {saving ? 'sync' : 'save'}
                  </span>
                </div>
                <span className="btn-label">{saving ? 'Saving...' : `${editMode ? 'Update' : 'Save'} ${isCreditMode ? 'Credit' : 'Bill'}`}</span>
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* PDF Preview Panel */}
      {pdfPreviewUrl && (
        <div className={`pdf-preview-panel${pdfExpanded ? ' expanded' : ''}`}>
          {/* Down the left edge, like the side nav's own collapse control, so it
              reads as the same gesture. Vertical because the panel has height to
              spare and no width to give away. */}
          <button
            type="button"
            className="pdf-expand-toggle"
            onClick={() => { setPdfExpanded((v) => !v); setPdfZoom(1.0); }}
            title={pdfExpanded ? 'Narrow the preview' : 'Widen the preview'}
          >
            <span className={`material-symbols-rounded pdf-expand-chevron${pdfExpanded ? ' open' : ''}`}>
              chevron_left
            </span>
            <span className="pdf-expand-word">{pdfExpanded ? 'COLLAPSE' : 'EXPAND'}</span>
          </button>
          <div className="pdf-preview-header">
            <span className="material-symbols-rounded">picture_as_pdf</span>
            <span className="pdf-preview-title">{pendingFile?.name || attachmentFilename}</span>
            <div className="pdf-zoom-controls">
              <button
                className="btn-icon"
                title="Zoom out"
                onClick={() => setPdfZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))}
              >
                <span className="material-symbols-rounded">zoom_out</span>
              </button>
              <span className="pdf-zoom-label">{Math.round(pdfZoom * 100)}%</span>
              <button
                className="btn-icon"
                title="Zoom in"
                onClick={() => setPdfZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))}
              >
                <span className="material-symbols-rounded">zoom_in</span>
              </button>
            </div>
            <a
              href={pdfPreviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-icon"
              title="Open in new tab"
            >
              <span className="material-symbols-rounded">open_in_new</span>
            </a>
          </div>
          <div className="pdf-preview-viewport">
            <div
              className="pdf-preview-scaler"
              style={{ transform: `scale(${pdfZoom})`, width: `${100 / pdfZoom}%` }}
            >
              <iframe
                src={pdfPreviewUrl}
                className="pdf-preview-frame"
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}

      </div>{/* end create-bill-layout */}

      {/* Create an inventory item without leaving a half-entered bill. The new
          item lands on the row that asked for it. */}
      <InventoryItemFormModal
        isOpen={addItemForRow !== null}
        item={null}
        onClose={() => setAddItemForRow(null)}
        onSaved={async (saved) => {
          const rowIndex = addItemForRow;
          setAddItemForRow(null);
          if (!saved) return;
          const items = await loadProducts();
          const fresh = items.find(i => i.id === saved.id) || saved;
          const resolved = resolveItemForVariant('bill', fresh);
          setLineItems(prev => {
            if (rowIndex === null || !prev[rowIndex]) return prev;
            const next = [...prev];
            // The new item's purchase words (utils/pickedItemWords, 2026-10-28).
            const { product_name: _n, ...rest } = resolved;
            next[rowIndex] = { ...next[rowIndex], ...rest, ...wordsOnItemPick('bill', next[rowIndex], fresh), _highlight: false, _hint: null };
            return next;
          });
        }}
      />

      {showSupplierModal && selectedSupplierId && (
        <ContactModal
          contactId={selectedSupplierId}
          onClose={() => setShowSupplierModal(false)}
        />
      )}

      {/* A supplier credit's POs: references (billed POs shown from the start). */}
      <BillPoPicker
        isOpen={showCreditPoPicker}
        supplierId={selectedSupplierId ? parseInt(selectedSupplierId, 10) : null}
        xeroContactId={(suppliers.find(x => String(x.id) === String(selectedSupplierId)) || {}).xero_contact_id || supplierXeroContactId || null}
        supplierName={supplierName}
        linkedIds={creditPoRefs.map(p => Number(p.id))}
        title="POs this credit relates to"
        addLabel="Relate"
        initialIncludeBilled
        onClose={() => setShowCreditPoPicker(false)}
        onAdd={(rows) => {
          setShowCreditPoPicker(false);
          setCreditPoRefs(prev => [...prev, ...rows.filter(r => !prev.some(p => Number(p.id) === Number(r.id))).map(r => ({
            id: Number(r.id), purchase_order_number: r.purchase_order_number, xero_purchase_order_id: r.xero_purchase_order_id || null
          }))]);
        }}
      />

      <BillPoPicker
        isOpen={showPoPicker}
        supplierId={selectedSupplierId ? parseInt(selectedSupplierId, 10) : null}
        xeroContactId={(suppliers.find(x => String(x.id) === String(selectedSupplierId)) || {}).xero_contact_id || supplierXeroContactId || null}
        supplierName={supplierName}
        excludeBillId={editMode ? editingBillId : null}
        linkedIds={linkedPOIds}
        onClose={() => setShowPoPicker(false)}
        onAdd={handleAddPOs}
      />

      {/* The invoice quotes a PO already billed (Cam, 2026-10-14): asked once
          the scan is read. × keeps the link (the save then asks whether it is
          a partial or second bill, as before). */}
      <ConfirmDialog
        isOpen={!!billedPoQuestion}
        title="This PO is already billed"
        className="billed-po-question"
        message={billedPoQuestion ? (
          <div className="billed-po-body">
            <p>{billedPoSentence(billedPoQuestion.billed)} The invoice quotes it again — usually a stock delivery, or an old PO number.</p>
            <ul className="billed-po-choices">
              <li>
                <span className="material-symbols-rounded">{BILLED_PO_CHOICES.review.icon}</span>
                <span>{BILLED_PO_CHOICES.review.label}</span>
              </li>
              <li className={pushSettings.localFieldsReady ? '' : 'is-unavailable'}>
                <span className="material-symbols-rounded">{BILLED_PO_CHOICES.stock.icon}</span>
                <span>
                  {BILLED_PO_CHOICES.stock.label.replace('the PO number', billedPoQuestion.billed.po?.purchase_order_number || 'the PO number')}
                  {!pushSettings.localFieldsReady && ` — not available yet: ${LOCAL_FIELDS_NOT_READY}`}
                </span>
              </li>
              <li>
                <span className="material-symbols-rounded">close</span>
                <span>Keep the PO link (a partial or second bill for it)</span>
              </li>
            </ul>
          </div>
        ) : ''}
        type="info"
        cancelText="Keep the PO link"
        onCancel={() => answerBilledPo('keep')}
        {...(pushSettings.localFieldsReady ? {
          secondaryText: BILLED_PO_CHOICES.review.label,
          secondaryIcon: BILLED_PO_CHOICES.review.icon,
          onSecondary: () => answerBilledPo('review'),
          confirmText: BILLED_PO_CHOICES.stock.label,
          confirmIcon: BILLED_PO_CHOICES.stock.icon,
          onConfirm: () => answerBilledPo('stock'),
        } : {
          confirmText: BILLED_PO_CHOICES.review.label,
          confirmIcon: BILLED_PO_CHOICES.review.icon,
          onConfirm: () => answerBilledPo('review'),
        })}
      />

      {/* The PDF reads as a credit note, on a new bill (Cam, 30 Oct 2026 —
          utils/billCreditRead): asked once it is read, and again at save if
          it was left to then. Each round button's label is its tooltip, so
          the body says which icon is which. */}
      <ConfirmDialog
        isOpen={!!creditReadWords}
        title={creditReadWords?.title || ''}
        className="billed-po-question credit-read-question"
        message={creditReadWords ? (
          <div className="billed-po-body">
            <p>{creditReadWords.message}</p>
            <ul className="billed-po-choices">
              {creditReadWords.choices.map(c => (
                <li key={c.key}>
                  <span className="material-symbols-rounded">{c.icon}</span>
                  <span><strong>{c.label}</strong> — {c.hint}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : ''}
        type="info"
        cancelText={creditReadWords?.cancelText || 'Cancel'}
        onCancel={() => answerCreditRead(creditReadDialog?.stage === 'save' ? 'back' : 'later')}
        secondaryText={creditReadWords?.choices[1].label}
        secondaryIcon={creditReadWords?.choices[1].icon}
        onSecondary={() => answerCreditRead('bill')}
        confirmText={creditReadWords?.choices[0].label}
        confirmIcon={creditReadWords?.choices[0].icon}
        onConfirm={() => answerCreditRead('credit')}
      />

      <ConfirmDialog
        isOpen={!!xeroNewerQuestion}
        type="warning"
        title={`${xeroNewerQuestion?.info?.purchase_order_number || 'The PO'} was changed in Xero`}
        message={xeroNewerQuestion ? xeroNewerQuestion.message : ''}
        confirmText="Send ours to Xero"
        cancelText="Go back"
        onConfirm={() => {
          const q = xeroNewerQuestion;
          setXeroNewerQuestion(null);
          const id = String(q.info.purchase_order_id);
          handleSaveBill(q.resolvedConflicts, q.xeroValues, q.confirmations,
            { ...q.opts, xeroNewerConfirmed: { ...(q.opts.xeroNewerConfirmed || {}), [id]: q.info.xero_updated } });
        }}
        onCancel={() => setXeroNewerQuestion(null)}
      />
      <ConfirmDialog
        isOpen={confirmUnlinkPo !== null}
        title="Take this PO off the bill?"
        message={(() => {
          const n = lineItems.filter(l => Number(l.purchase_order_id) === Number(confirmUnlinkPo)).length;
          const num = linkedPODetailsMap[confirmUnlinkPo]?.purchase_order_number || 'This PO';
          return `${num} comes off this bill${n ? `, with its ${n} line${n === 1 ? '' : 's'}` : ''}. It is marked billed only if the bill still covers it when saved.`;
        })()}
        confirmText="Take it off"
        onConfirm={() => unlinkPoAndLines(confirmUnlinkPo)}
        onCancel={() => setConfirmUnlinkPo(null)}
      />

      {/* On save: the bill's quantities against what the POs say was supplied. */}
      <BillQtySaveDialog
        isOpen={!!qtySave}
        questions={qtySave?.questions || []}
        answers={qtySave?.answers || {}}
        onChoose={(key, v) => setQtySave(prev => (prev ? { ...prev, answers: { ...prev.answers, [key]: v } } : prev))}
        onConfirm={answerQtySave}
        onCancel={() => setQtySave(null)}
      />
      {/* Copying a bill quantity far from what was ordered into supplied: asked, naming both. */}
      <ConfirmDialog
        isOpen={!!copyConfirm}
        title="Record the bill's quantity as supplied?"
        message={copyConfirm
          ? `${copyConfirm.number} · ${copyConfirm.what}: the bill says ${formatQuantity(copyConfirm.billQty)}, but ${formatQuantity(copyConfirm.ordered)} were ordered. Record ${formatQuantity(copyConfirm.billQty)} as what was supplied anyway? Only if the delivery (ticket or driver) says so — otherwise query it with the supplier.`
          : ''}
        confirmText="Yes, record it"
        cancelText="No"
        type="warning"
        onConfirm={() => {
          const c = copyConfirm;
          setCopyConfirm(null);
          setSuppliedCopies(prev => ({ ...prev, [c.poId]: { ...(prev[c.poId] || {}), [c.key]: 'confirmed' } }));
        }}
        onCancel={() => setCopyConfirm(null)}
      />

      {/* Querying a bill approved in Xero voids it there (Cam, 2026-10-05). */}
      <ConfirmDialog
        isOpen={confirmQueryApproved}
        title="Query an approved bill?"
        message="This bill is approved in Xero. Marking it queried will void it in Xero and send it again as a Draft. Continue?"
        confirmText="Yes"
        cancelText="No"
        type="warning"
        onConfirm={() => { setConfirmQueryApproved(false); setIsQueried(true); setStatus('Draft'); }}
        onCancel={() => setConfirmQueryApproved(false)}
      />

      {/* A bill approved in Xero: what changes there, and why (kept in its
          history here and as a note on the bill in Xero). */}
      <PostedEditDialog
        isOpen={!!reasonPrompt}
        title={isCreditMode
          ? (supplierReference ? `Change credit ${supplierReference} in Xero?` : 'Change this credit in Xero?')
          : (supplierReference ? `Change bill ${supplierReference} in Xero?` : 'Change this bill in Xero?')}
        intro={`This ${isCreditMode ? 'supplier credit' : 'bill'} is approved in Xero. Saving changes Xero's copy as well as this one — only this:`}
        changes={reasonPrompt?.changes}
        saving={saving}
        confirmLabel={isCreditMode ? 'Update credit' : 'Update bill'}
        onConfirm={(reason) => {
          const q = reasonPrompt;
          handleSaveBill(q.resolvedConflicts, q.xeroValues, q.confirmations || {}, { ...(q.opts || {}), reason });
        }}
        onCancel={() => setReasonPrompt(null)}
      />

      <HaulagePoLinkDialog
        isOpen={showHaulagePicker}
        billId={editMode ? editingBillId : null}
        transactionType={transactionType}
        supplierAccountId={selectedSupplierId || null}
        supplierXeroContactId={supplierXeroContactId || null}
        excludeIds={[...linkedPOIds, ...haulagePOs.map(p => p.id)]}
        onClose={() => setShowHaulagePicker(false)}
        linking={haulageBusy}
        onLink={async (po, reason) => {
          if (editMode && editingBillId) {
            // A saved bill: linked at once (POST /:id/haulage-links), as the
            // bill window does — the rest of the form is saved as usual.
            setHaulageBusy(true);
            try {
              const res = await fetch(`${API_URL}/api/bills/${editingBillId}/haulage-links`, {
                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ purchase_order_id: po.id, reason: reason || null })
              });
              const data = await res.json().catch(() => ({}));
              if (!res.ok || data.success === false) throw new Error(data.error || `Could not link ${po.purchase_order_number || 'the PO'}`);
              setShowHaulagePicker(false);
              setHaulagePOs(prev => (prev.some(p => Number(p.id) === Number(po.id)) ? prev : [...prev, po]));
            } catch (e) {
              setAlertDialog({ isOpen: true, message: e.message, type: 'error', onCloseCallback: null });
            } finally {
              setHaulageBusy(false);
            }
            return;
          }
          setShowHaulagePicker(false);
          setHaulagePOs(prev => (prev.some(p => Number(p.id) === Number(po.id)) ? prev : [...prev, po]));
        }}
      />
      <ConfirmDialog
        isOpen={!!haulageOffer}
        title="Link as third-party haulage?"
        message={`${haulageOffer?.message || ''} Save this bill as its third-party haulage?`}
        confirmText="Link as third-party haulage"
        cancelText="No"
        type="info"
        onConfirm={() => {
          const o = haulageOffer;
          setHaulageOffer(null);
          const next = moveToHaulage({ goodsIds: linkedPOIds, haulageIds: haulagePOs.map(p => Number(p.id)), lines: lineItems }, o.ids);
          setLinkedPOIds(next.goodsIds);
          setLineItems(next.lines);
          setHaulagePOs(prev => [...prev, ...o.ids.filter(id => !prev.some(p => Number(p.id) === id))
            .map(id => ({ id, purchase_order_number: linkedPODetailsMap[id]?.purchase_order_number || `PO #${id}`,
              xero_purchase_order_id: linkedPODetailsMap[id]?.xero_purchase_order_id || null,
              contact_name: linkedPODetailsMap[id]?.contact_name || null }))]);
          handleSaveBill(o.resolvedConflicts, o.xeroValues, o.confirmations || {}, { ...(o.opts || {}), haulageMove: o.ids });
        }}
        onCancel={() => setHaulageOffer(null)}
      />

      {/* One bill line over several PO lines: which, and how much on each (2026-10-14). */}
      {splitDialog && (
        <BillLineSplitDialog
          source={lineItems.find(l => l.id === splitDialog.sourceId) || splitDialog.source}
          candidates={splitDialog.candidates}
          initial={splitDialog.initial}
          onApply={(split) => { if (doSplit(split)) setSplitDialog(null); }}
          onClose={() => setSplitDialog(null)}
        />
      )}

      {/* The invoice raised on the old quantity: apply its changes and save,
          or go back to the bill with nothing saved (2026-10-08). */}
      {invoiceQuestion && (
        <InvoiceProposalDialog
          proposals={invoiceQuestion.proposals}
          notices={invoiceQuestion.notices}
          stale={invoiceQuestion.stale}
          busy={saving}
          continueLabel="Continue editing the bill"
          onApply={() => {
            const q = invoiceQuestion;
            setInvoiceQuestion(null);
            handleSaveBill(q.resolvedConflicts, q.xeroValues, q.confirmations || {}, { ...(q.opts || {}), invoiceChanges: applyAnswer(q.proposals) });
          }}
          onContinue={() => setInvoiceQuestion(null)}
        />
      )}

      {/* Dated in a month that should be locked in Xero (usePriorMonthConfirm). */}
      {priorMonth.dialog}

      {/* The server's questions before saving. Yes saves with the answer. */}
      <ConfirmDialog
        isOpen={!!saveQuestion}
        title={saveQuestion?.code === 'POSSIBLE_DUPLICATE' ? 'Already in the system?' : 'Bill a PO a second time?'}
        message={saveQuestion?.code === 'POSSIBLE_DUPLICATE'
          ? `This looks like ${saveQuestion.duplicates.map(d => `${d.supplier_reference || 'a bill'} (${formatCurrency(d.total)}, ${d.source === 'xero' ? 'keyed in Xero' : 'entered here'})`).join('; ')}. Save it anyway as a different invoice?`
          : `${(saveQuestion?.needsConfirmation || []).map(n => n.message).join(' ')} Save it as a partial or second bill?`}
        confirmText="Save anyway"
        onConfirm={() => {
          const q = saveQuestion;
          setSaveQuestion(null);
          handleSaveBill(q.resolvedConflicts, q.xeroValues, { ...q.confirmations, ...q.answer }, q.opts || {});
        }}
        onCancel={() => setSaveQuestion(null)}
      />

      <ConfirmDialog
        isOpen={confirmReset}
        title={editMode ? 'Discard changes?' : 'Reset this bill?'}
        message={editMode
          ? 'Go back to this bill as it is saved? Anything changed since will be lost.'
          : 'Clear the supplier, references, attachment, linked POs, notes and every line?'}
        confirmText={editMode ? 'Discard' : 'Reset'}
        onConfirm={handleReset}
        onCancel={() => setConfirmReset(false)}
      />

      {previousItemToast}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => {
          const cb = alertDialog.onCloseCallback;
          setAlertDialog({ isOpen: false, message: '', type: 'info', onCloseCallback: null });
          if (cb) cb();
        }}
      />

      <ConflictResolutionModal
        isOpen={showConflictModal}
        onClose={() => { setShowConflictModal(false); setConflictData(null); }}
        onResolve={({ resolutions, xeroValues }) => {
          setShowConflictModal(false);
          setConflictData(null);
          handleSaveBill(resolutions, xeroValues);
        }}
        entityType="bill"
        conflicts={conflictData?.conflicts || []}
        userChangedFields={conflictData?.userChangedFields || new Set()}
        xeroUpdatedAt={conflictData?.xeroUpdatedAt}
      />

      </div>
    </div>
  );
}

export default CreateBill;
